/**
 * agentWorkChatLogic
 *
 * Pure, framework-free helpers powering the reusable chat-first
 * Agent-Work workspace (AgentWorkChat & friends).
 *
 * Everything in this file MUST stay free of React/DOM/`window` so the
 * helpers can be exercised directly from `node --test` and reused
 * by every agent (DeepSeek, Claude, Gemini, OpenAI, Qwen, v0, …).
 */

// ── Status / phase ─────────────────────────────────────────────────────────

/** Canonical phases displayed in the small status bar. */
export const AGENT_WORK_PHASES = [
  "idle",
  "analyzing",
  "reviewing",
  "waiting_approval",
  "executing",
  "completed",
  "blocked",
  "error",
];

/** Short, human-readable German labels for the status bar. */
export const AGENT_WORK_PHASE_LABELS_DE = {
  idle:             "Bereit",
  analyzing:        "Denkt nach …",
  reviewing:        "Bereitet Vorschlag vor …",
  waiting_approval: "Wartet auf Freigabe",
  executing:        "Führt aus …",
  completed:        "Abgeschlossen",
  blocked:          "Blockiert",
  error:            "Fehler",
};

/**
 * Normalises any incoming phase/status value to one of AGENT_WORK_PHASES.
 * Falls back to "idle" so the UI never crashes on unknown values.
 */
export function normalizeAgentWorkPhase(raw) {
  if (typeof raw !== "string") return "idle";
  const key = raw.trim().toLowerCase().replace(/[\s-]/g, "_");
  if (AGENT_WORK_PHASES.includes(key)) return key;
  // Common aliases coming from the backend or other agents.
  if (key === "running" || key === "active" || key === "in_progress") return "analyzing";
  if (key === "waiting" || key === "pending_approval" || key === "needs_approval"
      || key === "waiting_for_execution" || key === "execution_ready") return "waiting_approval";
  if (key === "done" || key === "executed" || key === "success") return "completed";
  if (key === "failed" || key === "fail") return "error";
  return "idle";
}

/** True when the agent is currently doing or waiting for work. */
export function isAgentWorkBusy(phase) {
  const p = normalizeAgentWorkPhase(phase);
  return p === "analyzing" || p === "reviewing" || p === "executing";
}

/**
 * Build the human-readable single-line status text for the status bar.
 * Covers all spec examples: "DeepSeek prüft Datenfluss", "Claude prüft",
 * "OpenAI priorisiert", "Bereit zur Ausführung", "Blockiert", etc.
 *
 * @param {string} phase       - current phase (raw or normalised)
 * @param {string} agentLabel  - short agent label ("DeepSeek", "Claude", …)
 * @param {{ claude?: { checked?: boolean }, openai?: { approved?: boolean } }} [reviews]
 */
export function buildAgentStatusLine(phase, agentLabel, reviews) {
  const p = normalizeAgentWorkPhase(phase);
  const label = (typeof agentLabel === "string" && agentLabel.trim()) ? agentLabel.trim() : "Agent";
  const claudeChecked  = Boolean(reviews?.claude?.checked);
  const openAiApproved = Boolean(reviews?.openai?.approved);

  if (p === "waiting_approval") {
    if (claudeChecked && openAiApproved) return "Bereit zur Ausführung";
    if (claudeChecked)  return "Wartet auf OpenAI";
    return "Wartet auf Claude";
  }
  if (p === "blocked")    return "Blockiert";
  if (p === "analyzing")  return `${label} denkt nach`;
  if (p === "reviewing")  return `${label} bereitet Vorschlag vor`;
  if (p === "executing")  return `${label} führt aus`;
  if (p === "completed")  return "Abgeschlossen";
  if (p === "error")      return "Fehler";
  return `${label} bereit`;
}

// ── Messages ───────────────────────────────────────────────────────────────

const KNOWN_ROLES = new Set(["user", "agent", "system"]);

const AGENT_MESSAGE_FULL_TEXT_FIELDS = [
  "fullText",
  "assistantMessage",
  "reply",
  "message",
  "text",
  "content",
  "answer",
  "output",
  "raw",
  "result",
];

const AGENT_MESSAGE_DISPLAY_TEXT_FIELDS = [
  "displayText",
  "text",
  "content",
  "answer",
  "assistantMessage",
  "reply",
  "message",
  "fullText",
  "output",
  "raw",
  "result",
];

function pickFirstNonEmptyString(source, fields) {
  if (!source || typeof source !== "object") return "";
  for (const field of fields) {
    if (typeof source[field] !== "string") continue;
    const value = source[field].trim();
    if (value) return value;
  }
  return "";
}

function resolveNestedAgentMessageText(value, fields, depth = 0) {
  if (typeof value === "string") return value.trim();
  if (!value || typeof value !== "object" || depth >= 2) return "";
  for (const field of fields) {
    const next = resolveNestedAgentMessageText(value[field], fields, depth + 1);
    if (next) return next;
  }
  return "";
}

function pickPreferredNonEmptyString(source, fields) {
  if (!source || typeof source !== "object") return "";
  for (const field of fields) {
    const value = resolveNestedAgentMessageText(source[field], fields);
    if (value) return value;
  }
  return "";
}

export function resolveAgentMessageTextParts(source) {
  const fullText = pickPreferredNonEmptyString(source, AGENT_MESSAGE_FULL_TEXT_FIELDS);
  const displayText = pickFirstNonEmptyString(source, AGENT_MESSAGE_DISPLAY_TEXT_FIELDS) || fullText;
  const isTruncated = source?.isTruncated === true || (
    Boolean(fullText)
    && Boolean(displayText)
    && (displayText !== fullText || fullText.length > MAX_CHAT_TEXT_LENGTH)
  );
  return {
    fullText,
    displayText,
    isTruncated,
  };
}

/**
 * Normalise a single chat message coming from `/api/admin/agent-work/...`
 * Returns a stable shape independent of which agent produced it.
 *
 *   { id, role, agent, text, ts, kind, raw }
 *
 * `kind`:
 *   - "text"   – default narrative chat bubble
 *   - "status" – inline status update (rendered as small system note)
 *   - "result" – agent result summary
 *   - "error"  – inline error message
 */
export function normalizeAgentWorkMessage(raw, index = 0) {
  if (!raw || typeof raw !== "object") return null;

  const role = KNOWN_ROLES.has(raw.role) ? raw.role : (raw.from === "user" ? "user" : "agent");
  const { fullText, displayText, isTruncated } = resolveAgentMessageTextParts(raw);
  const text = displayText || fullText;

  // Build a stable id – the timeline relies on it for keying.
  const id = typeof raw.id === "string" && raw.id
    ? raw.id
    : `msg-${index}-${typeof raw.ts === "string" ? raw.ts : Date.now()}`;

  const agent = typeof raw.agent === "string" && raw.agent
    ? raw.agent.toLowerCase()
    : role === "user" ? "user" : "system";

  const kindRaw = typeof raw.kind === "string" ? raw.kind.toLowerCase() : "";
  const kind = ["text", "status", "result", "error", "handoff_started", "handoff_completed", "autonomy_run", "agent_dispatch"].includes(kindRaw) ? kindRaw : "text";
  const normalizedRaw = {
    ...raw,
    fullText: fullText || raw.fullText || null,
    displayText: displayText || raw.displayText || null,
    isTruncated,
  };

  return {
    id,
    role,
    agent,
    text: text.trim(),
    fullText: fullText || null,
    isTruncated,
    ts: typeof raw.ts === "string" ? raw.ts : null,
    kind,
    raw: normalizedRaw,
  };
}

/** Normalises an array of messages, dropping malformed entries. */
export function normalizeAgentWorkMessages(list) {
  if (!Array.isArray(list)) return [];
  const out = [];
  for (let i = 0; i < list.length; i += 1) {
    const m = normalizeAgentWorkMessage(list[i], i);
    if (m && (m.text || m.fullText || m.kind !== "text")) out.push(m);
  }
  return out;
}

// ── Primary action ─────────────────────────────────────────────────────────

/**
 * Every agent supports the same vocabulary of "primary actions". A view
 * only ever shows ONE big primary button at a time. The available action
 * is derived from the current phase – not from a free-form action list,
 * so the UI behaves identically for every agent.
 */
export const AGENT_PRIMARY_ACTIONS = {
  start_analysis:    { id: "start_analysis",    label: "Prüfung starten" },
  new_analysis:      { id: "new_analysis",      label: "Neue Prüfung starten" },
  prepare_proposal:  { id: "prepare_proposal",  label: "Vorschlag vorbereiten" },
  check_approval:    { id: "check_approval",    label: "Freigabe prüfen" },
  execute_approved:  { id: "execute_approved",  label: "Freigegebene Änderung ausführen" },
  refresh_status:    { id: "refresh_status",    label: "Status aktualisieren" },
  show_details:      { id: "show_details",      label: "Details anzeigen" },
};

/**
 * Decide which (at most ONE) primary action button is shown given the
 * current state. Returning `null` means "no big button right now".
 *
 *   phase                 → action id
 *   ──────────────────────────────────────────────
 *   idle                  → start_analysis
 *   analyzing/reviewing   → refresh_status        ("Status aktualisieren")
 *   waiting_approval      → execute_approved      (only if allowed=true)
 *   executing             → null (busy)
 *   completed             → new_analysis          ("Neue Analyse starten")
 *   blocked               → show_details          ("Details anzeigen")
 *   error                 → refresh_status
 *
 * `allowedActions` is an optional whitelist of action ids; if it is
 * provided and the derived id is not in it, no button is shown.
 */
export function derivePrimaryAction({ phase, allowedActions } = {}) {
  const p = normalizeAgentWorkPhase(phase);
  let candidate = null;
  if (p === "idle") candidate = "start_analysis";
  else if (p === "analyzing") candidate = "refresh_status";
  else if (p === "reviewing") candidate = "prepare_proposal";
  else if (p === "waiting_approval") candidate = "execute_approved";
  else if (p === "completed") candidate = "new_analysis";
  else if (p === "blocked")   candidate = "show_details";
  else if (p === "error")     candidate = "refresh_status";

  if (!candidate) return null;
  if (Array.isArray(allowedActions) && allowedActions.length > 0 && !allowedActions.includes(candidate)) {
    return null;
  }
  return AGENT_PRIMARY_ACTIONS[candidate] || null;
}

// ── "Don't ask the user for filenames" rule ────────────────────────────────

/**
 * Returns true when the user's natural-language chat command is a
 * generic, file-less work request like "prüfe den Datenfluss".
 *
 * Used by AgentWorkChat to suppress any UI state that would otherwise
 * ask the user to pick files/jobs/datasets manually. Instead the UI
 * shows the canonical message:
 *
 *   "Ich ermittle die betroffenen Dateien, Jobs und Datenflüsse selbst."
 */
export function isGenericWorkCommand(text) {
  if (typeof text !== "string") return false;
  const t = text.trim().toLowerCase();
  if (!t) return false;
  const patterns = [
    /\bprüfe\b/, /\bpruefe\b/,
    /\bdatenfluss\b/, /\bdaten\s*fluss\b/,
    /\bfinde\s+fehler\b/, /\bfehler\s+finden\b/,
    /\bführe\s+aus\b/, /\bfuehre\s+aus\b/,
    /\brepariere\b/, /\breparier\b/,
    /\bbereite?\s+(?:\w+\s+)?reparatur\b/,
    /\banalysiere\b/, /\banalyse\s+starten\b/,
    /\bfreigegeben/, /\bfreigegebenen\s+punkte\b/,
    /\baktualisiere\s+den?\s+status\b/,
  ];
  return patterns.some((re) => re.test(t));
}

/** Canonical "I'll figure out files myself" agent reply text. */
export const AGENT_SELF_DISCOVERY_TEXT =
  "Ich ermittle die betroffenen Dateien, Jobs und Datenflüsse selbst.";

/**
 * Returns true when the user's message is an explicit execution command –
 * i.e. they want to trigger the approved change via the backend.
 * These commands are forwarded to POST /api/admin/agent-work/chat; the UI
 * does NOT decide safety – that is the backend's responsibility.
 *
 * Covers the spec phrases:
 *   „führe aus" · „mach das" · „setze um" · „ändere die freigegebenen Punkte"
 */
export function isExecutionCommand(text) {
  if (typeof text !== "string") return false;
  const t = text.trim().toLowerCase();
  if (!t) return false;
  const patterns = [
    /\bführe\s+aus\b/, /\bfuehre\s+aus\b/,
    /\bmach\s+das\b/,
    /\bsetze\s+um\b/,
    // \b doesn't work for non-ASCII chars (ä), so use start/whitespace anchor.
    /(?:^|[\s,])änder(?:e|n)\s+die\s+freigegebenen?\s+punkte?(?:\b|$)/,
    /\baender(?:e|n)\s+die\s+freigegebenen?\s+punkte?\b/,
  ];
  return patterns.some((re) => re.test(t));
}

/**
 * Normalise the raw response body from POST …/execute-approved (or any
 * execution endpoint) into a display-ready shape for the chat view.
 *
 * Returns one of two shapes:
 *
 *   Success:
 *   { success: true,  text: string, events: string[], commitSha: string|null }
 *
 *   Blocked / error:
 *   { success: false, text: string, events: string[], commitSha: null }
 *
 * The `text` field is always a short, user-friendly German sentence.
 * No raw JSON, no GitHub metadata, no secrets.
 */
export function normalizeExecutionResult(data) {
  if (!data || typeof data !== "object") {
    return { success: false, text: "Ausführung konnte nicht gestartet werden.", events: [], commitSha: null };
  }

  const isSuccess = Boolean(
    data.success === true
    || data.status === "success"
    || data.status === "completed"
    || data.status === "executed",
  );
  const isBlocked = Boolean(
    data.blocked === true
    || data.status === "blocked"
    || data.status === "failed"
    || data.status === "error",
  );

  // Extract events (max 5, strings only).
  const events = Array.isArray(data.events)
    ? data.events
        .map((e) => (typeof e === "string" ? e : (e?.message || e?.description || "")))
        .filter(Boolean)
        .slice(0, 5)
    : [];

  if (isSuccess) {
    const rawSha = data.commitSha || data.commit_sha || data.sha || null;
    const commitSha = typeof rawSha === "string" && rawSha ? rawSha.slice(0, 7) : null;
    const base = "Fertig. Die freigegebene Änderung wurde auf main übernommen. Railway/Vercel sollte jetzt automatisch deployen.";
    const text = commitSha ? `${base} Commit: ${commitSha}` : base;
    return { success: true, text, events, commitSha };
  }

  if (isBlocked) {
    const reason = typeof data.reason === "string" && data.reason
      ? data.reason
      : typeof data.message === "string" && data.message
        ? data.message
        : "Unbekannte Ursache.";
    return { success: false, text: `Ausführung wurde gestoppt: ${reason}`, events, commitSha: null };
  }

  // Fallback for unexpected states.
  const fallbackText = typeof data.message === "string" && data.message
    ? data.message
    : "Ausführung abgeschlossen.";
  return { success: true, text: fallbackText, events, commitSha: null };
}

// ── Raw-data detection (rule #2 / #8) ──────────────────────────────────────

/**
 * Returns true when an item looks like raw/technical data that MUST NOT
 * appear in the main chat view but only inside the collapsible
 * "Details anzeigen" drawer.
 *
 * Examples: repair queues, audit-trails, claude review lists,
 * diagnostic tables, JSON dumps.
 */
export function isRawTechnicalDetail(item) {
  if (!item || typeof item !== "object") return false;
  const kind = typeof item.kind === "string" ? item.kind.toLowerCase() : "";
  const banned = new Set([
    "repair_queue",
    "data_flow_chain",
    "claude_review_list",
    "audit_trail",
    "diagnostics_table",
    "diagnostic_card",
    "raw_data",
    "raw",
    "table",
    "events_table",
  ]);
  return banned.has(kind);
}

// ── Intent ─────────────────────────────────────────────────────────────────

/**
 * Possible intent values returned by the backend for every chat response.
 *
 *   general_chat            – free conversation, no workflow
 *   system_question         – question about the system, no workflow
 *   diagnosis_request       – agent starts / continues analysis
 *   repair_planning_request – agent prepares a repair proposal
 *   execution_request       – agent executes an approved change
 */
export const AGENT_INTENT_LABELS_DE = {
  general_chat:            "Gespräch",
  system_question:         "Systemfrage",
  diagnosis_request:       "Analyse läuft",
  controlled_repair_preview: "Vorschlag wird vorbereitet",
  execution_request:       "Ausführung",
};

export const AGENT_WORK_CHAT_ERROR_TEXT =
  "Die Prüfung konnte gerade nicht abgeschlossen werden. Details findest du im Drawer.";

export const AGENT_LIVE_PROGRESS_AGENT_TEXTS_DE = {
  deepseek: "DeepSeek prüft Backend, Datenfluss und technische Ursachen …",
  claude:   "Claude prüft Risiko, Logik und Datenzusammenhänge …",
  gemini:   "Gemini prüft Oberfläche, Nutzerführung und Frontend-Kontext …",
  openai:   "OpenAI priorisiert, bewertet und fasst die Lage zusammen …",
  qwen:     "Qwen prüft Patchbarkeit und mögliche Codeänderungen …",
  v0:       "v0 prüft Design-Kontext …",
};

// ── v0 Design Agent – Timeout and Analyzing texts ─────────────────────────────

/**
 * Minimum client-side timeout for v0 + design_context requests.
 * Backend timeout for design_context is 90 s; frontend uses 110 s so the
 * browser AbortController never fires before the backend can respond.
 */
export const V0_DESIGN_CONTEXT_TIMEOUT_MS = 110_000;

/**
 * Minimum client-side timeout for v0 + full_system_context requests.
 * Backend timeout for full_system_context is 150 s; frontend uses 170 s so
 * the browser AbortController never fires before the backend can respond.
 */
export const V0_FULL_SYSTEM_CONTEXT_TIMEOUT_MS = 170_000;

/**
 * Status line shown in the chat header while v0 is processing a design request.
 * Replaces the generic "v0 denkt nach" to give users accurate context.
 */
export const V0_ANALYZING_STATUS_TEXT = "v0 prüft Design-Kontext …";

/**
 * Delayed hint shown after ~15 s to reassure the user that long v0 processing
 * is expected for UI analysis tasks.
 */
export const V0_ANALYZING_DELAY_TEXT = "Das kann bei UI-Analysen etwas dauern.";

// ── v0 Design Agent – UIChangeset + Qwen Handoff ────────────────────────────

/**
 * Short discreet hint lines shown below a v0 message when the backend
 * explicitly signals a design-context note (raw.designContextNote === true).
 * These are informational only – no fake data is created.
 */
export const V0_DESIGN_CONTEXT_HINT_LINES = [
  "v0 arbeitet mit geliefertem Systemkontext",
  "Kein Live-Dateizugriff",
  "Screenshot-/Designkontext kann später ergänzt werden",
];

/**
 * Label for the "transfer to Qwen" action button shown below structured v0 responses.
 */
export const V0_QWEN_HANDOFF_LABEL = "Qwen mit Umsetzung beauftragen";

/**
 * Tooltip / status text shown on the disabled Qwen-handoff button when the
 * backend has explicitly marked qwenHandoff === false.
 */
export const V0_QWEN_HANDOFF_COMING_SOON_TEXT = "Übergabe vom Backend nicht empfohlen";

/**
 * Button text shown while the handoff API call is in flight.
 */
export const V0_QWEN_HANDOFF_BUTTON_LOADING_TEXT = "Qwen-Auftrag wird erstellt...";

/**
 * Hint text shown in the handoff preview section below a structured UIChangeset.
 * Communicates to the user that Qwen can turn this changeset into an implementation task.
 */
export const V0_QWEN_HANDOFF_PREVIEW_HINT = "Qwen kann daraus einen Umsetzungsauftrag starten";

/**
 * Hint shown when the UIChangeset carries no suggested files.
 * Honest: we don't invent file names.
 */
export const V0_QWEN_HANDOFF_FILES_MISSING_TEXT = "Qwen sucht passende Dateien selbst";

/**
 * Chat bubble text shown immediately after the backend confirms a Qwen handoff task
 * was created (HTTP 202 + taskId).
 */
export const V0_QWEN_HANDOFF_STARTED_TEXT =
  "Qwen-Auftrag gestartet – Umsetzung läuft.";

/**
 * PendingAction summary label used while a Qwen handoff task is being polled.
 */
export const V0_QWEN_HANDOFF_PENDING_LABEL = "Qwen setzt v0-Vorschlag um";

/**
 * Status text shown in the chat when a Qwen handoff task is actively running,
 * to remind the user this is a follow-up on a v0 proposal.
 */
export const V0_QWEN_WORKING_TEXT = "Qwen arbeitet an v0-Vorschlag";

/**
 * Label shown on a completed Qwen result message that originated from a v0 handoff.
 * Lets the user trace the result back to the v0 proposal.
 */
export const V0_QWEN_RESULT_LABEL = "Qwen-Umsetzung zu v0-Vorschlag";

/**
 * Amber warning shown beneath a v0 message when the backend did NOT confirm
 * system context usage (systemContextUsed !== true). Not an error – v0 may
 * still answer correctly on design context alone, but the user should verify.
 */
export const V0_LIMITED_CONTEXT_WARNING =
  "Antwort basiert nur auf begrenztem Kontext. Vorschläge bitte prüfen.";

/**
 * Soft informational hint shown when v0 used Design-Kontext but no full system
 * context (systemContextUsed === false, designContextUsed === true).
 * Not a warning – design context is often sufficient for UI work.
 */
export const V0_DESIGN_CONTEXT_ONLY_HINT =
  "v0 nutzte Design-Kontext, aber keinen vollständigen Systemkontext.";

/**
 * Return the appropriate context notice for a v0 message.
 *
 * - systemContextUsed === false, designContextUsed === true
 *     → soft hint (V0_DESIGN_CONTEXT_ONLY_HINT)
 * - systemContextUsed === false, no design context
 *     → amber limited-context warning (V0_LIMITED_CONTEXT_WARNING)
 * - otherwise → null (no notice needed)
 *
 * @param {boolean|null} systemContextUsed
 * @param {boolean|null} designContextUsed
 * @returns {string|null}
 */
export function getV0ContextWarning(systemContextUsed, designContextUsed) {
  if (systemContextUsed !== false) return null;
  if (designContextUsed === true) return V0_DESIGN_CONTEXT_ONLY_HINT;
  return V0_LIMITED_CONTEXT_WARNING;
}

/**
 * Tooltip / title shown on the Qwen-handoff button when v0 only provided
 * freetext – no structured UIChangeset with component + goal is present.
 * The button is visible but disabled so the user understands why.
 */
export const V0_QWEN_HANDOFF_DISABLED_NO_CHANGESET =
  "Qwen-Handoff erst wenn UIChangeset und handoffReady vorhanden sind";

/**
 * Status label shown in the live-progress card when a v0 task ends in failure.
 * Replaces the generic "konnte den Arbeitslauf nicht abschließen" text so the
 * user immediately sees a clear, agent-specific failure heading.
 */
export const V0_TASK_FAILED_LABEL = "v0 fehlgeschlagen";

/**
 * Tooltip / disabled-button hint shown on the Qwen-handoff button when the
 * originating v0 task ended in failure (status === "failed").
 * Distinct from V0_QWEN_HANDOFF_DISABLED_NO_CHANGESET which covers the
 * freetext-only case.
 */
export const V0_HANDOFF_DISABLED_TASK_FAILED = "v0-Auftrag fehlgeschlagen";

/**
 * Split a flat list of known-issue strings into resolved vs. open items.
 *
 * An issue is considered resolved when its text contains a checkmark, which
 * matches the convention used in V0_KNOWN_ISSUES_V1.
 *
 * @param {string[]} issues
 * @returns {{ open: string[], resolved: string[] }}
 */
export function classifyV0KnownIssues(issues) {
  if (!Array.isArray(issues)) return { open: [], resolved: [] };
  const open = [];
  const resolved = [];
  for (const issue of issues) {
    const text = typeof issue === "string" ? issue : String(issue);
    if (text.includes("✓") || text.includes("✔")) {
      resolved.push(text);
    } else {
      open.push(text);
    }
  }
  return { open, resolved };
}

/**
 * Normalise a raw UIChangeset object from a v0 backend response into a
 * display-ready shape. Returns null when no changeset fields are present –
 * the UI must not invent data.
 *
 * Accepted input keys (camelCase or snake_case):
 *   component / affected_component / affectedComponent
 *   goal / design_goal / designGoal
 *   uxReason / ux_reason / justification
 *   files / suggested_files / suggestedFiles  (array of strings)
 *   priority                                  (string)
 *   qwenHandoff / qwen_handoff                (boolean)
 *
 * @param {any} raw - raw.uiChangeset from the backend message
 * @returns {{ component:string|null, goal:string|null, uxReason:string|null, files:string[], priority:string|null, qwenHandoff:boolean|null }|null}
 */
export function normalizeV0UIChangeset(raw) {
  if (!raw || typeof raw !== "object") return null;

  const component = (
    typeof raw.component === "string" ? raw.component :
    typeof raw.affectedComponent === "string" ? raw.affectedComponent :
    typeof raw.affected_component === "string" ? raw.affected_component : null
  )?.trim() || null;

  const goal = (
    typeof raw.goal === "string" ? raw.goal :
    typeof raw.designGoal === "string" ? raw.designGoal :
    typeof raw.design_goal === "string" ? raw.design_goal : null
  )?.trim() || null;

  const uxReason = (
    typeof raw.uxReason === "string" ? raw.uxReason :
    typeof raw.ux_reason === "string" ? raw.ux_reason :
    typeof raw.justification === "string" ? raw.justification : null
  )?.trim() || null;

  const rawFiles = raw.files ?? raw.suggestedFiles ?? raw.suggested_files;
  const files = Array.isArray(rawFiles)
    ? rawFiles.map((f) => (typeof f === "string" ? f.trim() : String(f).trim())).filter(Boolean).slice(0, 10)
    : [];

  const priority = (
    typeof raw.priority === "string" ? raw.priority : null
  )?.trim() || null;

  const rawQwenHandoff = raw.qwenHandoff ?? raw.qwen_handoff;
  const qwenHandoff = rawQwenHandoff === true ? true : rawQwenHandoff === false ? false : null;

  // Return null when absolutely no field was found – keep UI clean.
  if (!component && !goal && !uxReason && files.length === 0 && !priority && qwenHandoff === null) {
    return null;
  }

  return { component, goal, uxReason, files, priority, qwenHandoff };
}

/**
 * Returns true when the UIChangeset is structured enough to enable the
 * "transfer to Qwen" action – i.e. it has at least a component and a goal.
 *
 * @param {{ component:string|null, goal:string|null }|null} changeset
 * @returns {boolean}
 */
export function isV0UIChangesetStructured(changeset) {
  return Boolean(changeset && changeset.component && changeset.goal);
}

// ── v0 Design Context Feed V1 ────────────────────────────────────────────────

/** Title for the collapsible Design-Context panel shown in the v0 agent area. */
export const V0_DESIGN_CONTEXT_PANEL_TITLE = "Design-Kontext";

/**
 * Honest status text when no screenshot has been attached.
 * Never claim a screenshot was provided when none exists.
 */
export const V0_DESIGN_CONTEXT_NO_SCREENSHOT_TEXT = "Kein aktueller Screenshot übergeben";

/**
 * Honest status text when a screenshot is attached locally but the backend has
 * not yet confirmed receipt or processing.
 */
export const V0_SCREENSHOT_STATUS_ATTACHED =
  "Screenshot angehängt – Übergabe an v0 abhängig vom Backend";

/**
 * Honest status text when the screenshot data is actively being sent as part
 * of the v0 request payload.
 */
export const V0_SCREENSHOT_STATUS_SENDING = "Screenshot wird im v0-Kontext mitgesendet";

/**
 * @deprecated Use V0_DESIGN_CONTEXT_NO_SCREENSHOT_TEXT or V0_SCREENSHOT_STATUS_ATTACHED.
 * Kept for backwards compat with existing tests referencing the old placeholder.
 */
export const V0_DESIGN_CONTEXT_SCREENSHOT_PLACEHOLDER = "Screenshot-Kontext: noch nicht verbunden";

/**
 * Shown when no component documentation has been injected into the design
 * context yet. Honest – no fake component names are invented.
 */
export const V0_DESIGN_CONTEXT_NO_COMPONENTS_TEXT =
  "Noch keine vollständige Komponentendokumentation eingebunden";

// ── v0 Screenshot-Kontext V1 ──────────────────────────────────────────────────

// ── Screenshot-Status V2: Honest per-entry and per-result status texts ────────

/**
 * Status shown in the design context panel when a screenshot is locally
 * attached but no request has been sent yet.
 */
export const V0_SCREENSHOT_STATUS_LOCAL_ATTACHED = "Screenshot angehängt";

/**
 * Status shown (in design-context panel) when the screenshot entry has no
 * image data (e.g. file too large) – only metadata will be included in the
 * request payload.
 */
export const V0_SCREENSHOT_STATUS_METADATA_SENDING = "Nur Metadaten – bitte Screenshot erneut hochladen";

/**
 * Status shown (in design-context panel) when the screenshot entry includes
 * image data within the allowed size – the image content will be part of the
 * request payload so the backend can potentially forward it to the provider.
 */
export const V0_SCREENSHOT_STATUS_IMAGE_SENDING = "Bilddaten vorhanden";

/**
 * Status shown (in meta-footer) when the backend reports that the provider
 * does not support image input (screenshotProviderCapability === false).
 */
export const V0_SCREENSHOT_STATUS_PROVIDER_UNSUPPORTED = "v0 kann Bild nicht direkt sehen";

/**
 * Honest hint shown in the response meta-footer when screenshotContextUsed is
 * true but screenshotImageForwarded is false: metadata was sent but no visual
 * content was forwarded to the provider.
 */
export const V0_SCREENSHOT_META_ONLY_HINT =
  "Nur Metadaten – bitte Bild erneut hochladen.";

/**
 * Honest hint shown in the response meta-footer when screenshotImageForwarded
 * is true: the image content was passed to the provider as image input.
 */
export const V0_SCREENSHOT_FORWARDED_HINT =
  "Screenshot angehängt · Bilddaten vorhanden · Bildinput an v0 übergeben.";

/**
 * Determine the display status text for a screenshot entry shown in the
 * design-context panel (pre-send state).
 *
 * Decision tree:
 *   - "attached" status + image data within size → V0_SCREENSHOT_STATUS_IMAGE_SENDING
 *   - "attached" status + no image data / too large → V0_SCREENSHOT_STATUS_METADATA_SENDING
 *   - fallback → V0_SCREENSHOT_STATUS_LOCAL_ATTACHED
 *
 * @param {object|null} entry – a createScreenshotEntry result
 * @returns {string}
 */
export function getScreenshotEntryDisplayStatus(entry) {
  if (!entry || typeof entry !== "object") return V0_SCREENSHOT_STATUS_LOCAL_ATTACHED;
  const hasImageData =
    (typeof entry.dataUrl === "string" && entry.dataUrl.startsWith("data:")) ||
    (typeof entry.previewUrl === "string" && entry.previewUrl.startsWith("data:"));
  if (hasImageData) return V0_SCREENSHOT_STATUS_IMAGE_SENDING;
  if (entry.size > 0) return V0_SCREENSHOT_STATUS_METADATA_SENDING;
  return V0_SCREENSHOT_STATUS_LOCAL_ATTACHED;
}

/**
 * Status text shown when the backend confirms the screenshot image was
 * forwarded to the provider as a real image input.
 */
export const V0_SCREENSHOT_BACKEND_IMAGE_FORWARDED =
  "Screenshot angehängt · Bilddaten vorhanden · Bildinput an v0 übergeben";

/**
 * Status text shown when the backend confirms the screenshot was attached
 * but the image content was NOT forwarded – only metadata was sent.
 */
export const V0_SCREENSHOT_BACKEND_METADATA_ONLY =
  "Nur Metadaten – bitte Bild erneut hochladen";

/**
 * Status text shown when the backend reports the provider does not support
 * real image inputs (supportsImageInput === false).
 */
export const V0_SCREENSHOT_BACKEND_PROVIDER_UNSUPPORTED =
  "v0 kann Bild nicht direkt sehen";

/**
 * Compact hint shown near the upload button to inform the user that the
 * current provider processes only metadata, not the actual image content.
 */
export const V0_SCREENSHOT_UPLOAD_METADATA_ONLY_HINT =
  "Nur Metadaten – bitte Screenshot erneut hochladen";

/** Backend/UI status: v0 used screenshot-derived visual context. */
export const V0_VISUAL_CONTEXT_USED_TEXT = "v0 nutzt visuellen Kontext";

/** Backend/UI status: v0 uses a summary instead of direct image access. */
export const V0_VISUAL_CONTEXT_SUMMARY_ONLY_TEXT = "v0 nutzt visuelle Zusammenfassung, kein direktes Bild";

/** Backend/UI status: generated design image is available. */
export const V0_IMAGE_GENERATION_AVAILABLE_TEXT = "Designbild-Erstellung verfügbar";

/** Backend/UI status: generated design image is not available. */
export const V0_IMAGE_GENERATION_UNAVAILABLE_TEXT = "Designbild-Erstellung nicht verfügbar";

/**
 * Resolves whether the provider supports image input from a value that may be
 * a boolean OR an object with a `supportsImageInput` property (backend format).
 *
 * @param {boolean|{ supportsImageInput?: boolean }|null|undefined} cap
 * @returns {boolean|null} – true (supported), false (unsupported), null (unknown)
 */
export function resolveProviderSupportsImageInput(cap) {
  if (cap === null || cap === undefined) return null;
  if (typeof cap === "boolean") return cap;
  if (typeof cap === "object" && "supportsImageInput" in cap) {
    return cap.supportsImageInput === true;
  }
  return null;
}

/**
 * Derive a human-readable backend screenshot status from the meta fields
 * collected in the AgentMessageTimeline response footer.
 *
 * Returns null when there is no relevant data (old empty state stays correct).
 *
 * Decision tree (priority order):
 *  1. screenshotImageForwarded === true → BACKEND_IMAGE_FORWARDED
 *  2. screenshotProviderCapability resolves to false → BACKEND_PROVIDER_UNSUPPORTED
 *  3. screenshotContextUsed === true && screenshotImageForwarded === false → BACKEND_METADATA_ONLY
 *  4. otherwise → null
 *
 * @param {object|null} fields – the metaFields object (from AgentMessageTimeline)
 * @returns {string|null}
 */
export function getBackendScreenshotStatus(fields) {
  if (!fields || typeof fields !== "object") return null;
  if (fields.hasScreenshotDataUrl === false) return V0_SCREENSHOT_BACKEND_METADATA_ONLY;
  if (fields.screenshotImageForwarded === true) return V0_SCREENSHOT_BACKEND_IMAGE_FORWARDED;
  const supported = resolveProviderSupportsImageInput(
    fields.supportsDirectImageInput ?? fields.screenshotProviderCapability ?? null,
  );
  if (supported === false && fields.supportsVisualContextSummary === true) {
    return V0_VISUAL_CONTEXT_SUMMARY_ONLY_TEXT;
  }
  if (supported === false) return V0_SCREENSHOT_BACKEND_PROVIDER_UNSUPPORTED;
  if (
    fields.screenshotContextUsed === true &&
    fields.screenshotImageForwarded === false
  ) {
    return V0_SCREENSHOT_BACKEND_METADATA_ONLY;
  }
  return null;
}

/** Accepted image MIME types for screenshot upload. */
export const V0_SCREENSHOT_ACCEPTED_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
];

/** Accepted file extensions (for the file input accept attribute). */
export const V0_SCREENSHOT_ACCEPTED_EXTENSIONS = ".png,.jpg,.jpeg,.webp";

/**
 * Maximum screenshot file size in bytes (2 MiB).
 * Files larger than this are rejected to protect request size.
 */
export const V0_SCREENSHOT_MAX_SIZE_BYTES = 2 * 1024 * 1024;

// ── Screenshot-Komprimierung V1 ───────────────────────────────────────────────

/** Longest edge in px that the compressed image may have. */
export const SCREENSHOT_COMPRESSION_MAX_PX = 1280;

/** JPEG/WebP quality used when exporting the compressed canvas (0–1). */
export const SCREENSHOT_COMPRESSION_QUALITY = 0.82;

/**
 * Maximum allowed dataUrl string length after compression.
 * ~750 KB of characters ≈ ~547 KB of raw image bytes (base64 overhead).
 * Images whose compressed dataUrl exceeds this limit are rejected.
 */
export const SCREENSHOT_MAX_DATAURL_BYTES = 750 * 1024;

/** Maximum number of image attachments per chat message. */
export const SCREENSHOT_MAX_PER_MESSAGE = 3;

/** Label shown in the attachment chip when a screenshot was compressed. */
export const SCREENSHOT_COMPRESSED_LABEL = "optimiert";

/**
 * Error message shown when a screenshot is still too large after compression.
 */
export const SCREENSHOT_TOO_LARGE_ERROR =
  "Bild ist noch zu groß. Bitte Screenshot kleiner zuschneiden oder erneut versuchen.";

/**
 * Compress a screenshot dataUrl via the browser Canvas API.
 *
 * - Rescales so the longest edge ≤ SCREENSHOT_COMPRESSION_MAX_PX.
 * - Exports as image/jpeg at SCREENSHOT_COMPRESSION_QUALITY.
 * - Degrades gracefully in non-browser environments (returns the source as-is).
 *
 * @param {string} sourceDataUrl   – data URL of the original image
 * @param {string} [sourceMimeType] – MIME type of the original image
 * @returns {Promise<{
 *   dataUrl: string,
 *   mimeType: string,
 *   size: number,
 *   width: number,
 *   height: number,
 *   originalWidth: number,
 *   originalHeight: number,
 *   compressed: boolean,
 *   error?: string,
 * }>}
 */
export function compressScreenshotImage(sourceDataUrl, sourceMimeType) {
  const outMime = "image/jpeg";
  // Non-browser environment (Node.js / tests): return source unchanged
  if (
    typeof document === "undefined" ||
    typeof document.createElement !== "function"
  ) {
    const commaIdx = typeof sourceDataUrl === "string" ? sourceDataUrl.indexOf(",") : -1;
    const byteCount = commaIdx >= 0
      ? Math.round((sourceDataUrl.length - commaIdx - 1) * 0.75)
      : 0;
    return Promise.resolve({
      dataUrl: sourceDataUrl || "",
      mimeType: sourceMimeType || outMime,
      size: byteCount,
      width: 0,
      height: 0,
      originalWidth: 0,
      originalHeight: 0,
      compressed: false,
    });
  }
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const origW = img.naturalWidth;
      const origH = img.naturalHeight;
      const maxPx = SCREENSHOT_COMPRESSION_MAX_PX;
      let w = origW;
      let h = origH;
      if (w > maxPx || h > maxPx) {
        if (w >= h) { h = Math.round((h / w) * maxPx); w = maxPx; }
        else { w = Math.round((w / h) * maxPx); h = maxPx; }
      }
      try {
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL(outMime, SCREENSHOT_COMPRESSION_QUALITY);
        const commaIdx = dataUrl.indexOf(",");
        const byteCount = commaIdx >= 0
          ? Math.round((dataUrl.length - commaIdx - 1) * 0.75)
          : 0;
        resolve({
          dataUrl,
          mimeType: outMime,
          size: byteCount,
          width: w,
          height: h,
          originalWidth: origW,
          originalHeight: origH,
          compressed: w !== origW || h !== origH || sourceMimeType !== outMime,
        });
      } catch {
        // Canvas blocked or unavailable – return source
        resolve({
          dataUrl: sourceDataUrl,
          mimeType: sourceMimeType || outMime,
          size: 0,
          width: origW,
          height: origH,
          originalWidth: origW,
          originalHeight: origH,
          compressed: false,
          error: "Komprimierung fehlgeschlagen.",
        });
      }
    };
    img.onerror = () => {
      resolve({
        dataUrl: sourceDataUrl,
        mimeType: sourceMimeType || outMime,
        size: 0,
        width: 0,
        height: 0,
        originalWidth: 0,
        originalHeight: 0,
        compressed: false,
        error: "Bild konnte nicht geladen werden.",
      });
    };
    img.src = sourceDataUrl;
  });
}

/**
 * Validate a screenshot File object.
 *
 * Returns an object:
 *   { ok: true }                   – file is accepted
 *   { ok: false, reason: string }  – file is rejected; reason is a human-readable German message
 *
 * @param {{ name: string, type: string, size: number }|null} file
 * @returns {{ ok: boolean, reason?: string }}
 */
export function validateScreenshotFile(file) {
  if (!file || typeof file !== "object") {
    return { ok: false, reason: "Keine Datei ausgewählt." };
  }
  if (!V0_SCREENSHOT_ACCEPTED_MIME_TYPES.includes(file.type)) {
    return {
      ok: false,
      reason: `Dateiformat nicht unterstützt (${file.type || "unbekannt"}). Erlaubt: PNG, JPG, WEBP.`,
    };
  }
  if (typeof file.size === "number" && file.size > V0_SCREENSHOT_MAX_SIZE_BYTES) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      ok: false,
      reason: `Datei zu groß (${sizeMB} MB). Maximal ${V0_SCREENSHOT_MAX_SIZE_BYTES / (1024 * 1024)} MB.`,
    };
  }
  return { ok: true };
}

/**
 * Create a screenshot entry for designContext.screenshots[].
 *
 * Shape:
 *   {
 *     id:          string  – unique identifier (crypto.randomUUID or fallback)
 *     name:        string  – original file name
 *     source:      "upload"
 *     previewUrl:  string  – data URL for in-panel preview
 *     mimeType:    string  – e.g. "image/png"
 *     size:        number  – file size in bytes
 *     attachedAt:  string  – ISO timestamp
 *     note:        string  – empty by default
 *     status:      "attached"
 *   }
 *
 * @param {{ name: string, type: string, size: number }} file
 * @param {string} dataUrl  – result of FileReader.readAsDataURL (may be compressed)
 * @param {{
 *   compressed?: boolean,
 *   compressionRatio?: number,
 *   originalSize?: number,
 *   originalMimeType?: string,
 *   originalWidth?: number,
 *   originalHeight?: number,
 *   width?: number,
 *   height?: number,
 * }|null} [compressionMeta]  – optional metadata from compressScreenshotImage; omit for uncompressed entries
 * @returns {object}
 */
export function createScreenshotEntry(file, dataUrl, compressionMeta = null) {
  const id =
    typeof globalThis?.crypto?.randomUUID === "function"
      ? globalThis.crypto.randomUUID()
      : `sc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const entry = {
    id,
    name:       typeof file?.name === "string" ? file.name : "screenshot",
    source:     "upload",
    dataUrl:    typeof dataUrl === "string" ? dataUrl : "",
    previewUrl: typeof dataUrl === "string" ? dataUrl : "",
    mimeType:   typeof file?.type === "string" ? file.type : "image/png",
    size:       typeof file?.size === "number" ? file.size : 0,
    attachedAt: new Date().toISOString(),
    note:       "",
    status:     "attached",
  };
  if (compressionMeta && typeof compressionMeta === "object") {
    entry.compressed      = compressionMeta.compressed === true;
    entry.compressionRatio =
      typeof compressionMeta.compressionRatio === "number" ? compressionMeta.compressionRatio : null;
    entry.originalSize    =
      typeof compressionMeta.originalSize === "number" ? compressionMeta.originalSize : null;
    entry.originalMimeType =
      typeof compressionMeta.originalMimeType === "string" ? compressionMeta.originalMimeType : null;
    entry.originalWidth   =
      typeof compressionMeta.originalWidth === "number" ? compressionMeta.originalWidth : null;
    entry.originalHeight  =
      typeof compressionMeta.originalHeight === "number" ? compressionMeta.originalHeight : null;
    entry.width           =
      typeof compressionMeta.width === "number" ? compressionMeta.width : null;
    entry.height          =
      typeof compressionMeta.height === "number" ? compressionMeta.height : null;
  }
  return entry;
}

/**
 * Build the payload representation of a single screenshot entry.
 *
 * - If the dataUrl is small enough (≤ V0_SCREENSHOT_MAX_SIZE_BYTES in base64 equivalent),
 *   it is included as `dataUrl` so the backend can optionally pass it to the provider.
 * - Otherwise only metadata is included and `dataUrl` is omitted.
 * - `previewUrl` is never included in the payload (frontend-only).
 * - `status` is promoted to "sending" in the payload.
 *
 * No claim is made that the backend will actually use the image data.
 *
 * @param {object} entry  – a createScreenshotEntry result
 * @returns {object}      – payload-safe object
 */
export function buildScreenshotPayloadEntry(entry) {
  if (!entry || typeof entry !== "object") return null;

  const base = {
    id:         entry.id,
    name:       entry.name,
    source:     entry.source,
    mimeType:   entry.mimeType,
    size:       entry.size,
    attachedAt: entry.attachedAt,
    note:       entry.note || "",
    status:     "sending",
  };

  const dataUrl = typeof entry.dataUrl === "string" && entry.dataUrl.startsWith("data:")
    ? entry.dataUrl
    : typeof entry.previewUrl === "string" && entry.previewUrl.startsWith("data:")
      ? entry.previewUrl
      : "";
  if (dataUrl) base.dataUrl = dataUrl;

  return base;
}

/**
 * Build the direct chat attachment payload sent as top-level screenshots[].
 *
 * @param {object} entry
 * @returns {{ name: string, mimeType: string, size: number, dataUrl: string }|null}
 */
export function buildChatScreenshotPayloadEntry(entry) {
  if (!entry || typeof entry !== "object") return null;
  const dataUrl = typeof entry.dataUrl === "string" && entry.dataUrl.startsWith("data:")
    ? entry.dataUrl
    : typeof entry.previewUrl === "string" && entry.previewUrl.startsWith("data:")
      ? entry.previewUrl
      : "";
  if (!dataUrl) return null;
  return {
    name:     typeof entry.name === "string" ? entry.name : "screenshot",
    mimeType: typeof entry.mimeType === "string" ? entry.mimeType : "image/png",
    size:     typeof entry.size === "number" ? entry.size : 0,
    dataUrl,
  };
}

// ── V1: Komponenten-Kontext ───────────────────────────────────────────────────

/**
 * Known UI components in the HQS frontend.
 * Only components that verifiably exist in the source are listed here.
 * Each entry describes: name, purpose, area, notes.
 *
 * @type {Array<{ name: string, purpose: string, area: string, notes: string }>}
 */
export const V0_KNOWN_COMPONENTS_V1 = [
  {
    name:    "AdminAgentWorkChatView",
    purpose: "Haupt-Agenten-Arbeitsbereich; orchestriert alle Chat-, Workflow- und Polling-Logik",
    area:    "admin/agent-work",
    notes:   "Enthält v0-Design-Kontext-Panel, WorkflowStateBadge-Reset, v0→Qwen-Handoff",
  },
  {
    name:    "AgentWorkChat",
    purpose: "Chat-Interface-Komponente mit Nachrichten-Timeline, Schnellaktionen und PendingActionCard",
    area:    "agent-work",
    notes:   "Rendert AgentMessageTimeline, PendingActionCard, PendingActionEmptyState",
  },
  {
    name:    "AgentMessageTimeline",
    purpose: "Nachrichtenverlauf; rendert ChatBubbles mit v0-UIChangeset und v0-DesignContextHint",
    area:    "agent-work",
    notes:   "ChatBubble ist interner Sub-Component mit Inline-Expansion und Meta-Footer",
  },
  {
    name:    "ChatBubble",
    purpose: "Einzelne Agent-Nachricht; erweiterbar, kopierbarer vollständiger Text, Meta-Footer",
    area:    "agent-work",
    notes:   "Interner Sub-Component in AgentMessageTimeline; nicht eigenständig exportiert",
  },
  {
    name:    "AgentLiveHealthPanel",
    purpose: "Live-Systemstatus-Check; startet Health-Run auf Anforderung",
    area:    "agent-work",
    notes:   "In AdminAgentWorkChatView über dem Design-Kontext-Panel",
  },
  {
    name:    "DataSourceStatus",
    purpose: "Zeigt Datenstatus transparent an (aktiv / stale / empty)",
    area:    "shared",
    notes:   "Verhindert Fake-Daten-Anzeige; Stale/Empty-Zustand ehrlich dargestellt",
  },
  {
    name:    "PendingActionCard",
    purpose: "Zeigt ausstehende Agentenaktionen an und ermöglicht Ausführung",
    area:    "agent-work",
    notes:   "Interner Component in AgentWorkChat; inkl. PendingActionCompletionCard und PendingActionEmptyState",
  },
  {
    name:    "AgentDetailsDrawer",
    purpose: "Einklappbare Detailansicht für Agent-Antworten und Systemkontext",
    area:    "agent-work",
    notes:   "Drawer-Muster; enthält Kopierbutton für vollständige Antworten",
  },
  {
    name:    "ForecastVsOutcomeSection",
    purpose: "Vergleicht Prognosen mit tatsächlichen Ergebnissen; zeigt Qualitäts-KPIs",
    area:    "admin-review",
    notes:   "Interner Component in AdminReviewView",
  },
  {
    name:    "SymbolDetailPanel",
    purpose: "Detailansicht für ein einzelnes Symbol/Wertpapier im Review-Bereich",
    area:    "admin-review",
    notes:   "Interner Component in AdminReviewView; einklappbar",
  },
  {
    name:    "GuardianSection",
    purpose: "Guardian-Monitoring-Bereich; zeigt Trust-Pulse und Portfolio-Schutzstatus",
    area:    "companion",
    notes:   "Interner Component in CompanionView",
  },
];

// ── V1: Design-Tokens / Style-Kontext ────────────────────────────────────────

/**
 * Real CSS custom properties and Tailwind design tokens used in the HQS frontend.
 * All values are taken directly from src/index.css and tailwind.config.js.
 * No invented tokens – only documented, existing values.
 */
export const V0_DESIGN_TOKENS_V1 = [
  "CSS: --hqs-depth-0..5 (Oberflächen-Tiefenscala: #020810 → #172542)",
  "CSS: --hqs-status-positive (#10b981), neutral (#64748b), caution (#f59e0b), critical (#ef4444)",
  "CSS: --hqs-status-guarded (#8b5cf6), blocked (#f43f5e), stopped (#475569)",
  "CSS: --hqs-confidence-high (#10b981), medium (#f59e0b), low (#f43f5e)",
  "Tailwind: hqs.cyan (#22d3ee), hqs.gold (#facc15), hqs.purple (#a855f7), hqs.blue (#3b82f6)",
  "Tailwind: Schriften – DM Sans (sans), JetBrains Mono (mono), Space Grotesk (display)",
  "Tailwind: CSS-Klassen – .hqs-surface, .hqs-surface-raised, .hqs-focus-card (in index.css)",
  "Hintergrund: radial-gradient Deep-Space dark-first (#050c1a / #020810)",
];

// ── V1: Layout-Regeln ────────────────────────────────────────────────────────

/**
 * Known layout and UX principles for the HQS agent areas.
 * These rules guide v0 when suggesting or implementing UI changes.
 */
export const V0_LAYOUT_RULES_V1 = [
  "Chat-first: der Chat-Bereich ist immer der primäre Interaktionsbereich",
  "Wenig Tabs: keine überflüssige Tab-Navigation in Agentenbereichen",
  "Details einklappbar: Drawer und Meta-Footer sind standardmäßig zugeklappt",
  "Mobile lesbar: Tailwind responsive Breakpoints; keine horizontale Überladung",
  "Lange Antworten vollständig öffn-/kopierbar: Inline-Expansion mit Kopierbutton",
  "Keine Fake-Daten: nur echte Backend-Daten anzeigen, Platzhalter ehrlich kennzeichnen",
  "Keine überladenen Karten: kompakte Darstellung, Infos auf das Nötigste beschränken",
  "Dark-first: alle Oberflächen auf Basis der --hqs-depth-* Skala",
];

// ── V1: Known Issues ─────────────────────────────────────────────────────────

/**
 * Known UI/UX issues and their current status.
 * Listed as context/notes – not as new tasks.
 */
export const V0_KNOWN_ISSUES_V1 = [
  "Lange Agentenantworten: vollständig einsehbar – Inline-Expansion implementiert ✓",
  "Ghost-Actions: behoben – sessionPendingAction wird nach echter Antwort geleert ✓",
  "WorkflowStateBadge: Auto-Reset implementiert (2 s completed, 4 s failed/cancelled) ✓",
  "DataSourceStatus: stale/empty transparent angezeigt ✓",
  "v0→Qwen Handoff: implementiert (POST /api/admin/agent-work/handoff, taskId-Polling) ✓",
  "Screenshot-Kontext: Upload/Chat-Attachment mit dataUrl implementiert ✔",
];

// ── V1: Factory ───────────────────────────────────────────────────────────────

/**
 * Create a fresh, empty design-context object.
 *
 * Fields:
 *   components    – known UI component objects or names
 *   designTokens  – known design-token descriptions (string[])
 *   layoutRules   – known layout rules / constraints (string[])
 *   knownIssues   – known UI/UX issues and their status (string[])
 *   screenshots   – screenshot references (currently always empty; placeholder only)
 *   targetView    – name of the target view/page (string | null)
 *
 * No data is ever invented. All arrays start empty; the UI displays honest
 * "not yet connected" placeholders when fields are empty.
 *
 * @returns {{ components: Array, designTokens: string[], layoutRules: string[], knownIssues: string[], screenshots: string[], targetView: string|null }}
 */
export function createEmptyDesignContext() {
  return {
    components:   [],
    designTokens: [],
    layoutRules:  [],
    knownIssues:  [],
    screenshots:  [],
    targetView:   null,
  };
}

/**
 * Create a design-context pre-filled with V1 data.
 *
 * Uses only real, verified components, tokens, and rules from the HQS frontend.
 * No data is invented. Screenshots remain empty (not yet connected).
 *
 * @returns {{ components: Array<{name:string,purpose:string,area:string,notes:string}>, designTokens: string[], layoutRules: string[], knownIssues: string[], screenshots: string[], targetView: string|null }}
 */
export function createV0DesignContextV1() {
  return {
    components:   V0_KNOWN_COMPONENTS_V1,
    designTokens: V0_DESIGN_TOKENS_V1,
    layoutRules:  V0_LAYOUT_RULES_V1,
    knownIssues:  V0_KNOWN_ISSUES_V1,
    screenshots:  [],
    targetView:   null,
  };
}

/**
 * Build a minimal, payload-ready design-context object for v0 requests.
 *
 * Only non-empty fields are included so the backend payload stays lean.
 * Returns null when every field is empty – in that case the caller must
 * NOT attach `designContext` to the request at all (no fake data).
 *
 * Screenshots are passed through buildScreenshotPayloadEntry so that:
 *   – previewUrl (frontend-only) is stripped
 *   – dataUrl is included only when within the size limit
 *   – status is set to "sending" for each entry
 *
 * @param {{ components: Array, designTokens: string[], layoutRules: string[], knownIssues: string[], screenshots: Array, targetView: string|null }} ctx
 * @returns {object|null}
 */
export function buildV0DesignContextPayload(ctx) {
  if (!ctx || typeof ctx !== "object") return null;

  const payload = {};
  if (Array.isArray(ctx.components)   && ctx.components.length > 0)   payload.components   = ctx.components;
  if (Array.isArray(ctx.designTokens) && ctx.designTokens.length > 0) payload.designTokens = ctx.designTokens;
  if (Array.isArray(ctx.layoutRules)  && ctx.layoutRules.length > 0)  payload.layoutRules  = ctx.layoutRules;
  if (Array.isArray(ctx.knownIssues)  && ctx.knownIssues.length > 0)  payload.knownIssues  = ctx.knownIssues;
  if (Array.isArray(ctx.screenshots)  && ctx.screenshots.length > 0) {
    const entries = ctx.screenshots
      .map(buildScreenshotPayloadEntry)
      .filter(Boolean);
    if (entries.length > 0) payload.screenshots = entries;
  }
  if (typeof ctx.targetView === "string" && ctx.targetView.trim())     payload.targetView   = ctx.targetView.trim();

  return Object.keys(payload).length > 0 ? payload : null;
}

/**
 * Build the shared HQS system context block forwarded to every agent.
 *
 * This block mirrors the currently visible frontend/design context so agents do
 * not depend on stale, provider-side memory when evaluating UX state.
 *
 * @param {{ agentName?: string, message?: string, designContext?: object, chatContext?: object, visualContext?: object, screenshots?: Array }} params
 * @returns {object|null}
 */
export function buildHqsSystemContextBlock(params = {}) {
  if (!params || typeof params !== "object") return null;

  const designContext = params.designContext && typeof params.designContext === "object" && !Array.isArray(params.designContext)
    ? params.designContext
    : null;
  const designPayload = designContext ? buildV0DesignContextPayload(designContext) : null;
  const chatContext = params.chatContext && typeof params.chatContext === "object" && !Array.isArray(params.chatContext)
    ? params.chatContext
    : null;
  const visualContext = params.visualContext && typeof params.visualContext === "object" && !Array.isArray(params.visualContext)
    ? params.visualContext
    : null;
  const screenshots = Array.isArray(params.screenshots) ? params.screenshots : [];
  const issueSplit = classifyV0KnownIssues(designPayload?.knownIssues || []);

  const block = {
    schemaVersion: 1,
    kind: "hqs_system_context_block",
    source: "agent_work_frontend",
    agentName: typeof params.agentName === "string" && params.agentName ? params.agentName : null,
    currentMessage: trimChatContextText(params.message),
    contextFreshness: "current_frontend_state",
    instruction:
      "Nutze diesen hqsSystemContextBlock als aktuellen Systemzustand. Markiere fehlende Informationen offen, statt veralteten oder nicht gelieferten Kontext anzunehmen.",
  };

  if (designPayload) {
    block.frontend = {
      components: designPayload.components || [],
      designTokens: designPayload.designTokens || [],
      layoutRules: designPayload.layoutRules || [],
      knownIssues: {
        open: issueSplit.open,
        resolved: issueSplit.resolved,
      },
      targetView: designPayload.targetView || null,
    };
  }
  if (chatContext) {
    block.chat = {
      hasPriorContext: chatContext.hasPriorContext === true,
      topicContinuity: typeof chatContext.topicContinuity === "string" ? chatContext.topicContinuity : null,
      shouldContinueCurrentTopic: chatContext.shouldContinueCurrentTopic === true,
      lastAgentMessage: chatContext.lastAgentMessage || null,
      previousMessages: Array.isArray(chatContext.previousMessages) ? chatContext.previousMessages : [],
    };
  }
  if (visualContext) {
    block.visualContext = visualContext;
  }
  if (screenshots.length > 0) {
    block.screenshots = screenshots.map((entry) => ({
      name: typeof entry?.name === "string" ? entry.name : "screenshot",
      mimeType: typeof entry?.mimeType === "string" ? entry.mimeType : null,
      size: typeof entry?.size === "number" ? entry.size : 0,
      hasDataUrl: typeof entry?.dataUrl === "string" && entry.dataUrl.startsWith("data:"),
    }));
  }

  return block.frontend || block.chat || block.visualContext || block.screenshots ? block : null;
}

export const AGENT_LIVE_PROGRESS_PHASES_DE = {
  system_question: [
    "Anfrage empfangen",
    "Systemkontext wird erkannt",
    "Datenfluss wird geprüft",
    "Tabellen, Jobs und Routen werden geprüft",
    "Antwort wird vorbereitet",
  ],
  diagnosis_request: [
    "Anfrage empfangen",
    "Systemkontext wird erkannt",
    "Datenfluss wird geprüft",
    "Tabellen, Jobs und Routen werden geprüft",
    "Antwort wird vorbereitet",
  ],
  repair_planning_request: [
    "Anfrage empfangen",
    "Betroffene Komponenten werden gesucht",
    "Risiko und Zuständigkeit werden geprüft",
    "Vorschlag wird vorbereitet",
    "Freigabeweg wird geprüft",
  ],
  controlled_repair_preview: [
    "Vorschlag wird vorbereitet",
    "WorkOrder gefunden/erstellt",
    "Freigabe erforderlich",
    "Scope erlaubt",
    "Safety geprüft",
    "Bereit zur Freigabe",
  ],
  execution_request: [
    "Anfrage empfangen",
    "Freigabestatus wird geprüft",
    "SafetyGate wird geprüft",
    "Ausführung wird vorbereitet",
    "Ergebnis wird erwartet",
  ],
  general_chat: [
    "Anfrage empfangen",
    "Antwort wird vorbereitet",
  ],
};

export const AGENT_BACKEND_WORKFLOW_PROGRESS_STEPS_DE = [
  "Prüfung läuft…",
  "Datenfluss wird geprüft…",
  "Jobs werden geprüft…",
  "Routen werden geprüft…",
  "Ergebnis wird vorbereitet…",
];

export const AGENT_RUN_MODE_STATUS_LABELS_DE = {
  fast_chat: "Schnelle Antwort wird erstellt",
  design_review: "Designvorschlag wird erstellt",
  visual_analysis: "Screenshot wird analysiert",
  design_generation: "Designbild wird erstellt",
  implementation_handoff: "Umsetzung wird vorbereitet",
};

export function normalizeAgentRunMode(mode) {
  if (typeof mode !== "string") return null;
  const key = mode.trim().toLowerCase().replace(/[\s-]/g, "_");
  if (!key) return null;
  if (Object.hasOwn(AGENT_RUN_MODE_STATUS_LABELS_DE, key)) return key;
  if (key === "chat" || key === "general_chat") return "fast_chat";
  if (key === "design_proposal") return "design_review";
  if (key === "screenshot_analysis") return "visual_analysis";
  if (key === "image_generation" || key === "mockup_generation") return "design_generation";
  if (key === "qwen_handoff" || key === "handoff") return "implementation_handoff";
  return null;
}

export function buildAgentRunModeStatusLine(mode) {
  const normalized = normalizeAgentRunMode(mode);
  if (!normalized) return null;
  return AGENT_RUN_MODE_STATUS_LABELS_DE[normalized] || null;
}

const BACKEND_WORKFLOW_STEP_LABELS_DE = {
  init: "Prüfung läuft…",
  start: "Prüfung läuft…",
  started: "Prüfung läuft…",
  running: "Prüfung läuft…",
  checking: "Prüfung läuft…",
  diagnose: "Prüfung läuft…",
  diagnosis: "Prüfung läuft…",
  system: "Prüfung läuft…",
  system_check: "Prüfung läuft…",
  dataflow: "Datenfluss wird geprüft…",
  data_flow: "Datenfluss wird geprüft…",
  data_flows: "Datenfluss wird geprüft…",
  datenfluss: "Datenfluss wird geprüft…",
  jobs: "Jobs werden geprüft…",
  job: "Jobs werden geprüft…",
  routes: "Routen werden geprüft…",
  route: "Routen werden geprüft…",
  routen: "Routen werden geprüft…",
  result: "Ergebnis wird vorbereitet…",
  results: "Ergebnis wird vorbereitet…",
  final: "Ergebnis wird vorbereitet…",
  finalize: "Ergebnis wird vorbereitet…",
  preparing_result: "Ergebnis wird vorbereitet…",
};

export const ACTIVE_WORK_RUN_STATUS_LABELS_DE = {
  idle: "Bereit",
  running: "Läuft",
  waiting: "Wartet",
  completed: "Abgeschlossen",
  failed: "Fehlgeschlagen",
  blocked: "Blockiert",
};

export function normalizeAgentLiveProgressIntent(intent) {
  return KNOWN_INTENTS.has(intent) ? intent : "general_chat";
}

function normalizeBackendWorkflowType(raw, fallbackIntent = "general_chat") {
  const fallback = normalizeAgentLiveProgressIntent(fallbackIntent);
  if (typeof raw !== "string") return fallback;
  const key = raw.trim().toLowerCase().replace(/[\s-]/g, "_");
  if (!key) return fallback;
  if (KNOWN_INTENTS.has(key)) return key;
  if (key.includes("exec") || key.includes("ausf")) return "execution_request";
  if (key.includes("repair") || key.includes("proposal") || key.includes("vorschlag") || key.includes("patch")) {
    return "controlled_repair_preview";
  }
  if (key.includes("diagnos") || key.includes("check") || key.includes("scan") || key.includes("system")) {
    return "diagnosis_request";
  }
  return fallback;
}

function normalizeBackendWorkflowStep(raw) {
  if (typeof raw !== "string") return null;
  const key = raw.trim().toLowerCase().replace(/[\s-]/g, "_");
  return key || null;
}

function buildBackendWorkflowProgressLabel({ progressLabel, currentStep, workflowPhase, workflowActive }) {
  if (typeof progressLabel === "string" && progressLabel.trim()) return progressLabel.trim();
  const current = normalizeBackendWorkflowStep(currentStep);
  if (current && BACKEND_WORKFLOW_STEP_LABELS_DE[current]) return BACKEND_WORKFLOW_STEP_LABELS_DE[current];
  const phase = normalizeBackendWorkflowStep(workflowPhase);
  if (phase && BACKEND_WORKFLOW_STEP_LABELS_DE[phase]) return BACKEND_WORKFLOW_STEP_LABELS_DE[phase];
  return workflowActive ? AGENT_BACKEND_WORKFLOW_PROGRESS_STEPS_DE[0] : null;
}

export function inferAgentLiveProgressIntent(text, fallbackIntent) {
  const fallback = normalizeAgentLiveProgressIntent(fallbackIntent);
  if (typeof text !== "string") return fallback;
  const t = text.trim().toLowerCase();
  if (!t) return fallback;

  if (
    /\b(freigabe\w*|freigegeben\w*|ausführ\w*|ausfuehr\w*|execute|execution|safetygate)\b/.test(t)
  ) {
    return "execution_request";
  }
  if (
    /\b(vorschlag|reparatur|repair|patch|komponente|codeänder|codeaender|planung|plan)\b/.test(t)
  ) {
    return "controlled_repair_preview";
  }
  if (
    /\b(system|diagnos|analyse|prüf|pruef|fehler|risiko|datenfluss|daten\s*fluss|backend|route|job|tabelle)\b/.test(t)
  ) {
    return t.includes("system") && !/\b(diagnos|analyse|fehler)\b/.test(t)
      ? "system_question"
      : "diagnosis_request";
  }
  return fallback;
}

export function buildAgentLiveProgress(agentName, intent, elapsedSeconds = 0) {
  const key = typeof agentName === "string" ? agentName.trim().toLowerCase() : "";
  const normalizedIntent = normalizeAgentLiveProgressIntent(intent);
  const phases = AGENT_LIVE_PROGRESS_PHASES_DE[normalizedIntent]
    || AGENT_LIVE_PROGRESS_PHASES_DE.general_chat;
  const safeElapsed = Number.isFinite(elapsedSeconds) && elapsedSeconds > 0
    ? Math.floor(elapsedSeconds)
    : 0;
  const phaseIndex = phases.length <= 1
    ? 0
    : Math.min(phases.length - 1, Math.floor(safeElapsed / 4));
  const agentText = AGENT_LIVE_PROGRESS_AGENT_TEXTS_DE[key] || "Agent arbeitet …";
  const currentPhase = phases[phaseIndex] || phases[0];
  const delayedText = safeElapsed >= 300
    ? "Provider langsam oder Bildgenerierung läuft"
    : safeElapsed >= 180
      ? `Aktuelle Phase: ${currentPhase}`
      : safeElapsed >= 90
        ? "Schwerer Auftrag läuft"
        : safeElapsed >= 30
          ? "Antwort dauert etwas länger"
          : null;

  return {
    agentText,
    intent: normalizedIntent,
    phases,
    phaseIndex,
    currentPhase,
    statusText: `${agentText} ${currentPhase}`,
    elapsedSeconds: safeElapsed,
    delayedText,
    percent: phases.length <= 1
      ? 45
      : Math.min(92, Math.max(12, Math.round(((phaseIndex + 1) / phases.length) * 100))),
  };
}

function normalizeActiveWorkRunStatus(status) {
  const key = typeof status === "string" ? status.trim().toLowerCase() : "";
  if (key === "error") return "failed";
  if (Object.hasOwn(ACTIVE_WORK_RUN_STATUS_LABELS_DE, key)) return key;
  return "running";
}

function normalizeActiveWorkRunSteps(intent, steps) {
  const input = Array.isArray(steps) && steps.length > 0
    ? steps
    : AGENT_LIVE_PROGRESS_PHASES_DE[normalizeAgentLiveProgressIntent(intent)];
  return input
    .map((step) => (typeof step === "string" ? step : step?.label || ""))
    .map((label) => label.trim())
    .filter(Boolean)
    .slice(0, 6);
}

export function createActiveWorkRun({
  runId,
  agentId,
  agentName,
  userMessage,
  intent,
  action = "chat",
  startedAt,
  relatedMessageId,
  backendHint,
  backendSessionId,
  backendProgressLabel,
  steps,
  phase,
  phaseLabel,
  status,
  mode,
} = {}) {
  const message = typeof userMessage === "string" ? userMessage.trim() : "";
  const inferredIntent = inferAgentLiveProgressIntent(message, intent);
  const started = startedAt || new Date().toISOString();
  const workSteps = normalizeActiveWorkRunSteps(inferredIntent, steps);
  return {
    runId: runId || `run-${Date.now()}`,
    agentId: typeof agentId === "string" && agentId ? agentId : "agent",
    agentName: typeof agentName === "string" && agentName ? agentName : "Agent",
    userMessage: message,
    intent: normalizeAgentLiveProgressIntent(inferredIntent),
    action,
    startedAt: started,
    phase: phase || "received",
    phaseLabel: phaseLabel || workSteps[0] || "Anfrage empfangen",
    status: normalizeActiveWorkRunStatus(status || "running"),
    steps: workSteps,
    relatedMessageId: relatedMessageId || null,
    backendHint: backendHint || null,
    backendSessionId: backendSessionId || null,
    backendProgressLabel: backendProgressLabel || null,
    errorReason: null,
    lastUpdatedAt: started,
    mode: normalizeAgentRunMode(mode),
  };
}

export function completeActiveWorkRun(run, {
  status = "completed",
  phase,
  phaseLabel,
  backendHint,
  errorReason,
  lastUpdatedAt,
  mode,
} = {}) {
  if (!run || typeof run !== "object") return null;
  const normalizedStatus = normalizeActiveWorkRunStatus(status);
  const completedAt = lastUpdatedAt || new Date().toISOString();
  const finalLabel = phaseLabel
    || (normalizedStatus === "completed"
      ? "Antwort bereit"
      : normalizedStatus === "blocked"
        ? "Arbeitslauf blockiert"
        : "Arbeitslauf nicht abgeschlossen");
  return {
    ...run,
    status: normalizedStatus,
    phase: phase || normalizedStatus,
    phaseLabel: finalLabel,
    backendHint: backendHint || run.backendHint || null,
    backendSessionId: run.backendSessionId || null,
    backendProgressLabel: null,
    errorReason: errorReason || run.errorReason || null,
    lastUpdatedAt: completedAt,
    mode: normalizeAgentRunMode(mode) || normalizeAgentRunMode(run.mode),
  };
}

export function buildWorkflowActiveRunPatch(chatResponse) {
  if (!chatResponse || chatResponse.workflowActive !== true) return null;
  const now = new Date().toISOString();
  return {
    status: "running",
    phase: chatResponse.workflowPhase || chatResponse.currentStep || "running",
    phaseLabel: chatResponse.progressLabel || AGENT_BACKEND_WORKFLOW_PROGRESS_STEPS_DE[0],
    backendProgressLabel: chatResponse.progressLabel || AGENT_BACKEND_WORKFLOW_PROGRESS_STEPS_DE[0],
    steps: chatResponse.workflowSteps || AGENT_BACKEND_WORKFLOW_PROGRESS_STEPS_DE,
    backendHint: chatResponse.statusLine || null,
    backendSessionId: chatResponse.sessionId || null,
    errorReason: null,
    lastUpdatedAt: now,
  };
}

export function buildActiveWorkRunProgress(run, elapsedSeconds = 0) {
  if (!run || typeof run !== "object") return null;
  const status = normalizeActiveWorkRunStatus(run.status);
  const live = buildAgentLiveProgress(run.agentId || run.agentName, run.intent, elapsedSeconds);
  const steps = normalizeActiveWorkRunSteps(run.intent, run.steps);
  const phaseIndex = status === "completed"
    ? Math.max(0, steps.length - 1)
    : status === "failed" || status === "blocked"
      ? Math.min(Math.max(0, live.phaseIndex), Math.max(0, steps.length - 1))
      : Math.min(Math.max(0, live.phaseIndex), Math.max(0, steps.length - 1));
  const runMode = normalizeAgentRunMode(run.mode);
  const modePhaseLabel = buildAgentRunModeStatusLine(runMode);
  const phaseLabel = status === "running" || status === "waiting"
    ? (run.backendProgressLabel || modePhaseLabel || steps[phaseIndex] || run.phaseLabel || live.currentPhase)
    : (run.phaseLabel || (status === "completed" ? "Antwort bereit" : "Arbeitslauf nicht abgeschlossen"));
  const stepItems = steps.map((label, index) => {
    let stepStatus = "open";
    if (status === "completed") stepStatus = "completed";
    else if (status === "failed" || status === "blocked") {
      stepStatus = index < phaseIndex ? "completed" : index === phaseIndex ? status : "open";
    } else {
      stepStatus = index < phaseIndex ? "completed" : index === phaseIndex ? "running" : "open";
    }
    return { label, status: stepStatus };
  });

  // Suppress delayedText for terminal states: it is only meaningful while the
  // agent is still running. Showing "Noch keine finale Antwort…" alongside
  // "Antwort bereit" (completed) or an error card (failed/blocked) would be
  // contradictory and confusing for the user.
  const terminalStatus = status === "completed" || status === "failed" || status === "blocked";
  return {
    ...live,
    id: run.runId,
    runId: run.runId,
    agentId: run.agentId,
    agentLabel: run.agentName || live.agentLabel || "Agent",
    userMessage: run.userMessage || "",
    action: run.action || "chat",
    relatedMessageId: run.relatedMessageId || null,
    startedAt: run.startedAt,
    lastUpdatedAt: run.lastUpdatedAt,
    status,
    statusLabel: ACTIVE_WORK_RUN_STATUS_LABELS_DE[status],
    phase: run.phase || live.intent,
    phaseLabel,
    currentPhase: phaseLabel,
    steps: stepItems,
    backendHint: run.backendHint || null,
    backendSessionId: run.backendSessionId || null,
    backendProgressLabel: run.backendProgressLabel || null,
    errorReason: run.errorReason || null,
    percent: status === "completed" ? 100 : live.percent,
    delayedText: terminalStatus ? null : live.delayedText,
    mode: runMode,
  };
}

export function buildActiveWorkRunStatusLine(progress) {
  if (!progress) return null;
  const agent = progress.agentLabel || "Agent";
  if (progress.status === "completed") return `${agent} fertig · Antwort bereit`;
  if (progress.status === "failed" || progress.status === "blocked") {
    if (typeof progress.errorReason === "string" && progress.errorReason.trim()) {
      return `${agent} fehlgeschlagen · ${progress.errorReason.trim().slice(0, 80)}`;
    }
    return `${agent} fehlgeschlagen`;
  }
  const phase = progress.phaseLabel || progress.currentPhase || "Anfrage empfangen";
  return `${agent} arbeitet · ${phase}`;
}

// ── Track / Mode ───────────────────────────────────────────────────────────

/**
 * Backend may send a `track` field ("strategy" or "action") together with
 * an optional `mode` field. The combination maps to a short, human-readable
 * German status chip shown discreetly below the status bar.
 *
 * strategy track modes → "Gespräch" / "Strategie" / "Systemfrage"
 * action    track modes → "Prüft" / "Bereitet vor" / "Wartet auf Freigabe" / "Führt aus"
 */
export const AGENT_TRACK_LABELS_DE = {
  strategy: {
    general_chat:    "Gespräch",
    strategy:        "Strategie",
    system_question: "Systemfrage",
    _default:        "Gespräch",
  },
  action: {
    analyzing:        "Prüft",
    checking:         "Prüft",
    reviewing:        "Bereitet vor",
    preparing:        "Bereitet vor",
    waiting_approval: "Wartet auf Freigabe",
    executing:        "Führt aus",
    _default:         "Prüft",
  },
};

/**
 * Derives a short, human-readable German track label from the raw
 * `track` and `mode` (or phase) values returned by the backend.
 *
 * Returns null when no valid track is present (keeps the UI quiet for
 * normal chats where the backend does not send a track field).
 *
 * @param {string} track  - "strategy" | "action" (raw from backend)
 * @param {string} mode   - mode or phase string (raw from backend)
 */
export function normalizeAgentTrack(track, mode) {
  const t = typeof track === "string" ? track.trim().toLowerCase() : "";
  const m = typeof mode === "string" ? mode.trim().toLowerCase() : "";
  const trackMap = AGENT_TRACK_LABELS_DE[t];
  if (!trackMap) return null;
  return trackMap[m] || trackMap._default || null;
}

const KNOWN_INTENTS = new Set(Object.keys(AGENT_INTENT_LABELS_DE));

/**
 * Returns true when the intent signals a workflow that produces
 * technical artefacts (diagnosis, proposals, execution), so the UI
 * can decide to show the "Details anzeigen" drawer link.
 */
export function isWorkflowIntent(intent) {
  return (
    intent === "diagnosis_request" ||
    intent === "repair_planning_request" ||
    intent === "controlled_repair_preview" ||
    intent === "execution_request"
  );
}

// ── Empty-state copy ───────────────────────────────────────────────────────

export const AGENT_WORK_EMPTY_STATE = {
  title: "Noch keine Agentenarbeit gestartet.",
  hint:  "Starte eine Analyse im Chat.",
  note:  "Der Agent erkennt betroffene Dateien selbst.",
};

/**
 * Shown when the backend returns a successful response but no agent text,
 * or when an API call fails. The UI must never inject a local fallback
 * like "DeepSeek übernimmt …".
 */
export const AGENT_NO_RESPONSE_TEXT = "Ich konnte keine Agentenantwort laden. Bitte versuche es erneut.";

// ── API error mapping (rule #11) ───────────────────────────────────────────

/**
 * Map a raw `{ data, error, status }` result from the API into a friendly
 * empty-state-or-error tuple `{ isEmpty, errorText }`.
 *
 *  - 404 with no data → not a real error, treated as empty state.
 *  - other failures → human German error text.
 */
export function mapAgentWorkApiResult(result) {
  if (!result || typeof result !== "object") {
    return { isEmpty: true, errorText: null };
  }
  const { data, error, status } = result;
  const hasData =
    Array.isArray(data) ? data.length > 0 :
      data && typeof data === "object" ? Object.keys(data).length > 0 :
        false;

  if (!error && hasData) return { isEmpty: false, errorText: null };
  if (!error && !hasData) return { isEmpty: true, errorText: null };

  // Real error path – translate common status codes.
  if (status === 404) return { isEmpty: true, errorText: null };
  if (status === 401 || status === 403) {
    return { isEmpty: false, errorText: "Keine Berechtigung – bitte Admin-Zugang prüfen." };
  }
  if (status === 0) {
    return { isEmpty: false, errorText: "Verbindung zum Backend fehlgeschlagen." };
  }
  if (status >= 500) {
    return { isEmpty: false, errorText: "Backend-Fehler – bitte später erneut versuchen." };
  }
  return { isEmpty: false, errorText: typeof error === "string" ? error : "Unbekannter Fehler." };
}

// ── Chat payload builder (testable, no fetch) ────────────────────────────────

/**
 * Build and validate the JSON payload for POST /api/admin/agent-work/chat
 * without any network or DOM dependency so it can be unit-tested in Node.
 *
 * Accepts both `agentName` (spec) and `agent` (legacy) field names so the
 * UI callers stay compatible.
 *
 * Returns:
 *   { valid: true,  payload: { agentName, sessionId, message, context? } }
 *   { valid: false, error: "<human German error>" }
 */
export function buildAgentWorkChatPayload(params) {
  if (!params || typeof params !== "object") {
    return { valid: false, error: "Ungültige Chat-Anfrage." };
  }
  const agentName =
    typeof params.agentName === "string" && params.agentName ? params.agentName :
    typeof params.agent     === "string" && params.agent     ? params.agent     : "";
  const message = typeof params.message === "string" ? params.message : "";
  if (!agentName || !message) {
    return { valid: false, error: "Agent und Nachricht sind erforderlich." };
  }
  const payload = {
    agentName,
    sessionId: typeof params.sessionId === "string" ? params.sessionId : null,
    message,
  };

  // Caller-supplied context wins as the base; quickAction/intent/action then
  // override so the chat metadata is always explicit and the backend never
  // has to guess whether a message is free chat or a quick-action button.
  const baseContext = params.context && typeof params.context === "object" && !Array.isArray(params.context)
    ? { ...params.context }
    : {};

  const hasChatMeta =
    params.quickAction !== undefined
    || params.intent !== undefined
    || params.action !== undefined
    || params.source !== undefined;

  if (hasChatMeta) {
    const quickAction = params.quickAction === true;
    // Step 15: userConfirmedStart = true means a pending-action button was clicked.
    const userConfirmedStart = params.userConfirmedStart === true;
    const quickActionMeta = quickAction ? buildQuickActionChatMeta(message) : null;
    const intent = typeof params.intent === "string" && params.intent
      ? params.intent
      : (quickAction ? quickActionMeta?.intent || null : "general_chat");
    const action = quickAction
      ? (typeof params.action === "string" && params.action ? params.action : quickActionMeta?.action || null)
      : null;
    const expectedSource = quickAction ? "chat_button" : "chat_input";
    const source = typeof params.source === "string" && params.source.trim().toLowerCase() === expectedSource
      ? expectedSource
      : expectedSource;
    // Step 15: pending-action button clicks use mode "workflow"; everything else stays "chat".
    const mode = (quickAction && userConfirmedStart) ? "workflow" : "chat";
    const context = {
      ...baseContext,
      mode,
      // Free-text composer input is tagged "chat_input" so the backend can
      // distinguish it from quick-action button clicks ("chat_button").
      // The canonical signal remains the quickAction boolean; source is an
      // additional hint only.
      source,
      quickAction,
    };
    if (intent !== null) context.intent = intent;
    context.action = action;

    // Step 15: pending action follow-up context (free-text path only).
    // When the user types text while a pendingAction is active, the backend
    // receives the pendingActionId and the full pendingAction object so it can
    // decide whether to promote the message to a workflow_start.
    const pendingActionId = typeof params.pendingActionId === "string" && params.pendingActionId
      ? params.pendingActionId
      : null;
    if (!quickAction && pendingActionId) {
      context.hasPendingAction = true;
      if (params.pendingAction && typeof params.pendingAction === "object" && !Array.isArray(params.pendingAction)) {
        context.pendingAction = params.pendingAction;
      }
    }

    payload.context = context;

    // Step 15: top-level payload fields for pending actions.
    if (pendingActionId) payload.pendingActionId = pendingActionId;
    if (userConfirmedStart) payload.userConfirmedStart = true;
  } else if (Object.keys(baseContext).length > 0) {
    payload.context = baseContext;
  }

  // Screenshot / design context forwarding.
  // designContext is passed through as-is; it already contains screenshots
  // (via buildScreenshotPayloadEntry) from the caller.
  if (params.designContext && typeof params.designContext === "object" && !Array.isArray(params.designContext)) {
    payload.designContext = params.designContext;
  }
  // Top-level screenshots array: sent separately so the backend can receive
  // image data even without a full design context object.
  if (Array.isArray(params.screenshots) && params.screenshots.length > 0) {
    payload.screenshots = params.screenshots;
  }
  // VisualContext summary: forwarded only when analysisPerformed === true.
  if (params.visualContext && typeof params.visualContext === "object" && !Array.isArray(params.visualContext)) {
    payload.visualContext = params.visualContext;
  }
  if (params.visualContextSummary && typeof params.visualContextSummary === "object" && !Array.isArray(params.visualContextSummary)) {
    payload.visualContextSummary = params.visualContextSummary;
  } else if (payload.visualContext) {
    payload.visualContextSummary = payload.visualContext;
  }
  if (params.requestDesignProposal === true) {
    payload.requestDesignProposal = true;
  }
  if (params.wantsGeneratedImage === true) {
    payload.wantsGeneratedImage = true;
  }
  // General Chat Context V1: normal chat turns also receive the recent
  // conversation tail so follow-up prompts can refer to prior answers.
  if (Array.isArray(params.conversationTail) && params.conversationTail.length > 0) {
    payload.conversationTail = params.conversationTail;
  }
  if (params.chatContext && typeof params.chatContext === "object" && !Array.isArray(params.chatContext)) {
    payload.chatContext = params.chatContext;
    if (payload.context && typeof payload.context === "object" && !Array.isArray(payload.context)) {
      payload.context.hasConversationContext = params.chatContext.hasPriorContext === true;
      payload.context.conversationContinuity = {
        topicContinuity: typeof params.chatContext.topicContinuity === "string"
          ? params.chatContext.topicContinuity
          : null,
        shouldContinueCurrentTopic: params.chatContext.shouldContinueCurrentTopic === true,
        isExplicitTopicChange: params.chatContext.isExplicitTopicChange === true,
        reason: typeof params.chatContext.continuityReason === "string"
          ? params.chatContext.continuityReason
          : null,
      };
    }
  }
  if (params.hqsSystemContextBlock && typeof params.hqsSystemContextBlock === "object" && !Array.isArray(params.hqsSystemContextBlock)) {
    payload.hqsSystemContextBlock = params.hqsSystemContextBlock;
    if (payload.context && typeof payload.context === "object" && !Array.isArray(payload.context)) {
      payload.context.hqsSystemContextBlock = params.hqsSystemContextBlock;
      payload.context.systemContextFreshness = "current_frontend_state";
    }
  }
  if (typeof params.lastActionableProposalText === "string" && params.lastActionableProposalText) {
    payload.lastActionableProposalText = params.lastActionableProposalText;
  }
  if (params.followUpResolved === true) {
    payload.followUpResolved = true;
  }
  const normalizedSelectedImprovement = normalizeSelectedImprovement(params.selectedImprovement);
  if (normalizedSelectedImprovement) {
    payload.selectedImprovement = normalizedSelectedImprovement;
  }
  const normalizedSelectedIndexes = normalizeSelectedIndexes(params.selectedIndexes);
  if (normalizedSelectedIndexes.length > 0) {
    payload.selectedIndexes = normalizedSelectedIndexes;
  }
  if (typeof params.selectedImprovementText === "string" && params.selectedImprovementText) {
    payload.selectedImprovementText = params.selectedImprovementText;
  }
  if (params.normalizedProposalCandidate === true) {
    payload.normalizedProposalCandidate = true;
  }
  if (params.actionableProposal !== undefined) {
    payload.actionableProposal = params.actionableProposal;
  }
  if (
    payload.context
    && typeof payload.context === "object"
    && !Array.isArray(payload.context)
    && (
      normalizedSelectedImprovement
      || normalizedSelectedIndexes.length > 0
      || (typeof params.selectedImprovementText === "string" && params.selectedImprovementText)
      || (typeof params.lastActionableProposalText === "string" && params.lastActionableProposalText)
    )
  ) {
    payload.context.followUpContext = {
      followUpResolved: params.followUpResolved === true,
      selectedImprovement: normalizedSelectedImprovement,
      selectedIndexes: normalizedSelectedIndexes,
      selectedImprovementText:
        typeof params.selectedImprovementText === "string" && params.selectedImprovementText
          ? params.selectedImprovementText
          : null,
      lastActionableProposalText:
        typeof params.lastActionableProposalText === "string" && params.lastActionableProposalText
          ? params.lastActionableProposalText
          : null,
    };
  }

  // Natural Handoff Context V1: forward prior-proposal context so the backend
  // never replies "Der Vorschlag fehlt in Ihrer Anfrage."
  if (params.naturalHandoff === true) {
    payload.naturalHandoff = true;
    if (typeof params.handoffTargetAgent === "string" && params.handoffTargetAgent) {
      payload.handoffTargetAgent = params.handoffTargetAgent;
      // Also forward as requestedTargetAgent for backends that use this field name
      payload.requestedTargetAgent = params.handoffTargetAgent;
    }
    if (typeof params.lastAgentText === "string" && params.lastAgentText) {
      payload.lastAgentText = params.lastAgentText;
    }
    if (typeof params.lastAgentReply === "string" && params.lastAgentReply) {
      payload.lastAgentReply = params.lastAgentReply;
    }
    if (params.lastStructuredProposal && typeof params.lastStructuredProposal === "object" && !Array.isArray(params.lastStructuredProposal)) {
      payload.lastStructuredProposal = params.lastStructuredProposal;
    }
    if (typeof params.lastAgentName === "string" && params.lastAgentName) {
      payload.lastAgentName = params.lastAgentName;
    }
  }

  return {
    valid: true,
    payload,
  };
}

/**
 * Returns true when `text` matches one of the registered quick-action labels
 * for the given agent in FRONTEND_AGENT_REGISTRY. Used as a sanity check by
 * the chat view; the canonical signal remains the `meta.quickAction` flag
 * forwarded by AgentWorkComposer.
 *
 * Matching is case-sensitive on the full trimmed string so partial
 * "prüfe das System" free-text questions are NOT classified as quick actions.
 *
 * @param {Array} quickActions  agent.quickActions array from the registry
 * @param {string} text         message text to test
 */
export function isQuickActionLabel(quickActions, text) {
  if (!Array.isArray(quickActions) || quickActions.length === 0) return false;
  if (typeof text !== "string") return false;
  const trimmed = text.trim();
  if (!trimmed) return false;
  return quickActions.some((label) => typeof label === "string" && label === trimmed);
}

const QUICK_ACTION_CHAT_META_BY_LABEL = {
  "System prüfen": { intent: "diagnosis_request", action: "system_check" },
  "Fehler finden": { intent: "diagnosis_request", action: "find_errors" },
  "Code prüfen": { intent: "diagnosis_request", action: "code_review" },
  "Code bewerten": { intent: "diagnosis_request", action: "code_review" },
  "Oberfläche prüfen": { intent: "diagnosis_request", action: "frontend_review" },
  "UI prüfen": { intent: "diagnosis_request", action: "frontend_review" },
  "Risiko prüfen": { intent: "diagnosis_request", action: "risk_review" },
  "Punkt 1 umsetzen": { intent: "execution_request", action: "implement_point_1" },
  "PR vorbereiten": { intent: "repair_planning_request", action: "prepare_pr" },
  "Tests ergänzen": { intent: "repair_planning_request", action: "add_tests" },
  "Alles als Plan strukturieren": { intent: "repair_planning_request", action: "structure_plan" },
  "Vorschlag vorbereiten": { intent: "controlled_repair_preview", action: "prepare_proposal" },
  "Vorschlag prüfen": { intent: "repair_planning_request", action: "review_proposal" },
  "Patch vorbereiten": { intent: "repair_planning_request", action: "prepare_patch" },
  "Freigegebenes ausführen": { intent: "execution_request", action: "execute_approved" },
  "prüfe Freigabe": { intent: "controlled_repair_preview", action: "check_approval" },
  "UX verbessern": { intent: "repair_planning_request", action: "improve_ux" },
  "Design-Vorschlag": { intent: "repair_planning_request", action: "design_proposal" },
  "Designvorschlag erstellen": { intent: "repair_planning_request", action: "design_proposal" },
  "Komponente vorschlagen": { intent: "repair_planning_request", action: "suggest_component" },
  "Layout verbessern": { intent: "repair_planning_request", action: "improve_layout" },
  "Screenshot für v0 analysieren": { intent: "diagnosis_request", action: "analyze_screenshot" },
  "Screenshot analysieren": { intent: "diagnosis_request", action: "analyze_screenshot" },
  "Schnell fragen": { intent: "general_chat", action: "fast_chat" },
  "An Qwen übergeben": { intent: "controlled_repair_preview", action: "qwen_handoff" },
  "Zusammenfassen": { intent: "diagnosis_request", action: "summarize" },
  "Priorisieren": { intent: "diagnosis_request", action: "prioritize" },
  "Entscheidung vorbereiten": { intent: "controlled_repair_preview", action: "prepare_decision" },
  "Designbild erstellen": { intent: "design_image_request", action: "generate_design_image" },
  "Mockup generieren": { intent: "design_image_request", action: "generate_mockup" },
};

function fallbackQuickActionId(label) {
  if (typeof label !== "string") return null;
  const normalized = label
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return normalized || null;
}

export function buildQuickActionChatMeta(label) {
  if (typeof label !== "string") {
    return { quickAction: true, intent: null, action: null };
  }
  const trimmed = label.trim();
  const known = QUICK_ACTION_CHAT_META_BY_LABEL[trimmed];
  return {
    quickAction: true,
    intent: known?.intent || null,
    action: known?.action || fallbackQuickActionId(trimmed),
  };
}

// ── Sentinel / Continuous Observer (Step 3) ────────────────────────────────

/**
 * Canonical error text shown when a Sentinel scan fails or the backend is
 * unreachable.  The UI must never inject a local fake scan result.
 */
export const SENTINEL_SCAN_ERROR_TEXT =
  "Sentinel konnte gerade nicht prüfen. Bitte später erneut versuchen.";

/**
 * Short German labels for each possible Sentinel status value.
 * Used by buildSentinelStatusLine() and the tests.
 */
export const SENTINEL_STATUS_LABELS_DE = {
  active:   "Wächter aktiv",
  inactive: "Wächter inaktiv",
  scanning: "Wächter prüft …",
  error:    "Wächter Fehler",
};

/**
 * Normalise the raw response from GET /api/admin/agent-sentinel/status into a
 * stable display shape for the AgentWorkChat sentinel bar.
 *
 * Returns:
 *   {
 *     active:     boolean   – true when the sentinel is running
 *     lastScan:   string|null – ISO timestamp of the last scan
 *     issueCount: number    – number of detected issues (0 when unknown)
 *     status:     "active"|"inactive"|"scanning"|"error"
 *     agentScope: string|null – which agent area this applies to (null = all)
 *   }
 *
 * Never throws; missing/malformed data resolves to safe defaults.
 *
 * @param {any} data - raw response body from the API (may be null/undefined)
 */
export function normalizeSentinelStatus(data) {
  if (!data || typeof data !== "object") {
    return { active: false, lastScan: null, issueCount: 0, status: "inactive", agentScope: null };
  }

  // Resolve status string – accept both string and boolean active flag.
  const rawStatus = typeof data.status === "string" ? data.status.trim().toLowerCase() : "";
  const knownStatuses = new Set(Object.keys(SENTINEL_STATUS_LABELS_DE));
  const status = knownStatuses.has(rawStatus) ? rawStatus
    : (data.active === true ? "active" : "inactive");

  const active = status === "active" || status === "scanning" || Boolean(data.active);

  // Last scan timestamp – accept both camelCase and snake_case.
  const rawLastScan =
    (typeof data.lastScan === "string" && data.lastScan) ||
    (typeof data.last_scan === "string" && data.last_scan) ||
    (typeof data.scannedAt === "string" && data.scannedAt) ||
    null;

  // Issue count – coerce to safe non-negative integer.
  const rawCount = data.issueCount ?? data.issue_count ?? data.issues_count ?? data.total ?? 0;
  const issueCount = typeof rawCount === "number" && rawCount >= 0 ? Math.floor(rawCount) : 0;

  const agentScope =
    typeof data.agentScope === "string" && data.agentScope ? data.agentScope :
    typeof data.agent_scope === "string" && data.agent_scope ? data.agent_scope : null;

  return { active, lastScan: rawLastScan, issueCount, status, agentScope };
}

/**
 * Build the compact single-line Sentinel status text shown in the chat.
 *
 * Examples:
 *   "Wächter: aktiv · letzte Prüfung 20:22 · 1 Warnung"
 *   "Wächter: aktiv · letzte Prüfung 20:22 · Keine akuten Probleme erkannt"
 *   "Wächter: aktiv · 2 Auffälligkeiten erkannt"
 *   "Wächter inaktiv"
 *
 * Returns null when the sentinel state is not meaningful enough to display.
 * Always ≤ 80 chars for mobile.
 *
 * @param {{ active: boolean, lastScan: string|null, issueCount: number, status: string }} sentinel
 */
export function buildSentinelStatusLine(sentinel) {
  if (!sentinel || typeof sentinel !== "object") return null;
  const { active, lastScan, issueCount, status } = sentinel;

  if (!active && status !== "error") return "Wächter inaktiv";

  const parts = ["Wächter: aktiv"];

  if (lastScan) {
    const formatted = formatSentinelTimestamp(lastScan);
    if (formatted) parts.push(`letzte Prüfung ${formatted}`);
  }

  if (issueCount > 0) {
    parts.push(issueCount === 1 ? "1 Warnung" : `${issueCount} Auffälligkeiten erkannt`);
  } else if (active) {
    parts.push("Keine akuten Probleme erkannt");
  }

  const line = parts.join(" · ");
  // Safety cap for mobile: never exceed 80 chars (truncate gracefully)
  return line.length <= 80 ? line : line.slice(0, 78) + "…";
}

/** Format a Sentinel scan timestamp to "HH:MM" for the status line. */
function formatSentinelTimestamp(ts) {
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return null;
  }
}

/**
 * Normalise a raw Sentinel issues list for display in the details drawer.
 *
 * Each issue is collapsed to only the fields that are safe to show –
 * no raw JSON dumps, no secrets, no long technical strings.
 *
 * Returns an array of up to 10 normalised issue objects:
 *   {
 *     area:             string  – e.g. "MuleRouter / Qwen-Agent"
 *     component:        string  – e.g. "Qwen-Agent"
 *     risk:             string  – "low" | "medium" | "high" | "critical"
 *     recommendedAgent: string  – e.g. "deepseek"
 *     nextStep:         string  – human-readable next action
 *     evidence:         string  – short source/evidence text (≤80 chars)
 *   }
 *
 * @param {any} data - raw response body from the API
 */
export function normalizeSentinelIssues(data) {
  if (!data || typeof data !== "object") return [];
  const rawList = Array.isArray(data.issues) ? data.issues
    : Array.isArray(data.data?.issues) ? data.data.issues
    : Array.isArray(data) ? data
    : [];

  return rawList.slice(0, 10).map((item) => {
    if (!item || typeof item !== "object") return null;
    const area = sanitizeShortText(item.area || item.affected_area || item.layer, 60);
    const component = sanitizeShortText(item.component || item.affectedComponent || item.affected_component, 60);
    const risk = sanitizeRiskLevel(item.risk || item.riskLevel || item.risk_level);
    const recommendedAgent = sanitizeShortText(item.recommendedAgent || item.recommended_agent || item.agent, 20);
    const nextStep = sanitizeShortText(item.nextStep || item.next_step || item.suggestion, 80);
    const evidence = sanitizeShortText(item.evidence || item.source || item.reason, 80);
    if (!area && !component && !nextStep) return null;
    return { area, component, risk, recommendedAgent, nextStep, evidence };
  }).filter(Boolean);
}

function sanitizeShortText(raw, maxLen) {
  if (typeof raw !== "string" || !raw.trim()) return null;
  const t = raw.trim();
  return t.length > maxLen ? t.slice(0, maxLen - 1) + "…" : t;
}

function sanitizeRiskLevel(raw) {
  const known = new Set(["low", "medium", "high", "critical"]);
  const s = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  return known.has(s) ? s : null;
}

/**
 * Normalise the raw response from POST /api/admin/agent-sentinel/scan into a
 * display-ready shape for the AgentWorkChat chat bubble.
 *
 * Returns:
 *   {
 *     text:       string  – friendly German chat bubble text (never raw JSON)
 *     issueCount: number
 *     hasIssues:  boolean
 *     issues:     Array   – normalised issues for details drawer (max 10)
 *     scannedAt:  string|null
 *   }
 *
 * The `text` is always a short, readable German sentence suitable for a
 * chat bubble.  It must never contain raw JSON, API secrets, or long dumps.
 *
 * @param {any}    data       - raw API response body
 * @param {string} [agentLabel] - optional agent label for personalised message
 */
export function normalizeSentinelScanResult(data, agentLabel) {
  if (!data || typeof data !== "object") {
    return { text: SENTINEL_SCAN_ERROR_TEXT, issueCount: 0, hasIssues: false, issues: [], scannedAt: null };
  }

  const label = typeof agentLabel === "string" && agentLabel ? agentLabel : "Der Wächter";

  const rawCount = data.issueCount ?? data.issue_count ?? data.issues_count ?? data.total ??
    (Array.isArray(data.issues) ? data.issues.length : 0);
  const issueCount = typeof rawCount === "number" && rawCount >= 0 ? Math.floor(rawCount) : 0;
  const hasIssues = issueCount > 0;

  const issues = normalizeSentinelIssues(data);
  const scannedAt = typeof data.scannedAt === "string" ? data.scannedAt :
    typeof data.scanned_at === "string" ? data.scanned_at : null;

  let text;
  if (!data.success && data.success !== undefined) {
    // Backend explicitly returned failure
    text = SENTINEL_SCAN_ERROR_TEXT;
  } else if (!hasIssues) {
    text = `${label} hat dein System im Hintergrund geprüft. Keine akuten Probleme erkannt.`;
  } else if (issueCount === 1 && issues.length > 0) {
    const first = issues[0];
    const area = first.area || first.component || "einem Bereich";
    text = `${label} hat dein System geprüft und 1 Auffälligkeit erkannt: ${area}. Details findest du im Drawer. Ich bereite einen Vorschlag vor, ändere aber noch nichts.`;
  } else {
    const top = issues[0];
    const areaHint = top ? ` Wichtigster Punkt: ${top.area || top.component || top.nextStep || "Details im Drawer"}.` : "";
    text = `${label} hat ${issueCount} Auffälligkeiten erkannt.${areaHint} Details findest du im Drawer. Ich bereite einen Vorschlag vor, ändere aber noch nichts.`;
  }

  // Safety: never exceed 300 chars for a chat bubble
  if (text.length > 300) text = text.slice(0, 298) + "…";

  return { text, issueCount, hasIssues, issues, scannedAt };
}

// ── Persistent Sentinel Layer (Step 5) ────────────────────────────────────

/**
 * Maximum number of persistent issues shown in the Details Drawer.
 * Keeps the drawer compact; heavy list-views are explicitly forbidden.
 */
export const MAX_PERSISTENT_SENTINEL_ISSUES = 5;

/** Short German labels for persistent Sentinel issue statuses. */
export const SENTINEL_ISSUE_STATUS_LABELS_DE = {
  open:      "Offen",
  dismissed: "Ausgeblendet",
  preparing: "Vorschlag wird vorbereitet",
  queued:    "In der Warteschlange",
  resolved:  "Behoben",
};

/** Short German labels for persistent Sentinel repair-queue item statuses. */
export const SENTINEL_REPAIR_STATUS_LABELS_DE = {
  preparing:          "Vorbereitung läuft",
  queued:             "Wartet auf Freigabe",
  awaiting_approval:  "Wartet auf Freigabe",
  approved:           "Freigegeben",
  executing:          "Führt aus …",
  completed:          "Abgeschlossen",
  cancelled:          "Abgebrochen",
  error:              "Fehler",
};

/**
 * Normalise the raw persistent issues response (GET .../issues) to a
 * compact, display-safe array of up to MAX_PERSISTENT_SENTINEL_ISSUES items.
 *
 * Each item:
 *   {
 *     id:               string|null
 *     area:             string|null
 *     component:        string|null
 *     risk:             "low"|"medium"|"high"|"critical"|null
 *     status:           "open"|"dismissed"|"preparing"|"queued"|"resolved"
 *     recommendedAgent: string|null
 *     nextStep:         string|null
 *     executionReady:   boolean  – only true when backend explicitly signals it
 *   }
 *
 * Never throws; missing/malformed data is silently dropped.
 *
 * @param {any} data - raw API response body
 */
export function normalizePersistentSentinelIssues(data) {
  if (!data || typeof data !== "object") return [];
  const rawList =
    Array.isArray(data.issues)      ? data.issues :
    Array.isArray(data.data?.issues) ? data.data.issues :
    Array.isArray(data)              ? data : [];

  const knownStatuses = new Set(Object.keys(SENTINEL_ISSUE_STATUS_LABELS_DE));

  return rawList
    .slice(0, MAX_PERSISTENT_SENTINEL_ISSUES)
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const area      = sanitizeShortText(item.area || item.affected_area || item.layer, 60);
      const component = sanitizeShortText(item.component || item.affectedComponent || item.affected_component, 60);
      const risk      = sanitizeRiskLevel(item.risk || item.riskLevel || item.risk_level);
      const rawStatus = typeof item.status === "string" ? item.status.trim().toLowerCase() : "";
      const status    = knownStatuses.has(rawStatus) ? rawStatus : "open";
      const recommendedAgent = sanitizeShortText(
        item.recommendedAgent || item.recommended_agent || item.agent, 20,
      );
      const nextStep = sanitizeShortText(item.nextStep || item.next_step || item.suggestion, 80);
      const rawId    = typeof item.id === "string" && item.id ? item.id : null;
      // executionReady is ONLY true when the backend explicitly sends it –
      // never inferred locally so the "Execute" button can never appear by accident.
      const executionReady = item.executionReady === true || item.execution_ready === true;
      if (!area && !component && !nextStep) return null;
      return { id: rawId, area, component, risk, status, recommendedAgent, nextStep, executionReady };
    })
    .filter(Boolean);
}

/**
 * Normalise the raw repair-queue response (GET .../repair-queue) to a compact
 * display-safe shape.
 *
 * Returns:
 *   {
 *     items:   Array<{ id, issueId, status, agentAssigned, statusLabel }>
 *     total:   number
 *     critical: number  – items with status "queued" or "awaiting_approval"
 *   }
 *
 * @param {any} data - raw API response body
 */
export function normalizeSentinelRepairQueue(data) {
  const empty = { items: [], total: 0, critical: 0 };
  if (!data || typeof data !== "object") return empty;

  const rawItems =
    Array.isArray(data.items)      ? data.items :
    Array.isArray(data.data?.items) ? data.data.items :
    Array.isArray(data.queue)       ? data.queue :
    [];

  const knownStatuses = new Set(Object.keys(SENTINEL_REPAIR_STATUS_LABELS_DE));

  const items = rawItems.slice(0, 20).map((item) => {
    if (!item || typeof item !== "object") return null;
    const rawStatus = typeof item.status === "string" ? item.status.trim().toLowerCase() : "";
    const status    = knownStatuses.has(rawStatus) ? rawStatus : "queued";
    const statusLabel = SENTINEL_REPAIR_STATUS_LABELS_DE[status] || "Unbekannt";
    const id           = typeof item.id      === "string" ? item.id      : null;
    const issueId      = typeof item.issueId === "string" ? item.issueId :
      typeof item.issue_id === "string" ? item.issue_id : null;
    const agentAssigned = sanitizeShortText(
      item.agentAssigned || item.agent_assigned || item.agent, 20,
    );
    return { id, issueId, status, statusLabel, agentAssigned };
  }).filter(Boolean);

  const total    = typeof data.total === "number" ? data.total : items.length;
  const critical = items.filter(
    (i) => i.status === "queued" || i.status === "awaiting_approval",
  ).length;

  return { items, total, critical };
}

/**
 * Build the compact persistent sentinel status line for the sentinel bar.
 * Only shown when there are issues; returns null otherwise.
 *
 * Examples:
 *   "Wächter: 3 offene Punkte · 1 kritisch"
 *   "Wächter: 2 offene Punkte"
 *   null (no issues → nothing shown)
 *
 * @param {Array}  issues      – normalised persistent issues
 * @param {object} repairQueue – normalised repair queue
 * @returns {string|null}
 */
export function buildPersistentSentinelStatusLine(issues, repairQueue) {
  const openIssues = Array.isArray(issues)
    ? issues.filter((i) => i.status === "open" || i.status === "preparing" || i.status === "queued")
    : [];
  if (openIssues.length === 0) return null;

  const criticalCount = openIssues.filter((i) => i.risk === "critical" || i.risk === "high").length;
  const repairCritical = repairQueue?.critical || 0;

  const countText = openIssues.length === 1
    ? "1 offener Punkt"
    : `${openIssues.length} offene Punkte`;

  let line = `Wächter: ${countText}`;

  if (criticalCount > 0) {
    line += ` · ${criticalCount === 1 ? "1 kritisch" : `${criticalCount} kritisch`}`;
  } else if (repairCritical > 0) {
    line += ` · ${repairCritical === 1 ? "1 Reparatur wartet" : `${repairCritical} Reparaturen warten`}`;
  }

  return line.length <= 80 ? line : line.slice(0, 78) + "…";
}

/**
 * Normalise the raw response from POST .../scan-and-persist into a
 * display-ready chat bubble text + issue counts.
 *
 * Returns:
 *   {
 *     text:          string   – friendly German chat bubble
 *     newIssues:     number
 *     updatedIssues: number
 *     issueCount:    number
 *     hasIssues:     boolean
 *     issues:        Array    – normalised (max 5)
 *     scannedAt:     string|null
 *   }
 *
 * @param {any}    data       - raw API response body
 * @param {string} [agentLabel]
 */
export function normalizeScanAndPersistResult(data, agentLabel) {
  const errorResult = {
    text: SENTINEL_SCAN_ERROR_TEXT,
    newIssues: 0, updatedIssues: 0, issueCount: 0,
    hasIssues: false, issues: [], scannedAt: null,
  };
  if (!data || typeof data !== "object") return errorResult;

  const label = typeof agentLabel === "string" && agentLabel ? agentLabel : "Der Wächter";
  const newIssues     = typeof data.newIssues     === "number" ? data.newIssues     : 0;
  const updatedIssues = typeof data.updatedIssues === "number" ? data.updatedIssues : 0;
  const issueCount    = typeof data.issueCount    === "number" ? data.issueCount    :
    (Array.isArray(data.issues) ? data.issues.length : 0);
  const hasIssues = issueCount > 0;
  const issues   = normalizePersistentSentinelIssues(data);
  const scannedAt = typeof data.scannedAt  === "string" ? data.scannedAt  :
    typeof data.scanned_at === "string" ? data.scanned_at : null;

  let text;
  if (!data.success && data.success !== undefined) {
    return { ...errorResult, scannedAt };
  }

  if (!hasIssues && newIssues === 0 && updatedIssues === 0) {
    text = `${label} hat dein System geprüft. Keine Auffälligkeiten gespeichert.`;
  } else if (newIssues === 0 && updatedIssues > 0) {
    text = `${label} hat dein System geprüft. ${updatedIssues === 1 ? "1 bestehender Punkt" : `${updatedIssues} bestehende Punkte`} wurde${updatedIssues === 1 ? "" : "n"} aktualisiert. Ich bereite noch keine Änderung vor, solange du es nicht möchtest.`;
  } else if (newIssues > 0 && updatedIssues === 0) {
    text = `${label} hat dein System geprüft. ${newIssues === 1 ? "1 neue Auffälligkeit wurde" : `${newIssues} neue Auffälligkeiten wurden`} gespeichert. Ich bereite noch keine Änderung vor, solange du es nicht möchtest.`;
  } else {
    const newPart = newIssues === 1 ? "1 neue Auffälligkeit" : `${newIssues} neue Auffälligkeiten`;
    const updPart = updatedIssues === 1 ? "1 bestehender Punkt" : `${updatedIssues} bestehende Punkte`;
    text = `${label} hat dein System geprüft. ${newPart} wurden gespeichert, ${updPart} wurde${updatedIssues === 1 ? "" : "n"} aktualisiert. Ich bereite noch keine Änderung vor, solange du es nicht möchtest.`;
  }

  if (text.length > 300) text = text.slice(0, 298) + "…";
  return { text, newIssues, updatedIssues, issueCount, hasIssues, issues, scannedAt };
}

/**
 * Normalise the raw response from POST .../prepare-repair into a
 * display-ready chat bubble text.
 *
 * Returns:
 *   {
 *     text:          string   – friendly German chat bubble
 *     success:       boolean
 *     status:        "preparing"|"queued"|"error"|null
 *     agentAssigned: string|null
 *   }
 *
 * This is purely a preparation step. It must NEVER claim anything was executed.
 *
 * @param {any}    data       - raw API response body
 * @param {string} [agentLabel]
 */
export function normalizePrepareRepairResult(data, agentLabel) {
  if (!data || typeof data !== "object" || data.success === false) {
    const reason = typeof data?.message === "string" && data.message
      ? data.message
      : "Unbekannter Fehler.";
    return {
      text: `Ich konnte keine Reparatur-Vorbereitung starten: ${reason}`,
      success: false, status: "error", agentAssigned: null,
    };
  }

  const rawStatus = typeof data.status === "string" ? data.status.trim().toLowerCase() : "";
  const status = ["preparing", "queued", "error"].includes(rawStatus) ? rawStatus : "preparing";
  const agentAssigned = sanitizeShortText(data.agentAssigned || data.agent_assigned || data.agent, 20);
  const fallbackAgent = sanitizeShortText(agentLabel, 20);

  const agentNote = agentAssigned || fallbackAgent
    ? ` ${agentAssigned || fallbackAgent} prüft die betroffenen Dateien und Datenflüsse.`
    : "";

  const text = `Ich habe für diesen Punkt eine Reparatur-Vorbereitung gestartet.${agentNote} Noch wurde nichts ausgeführt.`;

  return { text, success: true, status, agentAssigned };
}

// ── Step 6: Sentinel Approval & Execution Gate ────────────────────────────

/**
 * Short German labels for each possible Sentinel approval gate status.
 * Used by normalizeSentinelApprovalSummary() and the tests.
 */
export const SENTINEL_APPROVAL_STATUS_LABELS_DE = {
  open:     "Offen",
  pending:  "Läuft",
  approved: "Freigegeben",
  rejected: "Abgelehnt",
  blocked:  "Blockiert",
};

/**
 * Short German labels for each possible Sentinel execution status.
 */
export const SENTINEL_EXECUTION_STATUS_LABELS_DE = {
  not_ready:  "Nicht bereit",
  ready:      "Bereit",
  running:    "Läuft",
  completed:  "Abgeschlossen",
  failed:     "Fehlgeschlagen",
};

const KNOWN_APPROVAL_STATUSES = new Set(Object.keys(SENTINEL_APPROVAL_STATUS_LABELS_DE));
const KNOWN_EXECUTION_STATUSES = new Set(Object.keys(SENTINEL_EXECUTION_STATUS_LABELS_DE));

/**
 * Normalise the raw response from GET .../issues/:id/approval-summary into a
 * stable, display-safe shape.
 *
 * Returns:
 *   {
 *     issueId:       string|null
 *     repairReady:   boolean   – repair proposal has been prepared
 *     claude:        "open"|"pending"|"approved"|"rejected"|"blocked"
 *     openai:        "open"|"pending"|"approved"|"rejected"|"blocked"
 *     safetyGate:    "open"|"green"|"blocked"
 *     execution:     "not_ready"|"ready"|"running"|"completed"|"failed"
 *     executionReady: boolean  – ONLY true when backend explicitly signals it
 *     blockReason:   string|null  – short German reason when blocked (max 120 chars)
 *   }
 *
 * Frontend MUST NOT infer executionReady locally.
 * Never throws; malformed data resolves to safe defaults.
 *
 * @param {any} data - raw API response body
 */
export function normalizeSentinelApprovalSummary(data) {
  const empty = {
    issueId: null,
    repairReady: false,
    claude: "open",
    openai: "open",
    safetyGate: "open",
    execution: "not_ready",
    executionReady: false,
    blockReason: null,
  };
  if (!data || typeof data !== "object") return empty;

  const issueId = typeof data.issueId === "string" && data.issueId
    ? data.issueId
    : typeof data.issue_id === "string" ? data.issue_id : null;

  const repairReady = Boolean(data.repairReady ?? data.repair_ready ?? false);

  // Claude gate status
  const rawClaude = typeof data.claude?.status === "string"
    ? data.claude.status.trim().toLowerCase()
    : typeof data.claudeStatus === "string"
      ? data.claudeStatus.trim().toLowerCase()
      : "open";
  const claude = KNOWN_APPROVAL_STATUSES.has(rawClaude) ? rawClaude : "open";

  // OpenAI gate status
  const rawOpenai = typeof data.openai?.status === "string"
    ? data.openai.status.trim().toLowerCase()
    : typeof data.openaiStatus === "string"
      ? data.openaiStatus.trim().toLowerCase()
      : "open";
  const openai = KNOWN_APPROVAL_STATUSES.has(rawOpenai) ? rawOpenai : "open";

  // Safety gate status ("open", "green", "blocked")
  const rawSafety = typeof data.safetyGate?.status === "string"
    ? data.safetyGate.status.trim().toLowerCase()
    : typeof data.safetyGateStatus === "string"
      ? data.safetyGateStatus.trim().toLowerCase()
      : "open";
  const safetyGate = ["open", "green", "blocked"].includes(rawSafety) ? rawSafety : "open";

  // Execution status
  const rawExec = typeof data.execution?.status === "string"
    ? data.execution.status.trim().toLowerCase()
    : typeof data.executionStatus === "string"
      ? data.executionStatus.trim().toLowerCase()
      : "not_ready";
  const execution = KNOWN_EXECUTION_STATUSES.has(rawExec) ? rawExec : "not_ready";

  // executionReady: ONLY true when backend explicitly sends it – never inferred
  const executionReady = data.executionReady === true || data.execution_ready === true;

  // Block reason: short German text, max 120 chars, no raw JSON
  const rawReason = typeof data.blockReason === "string" && data.blockReason
    ? data.blockReason
    : typeof data.block_reason === "string" && data.block_reason
      ? data.block_reason
      : null;
  const blockReason = rawReason ? rawReason.slice(0, 120) : null;

  return { issueId, repairReady, claude, openai, safetyGate, execution, executionReady, blockReason };
}

/**
 * Build a short German chat bubble text for a Sentinel approval step result.
 *
 * step values:
 *   "start"    – approval process started (proposal being prepared)
 *   "claude"   – Claude has reviewed
 *   "openai"   – OpenAI has decided
 *   "evaluate" – all gates evaluated
 *   "execute"  – execution completed
 *   "blocked"  – execution was blocked
 *
 * Returns a short, user-friendly German sentence (max 250 chars).
 * Never returns raw JSON, secrets, or technical details.
 *
 * @param {string} step     - which step occurred
 * @param {any}    data     - raw API response data for that step
 * @returns {string}
 */
export function buildApprovalChatBubble(step, data) {
  const s = typeof step === "string" ? step.trim().toLowerCase() : "";

  if (s === "start") {
    return "Ich bereite den Vorschlag vor. Noch wird nichts ausgeführt.";
  }

  if (s === "claude") {
    const status = typeof data?.claude?.status === "string"
      ? data.claude.status.trim().toLowerCase()
      : (data?.success === true ? "approved" : "open");
    if (status === "approved") {
      return "Claude hat den Vorschlag geprüft und freigegeben.";
    }
    if (status === "rejected") {
      const reason = typeof data?.claude?.reason === "string" && data.claude.reason
        ? data.claude.reason.slice(0, 80)
        : "Kein Grund angegeben.";
      return `Claude hat den Vorschlag abgelehnt: ${reason}`;
    }
    return "Claude hat den Vorschlag geprüft.";
  }

  if (s === "openai") {
    const status = typeof data?.openai?.status === "string"
      ? data.openai.status.trim().toLowerCase()
      : (data?.success === true ? "approved" : "open");
    if (status === "approved") {
      return "OpenAI hat priorisiert und die Umsetzung freigegeben.";
    }
    if (status === "rejected") {
      const reason = typeof data?.openai?.reason === "string" && data.openai.reason
        ? data.openai.reason.slice(0, 80)
        : "Kein Grund angegeben.";
      return `OpenAI hat die Umsetzung abgelehnt: ${reason}`;
    }
    return "OpenAI hat die Priorisierung abgeschlossen.";
  }

  if (s === "evaluate") {
    if (data?.executionReady === true || data?.execution_ready === true) {
      return "SafetyGate ist grün. Die Änderung ist bereit zur Ausführung.";
    }
    const reason = typeof data?.blockReason === "string" && data.blockReason
      ? data.blockReason.slice(0, 80)
      : typeof data?.block_reason === "string" && data.block_reason
        ? data.block_reason.slice(0, 80)
        : "Nicht alle Freigaben vorhanden.";
    return `Die Ausführung wurde blockiert: ${reason}`;
  }

  if (s === "execute") {
    if (data?.success === true || data?.status === "completed" || data?.status === "success") {
      const rawSha = data?.commitSha || data?.commit_sha || data?.sha || null;
      const sha = typeof rawSha === "string" && rawSha ? rawSha.slice(0, 7) : null;
      const base = "Die Änderung wurde auf main angewendet. Das Deployment läuft automatisch über Railway/Vercel.";
      return sha ? `${base} Commit: ${sha}` : base;
    }
    const reason = typeof data?.reason === "string" && data.reason
      ? data.reason.slice(0, 80)
      : typeof data?.message === "string" && data.message
        ? data.message.slice(0, 80)
        : "Unbekannter Fehler.";
    return `Ausführung fehlgeschlagen: ${reason}`;
  }

  if (s === "blocked") {
    const reason = typeof data?.reason === "string" && data.reason
      ? data.reason.slice(0, 80)
      : "Nicht alle Freigaben vorhanden.";
    return `Die Ausführung wurde blockiert: ${reason}`;
  }

  return "Freigabe-Schritt abgeschlossen.";
}

/**
 * Detect approval-related chat commands from user input.
 *
 * Returns one of:
 *   "start_approval"     – "Vorschlag vorbereiten"
 *   "claude_review"      – "lass Claude prüfen"
 *   "openai_decision"    – "OpenAI soll entscheiden"
 *   "evaluate_approval"  – "prüfe Freigabe"
 *   "execute_issue"      – "führe aus" / "mach das" / "setze es um"
 *   null                 – not an approval command
 *
 * Frontend sends these as chat messages to the backend.
 * Execution is only started when the backend returns executionReady=true.
 *
 * @param {string} text
 * @returns {string|null}
 */
export function detectApprovalIntent(text) {
  if (typeof text !== "string") return null;
  const t = text.trim().toLowerCase();
  if (!t) return null;

  if (/\bvorschlag\s+vorbereiten\b/.test(t)) return "start_approval";
  if (/\blass\s+claude\s+pr[üu]fen\b/.test(t) || /\bclaude\s+soll\s+pr[üu]fen\b/.test(t)) return "claude_review";
  if (/\bopenai\s+soll\s+entscheiden\b/.test(t) || /\bopenai\s+entscheiden\b/.test(t)) return "openai_decision";
  if (/\bpr[üu]fe\s+freigabe\b/.test(t) || /\bfreigabe\s+pr[üu]fen\b/.test(t)) return "evaluate_approval";
  if (/\bf[üu]hre?\s+aus\b/.test(t) || /\bfuehre?\s+aus\b/.test(t)) return "execute_issue";
  if (/\bmach\s+das\b/.test(t)) return "execute_issue";
  if (/\bsetze?\s+(es\s+)?um\b/.test(t)) return "execute_issue";

  return null;
}

/**
 * Derive the primary action button for the Sentinel approval/execution flow.
 *
 * Rules per spec:
 *   - "prepare_proposal"  when no repair proposed yet (repairReady=false)
 *   - "check_approval"    when proposal ready but Claude/OpenAI/Safety incomplete
 *   - "execute_approved"  ONLY when backend explicitly sends executionReady=true
 *   - "refresh_status"    when execution is running
 *   - null                when execution is completed/failed, or no active issue,
 *                         or intent is general_chat/system_question
 *
 * Frontend MUST NOT set executionReady to true locally.
 *
 * @param {object} approvalSummary – from normalizeSentinelApprovalSummary
 * @param {string} [intent]        – current chat intent
 * @returns {{ id: string, label: string }|null}
 */
export function deriveApprovalPrimaryAction(approvalSummary, intent) {
  // No approval buttons for non-workflow intents
  if (intent === "general_chat" || intent === "system_question") return null;
  if (!approvalSummary || typeof approvalSummary !== "object") return null;

  const { repairReady, claude, openai, safetyGate, execution, executionReady } = approvalSummary;

  if (execution === "running") return AGENT_PRIMARY_ACTIONS.refresh_status;
  if (execution === "completed" || execution === "failed") return null;

  // executionReady comes exclusively from the backend
  if (executionReady === true) return AGENT_PRIMARY_ACTIONS.execute_approved;

  if (!repairReady) return AGENT_PRIMARY_ACTIONS.prepare_proposal;

  // Proposal is ready, check if all gates have passed
  const allGatesOpen =
    claude !== "approved" ||
    openai !== "approved" ||
    safetyGate !== "green";
  if (allGatesOpen) return AGENT_PRIMARY_ACTIONS.check_approval;

  return null;
}

// ── Step 7: Live Polling & Chat Status Updates ─────────────────────────────

/**
 * Default polling interval in milliseconds (8 s – between the 6–10 s spec range).
 * Kept as a named constant so tests can reference it without hard-coding.
 */
export const SENTINEL_LIVE_POLL_INTERVAL_MS = 8_000;

/**
 * Decide whether live status polling should be active.
 *
 * Rules (in priority order):
 *   1. Never poll for `general_chat` or `system_question` – no workflow context.
 *   2. Stop polling when phase is `completed` or `blocked` – no further action.
 *   3. Stop polling when execution is `completed` or `failed`.
 *   4. Poll when there is an `activeIssueId` being worked on.
 *   5. Poll when persistentIssues has any open/preparing/queued items.
 *   6. Otherwise do not poll.
 *
 * @param {object} ctx
 * @param {string}  [ctx.intent]           – current chat intent
 * @param {Array}   [ctx.persistentIssues] – normalised persistent issues
 * @param {string|null} [ctx.activeIssueId]
 * @param {object|null} [ctx.approvalSummary] – normalizeSentinelApprovalSummary result
 * @param {string}  [ctx.phase]            – current chat phase
 * @returns {boolean}
 */
export function shouldPollLiveStatus({ intent, persistentIssues, activeIssueId, approvalSummary, phase } = {}) {
  // Rule 1: no polling in pure conversation modes
  if (intent === "general_chat" || intent === "system_question") return false;

  // Rule 2: terminal phases stop polling
  const p = normalizeAgentWorkPhase(phase);
  if (p === "completed" || p === "blocked") return false;

  // Rule 3: terminal approval/execution states stop polling
  const execStatus = approvalSummary?.execution;
  if (execStatus === "completed" || execStatus === "failed") return false;

  // Rule 4: active issue in focus → poll
  if (activeIssueId && typeof activeIssueId === "string") return true;

  // Rule 5: open persistent issues → poll
  if (Array.isArray(persistentIssues) && persistentIssues.length > 0) {
    const hasOpen = persistentIssues.some(
      (i) => i.status === "open" || i.status === "preparing" || i.status === "queued",
    );
    if (hasOpen) return true;
  }

  return false;
}

/**
 * Normalise the raw response from GET .../issues/:id/live-status into a
 * stable display-safe snapshot for polling comparisons.
 *
 * Returns:
 *   {
 *     issueId:       string|null
 *     phase:         normalised phase string
 *     executionReady: boolean  – ONLY true when backend explicitly sends it
 *     claude:        approval status string
 *     openai:        approval status string
 *     safetyGate:    "open"|"green"|"blocked"
 *     execution:     execution status string
 *     blockReason:   string|null
 *     nextStep:      string|null
 *     updatedAt:     string|null
 *   }
 *
 * Frontend MUST NOT infer executionReady from other fields.
 * Never throws; malformed data resolves to safe defaults.
 *
 * @param {any} data – raw API response body
 */
export function normalizeSentinelIssueLiveStatus(data) {
  if (!data || typeof data !== "object") {
    return {
      issueId: null,
      phase: "idle",
      executionReady: false,
      claude: "open",
      openai: "open",
      safetyGate: "open",
      execution: "not_ready",
      blockReason: null,
      nextStep: null,
      updatedAt: null,
    };
  }

  const issueId = typeof data.issueId === "string" && data.issueId
    ? data.issueId
    : typeof data.issue_id === "string" ? data.issue_id : null;

  const phase = normalizeAgentWorkPhase(data.phase || data.status);

  // executionReady: exclusively from backend
  const executionReady = data.executionReady === true || data.execution_ready === true;

  // Claude gate
  const rawClaude = typeof data.claude?.status === "string"
    ? data.claude.status.trim().toLowerCase()
    : typeof data.claudeStatus === "string" ? data.claudeStatus.trim().toLowerCase() : "open";
  const KNOWN_APPROVAL = new Set(["open", "pending", "approved", "rejected", "blocked"]);
  const claude = KNOWN_APPROVAL.has(rawClaude) ? rawClaude : "open";

  // OpenAI gate
  const rawOpenai = typeof data.openai?.status === "string"
    ? data.openai.status.trim().toLowerCase()
    : typeof data.openaiStatus === "string" ? data.openaiStatus.trim().toLowerCase() : "open";
  const openai = KNOWN_APPROVAL.has(rawOpenai) ? rawOpenai : "open";

  // Safety gate
  const rawSafety = typeof data.safetyGate?.status === "string"
    ? data.safetyGate.status.trim().toLowerCase()
    : typeof data.safetyGateStatus === "string" ? data.safetyGateStatus.trim().toLowerCase() : "open";
  const safetyGate = ["open", "green", "blocked"].includes(rawSafety) ? rawSafety : "open";

  // Execution status
  const KNOWN_EXEC = new Set(["not_ready", "ready", "running", "completed", "failed"]);
  const rawExec = typeof data.execution?.status === "string"
    ? data.execution.status.trim().toLowerCase()
    : typeof data.executionStatus === "string" ? data.executionStatus.trim().toLowerCase() : "not_ready";
  const execution = KNOWN_EXEC.has(rawExec) ? rawExec : "not_ready";

  const rawReason = typeof data.blockReason === "string" && data.blockReason
    ? data.blockReason
    : typeof data.block_reason === "string" ? data.block_reason : null;
  const blockReason = rawReason ? rawReason.slice(0, 120) : null;

  const rawNextStep = typeof data.nextStep === "string" && data.nextStep
    ? data.nextStep
    : typeof data.next_step === "string" ? data.next_step : null;
  const nextStep = rawNextStep ? rawNextStep.slice(0, 120) : null;

  const updatedAt = typeof data.updatedAt === "string" ? data.updatedAt
    : typeof data.updated_at === "string" ? data.updated_at : null;

  return { issueId, phase, executionReady, claude, openai, safetyGate, execution, blockReason, nextStep, updatedAt };
}

/**
 * Build a short German chat bubble text for a live status change.
 *
 * Returns `null` when nothing meaningful changed (deduplication guard).
 * Each text is ≤ 100 chars so it stays compact on mobile.
 *
 * Transition rules (checked in priority order):
 *   execution completed  → "Die Änderung wurde abgeschlossen."
 *   execution running    → "Die Änderung wird ausgeführt."
 *   execution blocked    → "Die Ausführung wurde blockiert. Details findest du im Drawer."
 *   executionReady=true  → "Die Freigabekette ist vollständig. Du kannst jetzt ausführen."
 *   claude+openai both approved → "Freigabe durch Claude und OpenAI. SafetyGate prüft."
 *   claude approved (openai pending) → "Claude hat den Vorschlag geprüft. OpenAI entscheidet noch."
 *   claude pending (just started) → "Ich prüfe gerade die Freigabe."
 *
 * @param {object|null} prevStatus – previous normalizeSentinelIssueLiveStatus result (null on first poll)
 * @param {object}      nextStatus – current normalizeSentinelIssueLiveStatus result
 * @returns {string|null}
 */
export function buildLiveStatusChatBubble(prevStatus, nextStatus) {
  if (!nextStatus || typeof nextStatus !== "object") return null;

  const prev = prevStatus || {};

  // Execution completed
  if (nextStatus.execution === "completed" && prev.execution !== "completed") {
    return "Die Änderung wurde abgeschlossen.";
  }

  // Execution running
  if (nextStatus.execution === "running" && prev.execution !== "running") {
    return "Die Änderung wird ausgeführt.";
  }

  // Execution blocked / failed
  if (
    (nextStatus.execution === "failed" || nextStatus.phase === "blocked") &&
    prev.execution !== "failed" &&
    prev.phase !== "blocked"
  ) {
    return "Die Ausführung wurde blockiert. Details findest du im Drawer.";
  }

  // executionReady just became true
  if (nextStatus.executionReady === true && !prev.executionReady) {
    return "Die Freigabekette ist vollständig. Du kannst jetzt ausführen.";
  }

  // Both Claude and OpenAI just approved
  if (
    nextStatus.claude === "approved" &&
    nextStatus.openai === "approved" &&
    !(prev.claude === "approved" && prev.openai === "approved")
  ) {
    return "Freigabe durch Claude und OpenAI. SafetyGate prüft.";
  }

  // Claude approved, OpenAI still pending
  if (nextStatus.claude === "approved" && prev.claude !== "approved" && nextStatus.openai !== "approved") {
    return "Claude hat den Vorschlag geprüft. OpenAI entscheidet noch.";
  }

  // Approval just started (claude moved from open to pending)
  if (nextStatus.claude === "pending" && (!prev.claude || prev.claude === "open")) {
    return "Ich prüfe gerade die Freigabe.";
  }

  return null;
}

/**
 * Build the compact live sentinel bar line from the live repair-queue snapshot.
 *
 * Spec examples:
 *   "Wächter: 2 offene Punkte · 1 kritisch"
 *   "Wächter: Freigabe bereit"
 *   "Wächter: Ausführung läuft"
 *   "Wächter: keine offenen Punkte"
 *
 * Falls back to `buildPersistentSentinelStatusLine` patterns when the live
 * endpoint returns the same shape as the regular repair-queue.
 * Returns null when data is missing/unavailable (caller keeps previous line).
 *
 * @param {any} data – raw API response body from GET .../repair-queue/live
 * @returns {string|null}
 */
export function buildRepairQueueLiveLine(data) {
  if (!data || typeof data !== "object") return null;
  // Unavailable (404/501) → caller keeps previous line
  if (data.unavailable) return null;

  const rawItems =
    Array.isArray(data.items) ? data.items :
    Array.isArray(data.queue) ? data.queue :
    Array.isArray(data.data?.items) ? data.data.items : [];

  const isRunning = rawItems.some((i) => i?.status === "executing" || i?.status === "running");
  if (isRunning) return "Wächter: Ausführung läuft";

  const isApprovalReady = rawItems.some((i) => i?.status === "approved");
  if (isApprovalReady) return "Wächter: Freigabe bereit";

  const openItems = rawItems.filter(
    (i) => i?.status === "open" || i?.status === "queued" || i?.status === "awaiting_approval" || i?.status === "preparing",
  );

  if (openItems.length === 0) return "Wächter: keine offenen Punkte";

  const criticalCount = openItems.filter(
    (i) => i?.risk === "critical" || i?.risk === "high",
  ).length;

  const countText = openItems.length === 1 ? "1 offener Punkt" : `${openItems.length} offene Punkte`;
  let line = `Wächter: ${countText}`;
  if (criticalCount > 0) {
    line += ` · ${criticalCount === 1 ? "1 kritisch" : `${criticalCount} kritisch`}`;
  }
  return line.length <= 80 ? line : line.slice(0, 78) + "…";
}

// ── Sentinel scan-and-route fallback (Step 4) ─────────────────────────────

/**
 * Determine whether a scan-and-route API result indicates the endpoint is
 * not yet deployed.  Returns true when the caller should fall back to the
 * standard runAgentSentinelScan() endpoint.
 *
 * Triggers:
 *   - null / undefined result (network failure)
 *   - HTTP 404 (endpoint not found)
 *   - HTTP 501 (not implemented)
 *   - HTTP 405 (method not allowed)
 *   - error without any data payload
 *
 * @param {any} result - API response object (may be null/undefined)
 * @returns {boolean}
 */
export function isScanAndRouteUnavailable(result) {
  if (!result) return true;
  const s = result.status;
  if (s === 404 || s === 501 || s === 405) return true;
  if (result.error && !result.data) return true;
  return false;
}

// ── System Awareness (Step 2) ──────────────────────────────────────────────

/**
 * Normalise a raw awareness target label to a short, mobile-friendly chip label.
 *
 * Long file paths are collapsed to the basename so the main view stays clean.
 * Technical raw text is trimmed to 30 characters.  Pass long content to the
 * details drawer instead.
 *
 * @param {string|object} raw  - raw target entry from the backend
 * @returns {string|null}       - short chip label or null if unusable
 */
export function normalizeAwarenessTargetLabel(raw) {
  const str =
    typeof raw === "string" ? raw :
    typeof raw?.label === "string" ? raw.label :
    typeof raw?.name  === "string" ? raw.name  :
    typeof raw?.target === "string" ? raw.target :
    typeof raw?.component === "string" ? raw.component : null;

  if (!str || typeof str !== "string") return null;
  const trimmed = str.trim();
  if (!trimmed) return null;

  // Strip long absolute / relative paths: keep basename only.
  // e.g. "src/components/shared/agent/work/AgentWorkChat.jsx" → "AgentWorkChat.jsx"
  const slashIdx = Math.max(trimmed.lastIndexOf("/"), trimmed.lastIndexOf("\\"));
  const short = slashIdx >= 0 ? trimmed.slice(slashIdx + 1) : trimmed;

  // Trim to 30 chars for chip display.
  return short.length > 30 ? short.slice(0, 28) + "…" : short;
}

/**
 * Normalise an array of raw awareness targets to a deduplicated list of
 * short chip labels (at most MAX_AWARENESS_CHIPS).
 *
 * Returns an empty array when nothing usable is present – never null/undefined.
 *
 * @param {any} rawTargets
 * @returns {string[]}
 */
export const MAX_AWARENESS_CHIPS = 5;

export function normalizeAwarenessTargets(rawTargets) {
  if (!Array.isArray(rawTargets) || rawTargets.length === 0) return [];
  const seen = new Set();
  const result = [];
  for (const t of rawTargets) {
    if (result.length >= MAX_AWARENESS_CHIPS) break;
    const label = normalizeAwarenessTargetLabel(t);
    if (label && !seen.has(label)) {
      seen.add(label);
      result.push(label);
    }
  }
  return result;
}

/**
 * Extract a systemAwareness block from the raw backend response.
 *
 * Supported field paths (in priority order):
 *   data.metadata.awareness         → { targets, recommendedAgent, confidence, reason }
 *   data.metadata.targets           → targets array only
 *   data.metadata.systemTargets     → targets array only
 *   data.systemAwareness            → { targets, recommendedAgent, confidence, reason }
 *   data.targets                    → targets array only
 *
 * Returns a normalised object:
 *   { targets: string[], recommendedAgent: string|null,
 *     confidence: number|null, reason: string|null, rawTargets: any[] }
 *
 * Never throws; missing fields default to safe values.
 */
export function normalizeSystemAwareness(data) {
  if (!data || typeof data !== "object") {
    return { targets: [], recommendedAgent: null, confidence: null, reason: null, rawTargets: [] };
  }

  // Resolve the awareness block from all known paths.
  const awareness =
    (data.metadata && typeof data.metadata === "object" && data.metadata.awareness) ||
    (data.systemAwareness && typeof data.systemAwareness === "object" ? data.systemAwareness : null) ||
    null;

  // Resolve raw targets from the awareness block or from top-level fallbacks.
  const rawTargets =
    (Array.isArray(awareness?.targets) && awareness.targets) ||
    (Array.isArray(data.metadata?.targets) && data.metadata.targets) ||
    (Array.isArray(data.metadata?.systemTargets) && data.metadata.systemTargets) ||
    (Array.isArray(data.targets) && data.targets) ||
    [];

  const targets = normalizeAwarenessTargets(rawTargets);

  const recommendedAgent =
    typeof awareness?.recommendedAgent === "string" && awareness.recommendedAgent
      ? awareness.recommendedAgent
      : typeof data.metadata?.recommendedAgent === "string" ? data.metadata.recommendedAgent : null;

  const rawConfidence = awareness?.confidence ?? data.metadata?.confidence ?? null;
  const confidence =
    typeof rawConfidence === "number" && rawConfidence >= 0 && rawConfidence <= 1
      ? rawConfidence
      : null;

  const reason =
    typeof awareness?.reason === "string" && awareness.reason
      ? awareness.reason
      : typeof data.metadata?.reason === "string" ? data.metadata.reason : null;

  return { targets, recommendedAgent, confidence, reason, rawTargets };
}

/**
 * Build a human-readable awareness hint for the status bar.
 *
 * Examples:
 *   0 targets  → null (nothing shown)
 *   1 target   → "Kontext erkannt"
 *   2+ targets → "3 Bereiche erkannt"
 *
 * @param {string[]} targets - normalised chip labels
 * @returns {string|null}
 */
export function buildAwarenessStatusHint(targets) {
  if (!Array.isArray(targets) || targets.length === 0) return null;
  if (targets.length === 1) return "Kontext erkannt";
  return `${targets.length} Bereiche erkannt`;
}

// Phases where the agent is actively working or waiting for approval.
// The statusLine uses the phase-derived label for these to show progress
// (e.g. "DeepSeek prüft Datenfluss", "Bereit zur Ausführung"); for all
// other phases the intent label is preferred.
const ACTIVE_STATUS_PHASES = new Set(["analyzing", "reviewing", "executing", "waiting_approval"]);


/**
 * Normalise the raw response body from POST /api/admin/agent-work/chat into
 * a stable shape that AgentWorkChat can consume directly:
 *
 *   {
 *     text            – main agent message text for the chat bubble
 *     phase           – normalised phase string
 *     sessionId       – updated session ID (or null)
 *     executionReady  – true when the agent has a change ready to execute
 *     claudeChecked   – true when Claude reviewed the change
 *     openAiApproved  – true when OpenAI approved the change
 *     events          – array of short human-readable event strings (max 5)
 *     statusLine      – pre-built status bar label (short German phrase)
 *   }
 *
 * @param {object}  data       - raw response body from the API
 * @param {string}  [agentLabel] - optional label for the status bar ("DeepSeek", "Claude", …)
 *
 * The function never throws; unknown/missing fields resolve to safe defaults.
 */
export function normalizeAgentWorkChatResponse(data, agentLabel) {
  if (!data || typeof data !== "object") {
    return {
      text: "", fullText: "", displayText: "", phase: "idle", sessionId: null,
      executionReady: false, claudeChecked: false, openAiApproved: false,
      events: [], statusLine: null,
      workflowActive: false, workflowType: null, workflowPhase: null,
      progressLabel: null, currentStep: null, workflowSteps: [],
      agentFlow: normalizeAgentFlowMeta(null),
      canonicalRoute: null, isCanonical: null, legacyRouteUsed: null,
      routeType: null, flowType: "unknown", executionPath: null,
      sourceStage: null, sourceRoute: null, route: null, endpoint: null, routeLabel: null,
      blockReason: null, blockReasonText: null, safetyGateStatus: null, safetyGateActive: null,
      requiresApproval: null, nextRequiredStep: null, nextRequiredStepText: null, errorCode: null,
      designContextUsed: null, screenshotContextUsed: null, screenshotImageForwarded: null,
      screenshotsMissingContent: [], screenshotProviderCapability: null,
      visualContextUsed: null, visualContextUsedByV0: null, hasScreenshotDataUrl: null,
      supportsDirectImageInput: null, supportsVisualContextSummary: null,
      designProposal: null, generatedImage: null, imageGenerationAvailable: null, imageGenerationError: null,
      controlled_real_repair_status: null, latest_controlled_repair_run: null,
      latest_pr_url: null, latest_branch: null, latest_commit_sha: null,
      latest_changed_file: null, latest_work_order_id: null, latest_token_status: null,
      latest_result_status: null, latest_safety_status: null,
      ready_for_dashboard_e2e_test: false, next_recommended_action: null,
    };
  }

  // Extract agent reply text – keep fullText agent-neutral across providers.
  // Newer payloads may send fullText/displayText, older ones assistantMessage /
  // reply / message / text / content / answer.
  const { fullText, displayText } = resolveAgentMessageTextParts(data);
  const text = fullText;

  const workflowActive = data.workflowActive === true || data.workflow_active === true;
  const workflowPhaseRaw = typeof data.workflowPhase === "string" ? data.workflowPhase :
    typeof data.workflow_phase === "string" ? data.workflow_phase : null;
  const currentStep = typeof data.currentStep === "string" ? data.currentStep :
    typeof data.current_step === "string" ? data.current_step : null;
  const rawProgressLabel = typeof data.progressLabel === "string" ? data.progressLabel :
    typeof data.progress_label === "string" ? data.progress_label : null;
  const progressLabel = buildBackendWorkflowProgressLabel({
    progressLabel: rawProgressLabel,
    currentStep,
    workflowPhase: workflowPhaseRaw,
    workflowActive,
  });

  const normalizedPhase = normalizeAgentWorkPhase(
    workflowActive ? (workflowPhaseRaw || data.phase || data.status || "running") : (data.phase || data.status),
  );
  const phase = workflowActive && normalizedPhase === "idle" ? "analyzing" : normalizedPhase;
  const sessionId = typeof data.sessionId === "string" && data.sessionId
    ? data.sessionId
    : typeof data.session_id === "string" && data.session_id
      ? data.session_id
      : null;

  const executionReady = data.executionReady === true || data.execution_ready === true;
  const claudeChecked = Boolean(
    data.claudeChecked ?? data.claude_checked ?? data.reviews?.claude,
  );
  const openAiApproved = Boolean(
    data.openAiApproved ?? data.openai_approved ?? data.reviews?.openai?.approved,
  );

  // Collect up to 5 short event strings (never raw objects in main view).
  const events = Array.isArray(data.events)
    ? data.events
        .map((e) => (typeof e === "string" ? e : (e?.message || e?.description || "")))
        .filter(Boolean)
        .slice(0, 5)
    : [];

  // Extract intent, falling back to workflowType/general_chat for unknown values.
  const rawIntent = typeof data.intent === "string" ? data.intent : "";
  const workflowType = normalizeBackendWorkflowType(
    typeof data.workflowType === "string" ? data.workflowType :
      typeof data.workflow_type === "string" ? data.workflow_type : null,
    KNOWN_INTENTS.has(rawIntent) ? rawIntent : "general_chat",
  );
  const intent = KNOWN_INTENTS.has(rawIntent)
    ? rawIntent
    : workflowActive
      ? workflowType
      : "general_chat";

  const reviews = {
    claude: { checked: claudeChecked },
    openai: { approved: openAiApproved },
  };

  // For active/waiting phases use the phase-derived label (e.g. "Bereit zur
  // Ausführung"); for settled phases use the intent label so the user sees
  // a human-readable conversation mode rather than a stale phase string.
  const phaseLine = buildAgentStatusLine(phase, agentLabel || null, reviews);
  const intentLabel = AGENT_INTENT_LABELS_DE[intent];
  const statusLine = workflowActive && progressLabel
    ? progressLabel
    : ACTIVE_STATUS_PHASES.has(phase) ? phaseLine : (intentLabel || phaseLine);

  // Extract track / mode / autonomyLevel (optional; backend may omit them).
  const track = typeof data.track === "string" ? data.track.trim().toLowerCase() : null;
  const mode  = typeof data.mode  === "string" ? data.mode.trim().toLowerCase()  : null;
  const autonomyLevel = typeof data.autonomyLevel === "string" ? data.autonomyLevel :
    typeof data.autonomy_level === "string" ? data.autonomy_level : null;
  // Derive a discreet single-word track label from track+mode (or phase as fallback).
  const trackLabel = normalizeAgentTrack(track, mode || phase);

  // Extract system awareness block (Step 2).
  const awareness = normalizeSystemAwareness(data);
  const agentFlow = normalizeAgentFlowMeta(data);

  const workflowSteps = workflowActive
    ? (Array.isArray(data.progressSteps) && data.progressSteps.length > 0
        ? data.progressSteps.map((s) => (typeof s === "string" ? s : s?.label || "")).filter(Boolean).slice(0, 8)
        : AGENT_BACKEND_WORKFLOW_PROGRESS_STEPS_DE)
    : [];

  // Step 11B: additional backend workflow contract fields
  const rawIssueCount  = data.issueCount  ?? data.issue_count  ?? null;
  const issueCount     = typeof rawIssueCount === "number" && rawIssueCount >= 0 ? Math.floor(rawIssueCount) : 0;
  const rawRoutedCount = data.routedCount ?? data.routed_count ?? null;
  const routedCount    = typeof rawRoutedCount === "number" && rawRoutedCount >= 0 ? Math.floor(rawRoutedCount) : 0;
  const nextAction     = typeof data.nextAction === "string" && data.nextAction
    ? data.nextAction.slice(0, 100)
    : typeof data.next_action === "string" ? data.next_action.slice(0, 100) : null;

  // toolResults: array of result entries from backend (safe, no secrets)
  const toolResults = Array.isArray(data.toolResults) ? data.toolResults :
    Array.isArray(data.tool_results) ? data.tool_results : [];

  // detailsSections: backend-provided drawer sections (safe structured data)
  const detailsSections = Array.isArray(data.detailsSections) ? data.detailsSections :
    Array.isArray(data.details_sections) ? data.details_sections : [];

  // Step 13: Pure Engine / Provider Call fields
  // providerCall: true when an actual LLM provider was called; false when no call was made.
  // null means the backend did not send this field (old backend – treated as unknown).
  const providerCall = data.providerCall !== undefined
    ? (data.providerCall === true ? true : data.providerCall === false ? false : null)
    : data.provider_call !== undefined
      ? (data.provider_call === true ? true : data.provider_call === false ? false : null)
      : null;
  // source: "provider" for real LLM answer, "error" for error, "fallback" for local fallback.
  const source = typeof data.source === "string" ? data.source.trim().toLowerCase() : null;
  // fallbackUsed: true when the backend fell back to a local/static response.
  const fallbackUsed = data.fallbackUsed === true || data.fallback_used === true;
  // errorCode: optional machine-readable error key from the backend.
  const errorCode = typeof data.errorCode === "string" ? data.errorCode :
    typeof data.error_code === "string" ? data.error_code : null;
  // errorCategory: optional human-readable error category from the backend contract.
  const errorCategory = typeof data.errorCategory === "string" ? data.errorCategory :
    typeof data.error_category === "string" ? data.error_category : null;
  // traceId: optional distributed tracing identifier for support lookups.
  const traceId = typeof data.traceId === "string" && data.traceId
    ? data.traceId
    : typeof data.trace_id === "string" && data.trace_id
      ? data.trace_id
      : null;
  // retryable: true when the backend signals the request can be retried safely.
  const retryable = data.retryable === true ? true : data.retryable === false ? false : null;
  // provider: short display name of the LLM provider ("Claude", "OpenAI", …)
  const providerName = typeof data.provider === "string" ? data.provider.slice(0, 60) : null;

  // Step 15: Pending Actions – extract backend pendingAction object if present.
  const pendingAction = normalizePendingAction(
    data.pendingAction !== undefined ? data.pendingAction :
    data.pending_action !== undefined ? data.pending_action : null,
  );

  // Step 16: Workflow State & Execution Verification fields
  // workflowState: canonical workflow state machine key from backend
  const rawWorkflowState = typeof data.workflowState === "string" ? data.workflowState :
    typeof data.workflow_state === "string" ? data.workflow_state : null;
  const workflowState = rawWorkflowState ? rawWorkflowState.trim().toLowerCase() : null;
  // workflowRunId: ID of the currently running workflow
  const workflowRunId = typeof data.workflowRunId === "string" && data.workflowRunId
    ? data.workflowRunId
    : typeof data.workflow_run_id === "string" && data.workflow_run_id
      ? data.workflow_run_id
      : null;
  // workflowMessage: human-readable message from the workflow engine
  const workflowMessage = typeof data.workflowMessage === "string" && data.workflowMessage
    ? data.workflowMessage.slice(0, 200)
    : typeof data.workflow_message === "string" && data.workflow_message
      ? data.workflow_message.slice(0, 200)
      : null;
  // executionClaimVerified: backend has verified the execution claim is accurate
  const executionClaimVerified =
    data.executionClaimVerified === true  || data.execution_claim_verified === true  ? true :
    data.executionClaimVerified === false || data.execution_claim_verified === false ? false : null;
  // realExecutionPerformed: backend confirms a real side-effecting action was performed
  const realExecutionPerformed =
    data.realExecutionPerformed === true  || data.real_execution_performed === true  ? true :
    data.realExecutionPerformed === false || data.real_execution_performed === false ? false : null;
  // nextUserAction: hint from the backend about what the user should do next
  const nextUserAction = typeof data.nextUserAction === "string" && data.nextUserAction
    ? data.nextUserAction.slice(0, 200)
    : typeof data.next_user_action === "string" && data.next_user_action
      ? data.next_user_action.slice(0, 200)
      : null;

  // Follow-up Context UX V1: followUpResolved signals the backend matched the
  // follow-up command against the previous agent reply.
  const followUpResolved =
    data.followUpResolved === true || data.follow_up_resolved === true;
  const selectedImprovement = normalizeSelectedImprovement(
    data.selectedImprovement !== undefined ? data.selectedImprovement : data.selected_improvement,
  );
  const selectedIndexes = normalizeSelectedIndexes(
    Array.isArray(data.selectedIndexes) ? data.selectedIndexes : data.selected_indexes,
  );
  const selectedImprovementText =
    typeof data.selectedImprovementText === "string" && data.selectedImprovementText.trim()
      ? data.selectedImprovementText.trim().slice(0, 1200)
      : typeof data.selected_improvement_text === "string" && data.selected_improvement_text.trim()
        ? data.selected_improvement_text.trim().slice(0, 1200)
        : typeof data.selectedImprovement === "string" && data.selectedImprovement.trim()
          ? data.selectedImprovement.trim().slice(0, 1200)
          : typeof data.selected_improvement === "string" && data.selected_improvement.trim()
            ? data.selected_improvement.trim().slice(0, 1200)
            : null;
  const actionableProposal =
    data.actionableProposal !== undefined ? data.actionableProposal :
    data.actionable_proposal !== undefined ? data.actionable_proposal :
    null;

  // Step 18: diagnosticTrace – optional diagnostic/trace string from backend
  const diagnosticTrace = typeof data.diagnosticTrace === "string" && data.diagnosticTrace
    ? data.diagnosticTrace
    : typeof data.diagnostic_trace === "string" && data.diagnostic_trace
      ? data.diagnostic_trace
      : null;

  // Step 18: toolResultsSummary – optional short summary of tool results
  const toolResultsSummary = typeof data.toolResultsSummary === "string" && data.toolResultsSummary
    ? data.toolResultsSummary
    : typeof data.tool_results_summary === "string" && data.tool_results_summary
      ? data.tool_results_summary
      : null;

  // Step 18: agentName – echoed back from backend for display in drawer
  const agentName = typeof data.agentName === "string" && data.agentName
    ? data.agentName
    : typeof data.agent_name === "string" && data.agent_name
      ? data.agent_name
      : null;

  // Step 19: Agent System Context Snapshot fields
  // systemContextUsed: true when the agent had access to real system context before answering
  const rawSystemContextUsed =
    data.systemContextUsed !== undefined ? data.systemContextUsed :
    data.system_context_used !== undefined ? data.system_context_used : undefined;
  const systemContextUsed =
    rawSystemContextUsed === true ? true :
    rawSystemContextUsed === false ? false : null;

  // systemContextSummary: short human-readable description of what context was used
  const systemContextSummary =
    typeof data.systemContextSummary === "string" && data.systemContextSummary
      ? data.systemContextSummary.slice(0, 400)
      : typeof data.system_context_summary === "string" && data.system_context_summary
        ? data.system_context_summary.slice(0, 400)
        : null;

  // evidenceRefs: array of { type, name } items – normalised + grouped by category
  const rawEvidenceRefs =
    Array.isArray(data.evidenceRefs) ? data.evidenceRefs :
    Array.isArray(data.evidence_refs) ? data.evidence_refs : null;
  const evidenceRefs = normalizeEvidenceRefs(rawEvidenceRefs);

  // missingContext: array of string labels the agent could NOT access
  const rawMissingContext =
    Array.isArray(data.missingContext) ? data.missingContext :
    Array.isArray(data.missing_context) ? data.missing_context : null;
  const missingContext = Array.isArray(rawMissingContext)
    ? rawMissingContext.map((c) => (typeof c === "string" ? c.trim() : String(c).trim())).filter(Boolean).slice(0, 20)
    : [];

  // v0 Timeout fields: extracted from structured backend timeout responses.
  // timeout: true when the backend explicitly signals a timeout occurred.
  const timeoutFlag =
    data.timeout === true ? true : data.timeout === false ? false : null;
  // contextType: "design_context" | "full_system_context" | null
  const contextType = typeof data.contextType === "string" && data.contextType
    ? data.contextType.trim()
    : typeof data.context_type === "string" && data.context_type
      ? data.context_type.trim()
      : null;
  // reason: short human-readable timeout reason from the backend
  const timeoutReason = typeof data.reason === "string" && data.reason
    ? data.reason.slice(0, 300)
    : null;
  // suggestion: backend suggestion for what the user should do after a timeout
  const timeoutSuggestion = typeof data.suggestion === "string" && data.suggestion
    ? data.suggestion.slice(0, 300)
    : null;

  const designContextUsed = normalizeOptionalBoolean(
    data.designContextUsed !== undefined ? data.designContextUsed : data.design_context_used,
  );
  const screenshotContextUsed = normalizeOptionalBoolean(
    data.screenshotContextUsed !== undefined ? data.screenshotContextUsed : data.screenshot_context_used,
  );
  const screenshotImageForwarded = normalizeOptionalBoolean(
    data.screenshotImageForwarded !== undefined ? data.screenshotImageForwarded : data.screenshot_image_forwarded,
  );
  const screenshotsMissingContent = Array.isArray(data.screenshotsMissingContent)
    ? data.screenshotsMissingContent
    : Array.isArray(data.screenshots_missing_content)
      ? data.screenshots_missing_content
      : [];
  const screenshotProviderCapability = data.screenshotProviderCapability !== undefined
    ? data.screenshotProviderCapability
    : data.screenshot_provider_capability !== undefined
      ? data.screenshot_provider_capability
      : null;
  const visualContextUsedByV0 = normalizeOptionalBoolean(
    data.visualContextUsedByV0 !== undefined ? data.visualContextUsedByV0 : data.visual_context_used_by_v0,
  );
  const visualContextUsed = normalizeOptionalBoolean(
    data.visualContextUsed !== undefined
      ? data.visualContextUsed
      : data.visual_context_used !== undefined
        ? data.visual_context_used
        : visualContextUsedByV0,
  );
  const hasScreenshotDataUrl = normalizeOptionalBoolean(
    data.hasScreenshotDataUrl !== undefined ? data.hasScreenshotDataUrl : data.has_screenshot_data_url,
  );
  const supportsDirectImageInput = normalizeOptionalBoolean(
    data.supportsDirectImageInput !== undefined ? data.supportsDirectImageInput : data.supports_direct_image_input,
  );
  const supportsVisualContextSummary = normalizeOptionalBoolean(
    data.supportsVisualContextSummary !== undefined ? data.supportsVisualContextSummary : data.supports_visual_context_summary,
  );
  const designProposal = (data.designProposal && typeof data.designProposal === "object" && !Array.isArray(data.designProposal))
    ? data.designProposal
    : (data.design_proposal && typeof data.design_proposal === "object" && !Array.isArray(data.design_proposal))
      ? data.design_proposal
      : null;
  const generatedImage = (data.generatedImage && typeof data.generatedImage === "object" && !Array.isArray(data.generatedImage))
    ? data.generatedImage
    : (data.generated_image && typeof data.generated_image === "object" && !Array.isArray(data.generated_image))
      ? data.generated_image
      : null;
  const imageGenerationAvailable = normalizeOptionalBoolean(
    data.imageGenerationAvailable !== undefined ? data.imageGenerationAvailable : data.image_generation_available,
  );
  const imageGenerationError = typeof data.imageGenerationError === "string" && data.imageGenerationError
    ? data.imageGenerationError
    : typeof data.image_generation_error === "string" && data.image_generation_error
      ? data.image_generation_error
      : null;

  const controlled_real_repair_status = data.controlled_real_repair_status ?? data.controlledRealRepairStatus ?? null;
  const latest_controlled_repair_run = data.latest_controlled_repair_run ?? data.latestControlledRepairRun ?? null;
  const latest_pr_url = data.latest_pr_url ?? data.latestPrUrl ?? null;
  const latest_branch = data.latest_branch ?? data.latestBranch ?? null;
  const latest_commit_sha = data.latest_commit_sha ?? data.latestCommitSha ?? null;
  const latest_changed_file = data.latest_changed_file ?? data.latestChangedFile ?? null;
  const latest_work_order_id = data.latest_work_order_id ?? data.latestWorkOrderId ?? null;
  const latest_token_status = data.latest_token_status ?? data.latestTokenStatus ?? null;
  const latest_result_status = data.latest_result_status ?? data.latestResultStatus ?? null;
  const latest_safety_status = data.latest_safety_status ?? data.latestSafetyStatus ?? null;
  const ready_for_dashboard_e2e_test = data.ready_for_dashboard_e2e_test === true || data.readyForDashboardE2eTest === true;
  const next_recommended_action = data.next_recommended_action ?? data.nextRecommendedAction ?? null;

  const hasA3Data = Boolean(
    controlled_real_repair_status ||
    latest_controlled_repair_run ||
    latest_work_order_id
  );

  let enhancedText = text;
  let enhancedDisplayText = displayText;
  if (intent === "controlled_repair_preview") {
    const isLiveHealthNotLoaded = data.blockReason === "live_health_not_loaded" || data.block_reason === "live_health_not_loaded" || data.blockedReason === "live_health_not_loaded" || data.blocked_reason === "live_health_not_loaded" || data.errorCode === "live_health_not_loaded" || data.error_code === "live_health_not_loaded" || errorCode === "live_health_not_loaded";
    if (isLiveHealthNotLoaded) {
      if (hasA3Data) {
        enhancedText = "Live-Health ist nicht geladen, A3-Status wird aus Dashboard-/Result-Daten gelesen. Die Datei ist im kontrollierten A3-Testbereich erlaubt. Ich kann einen sicheren Repair-Vorschlag vorbereiten. Es wird noch nichts ausgeführt.";
      } else {
        enhancedText = "Live-Health ist nicht geladen, und keine A3-Daten sind verfügbar. Die A3.5 Controlled Repair Prüfung fehlt.";
      }
    } else {
      const hasBlockedReason = Boolean(
        data.blockReason || data.block_reason || data.blockedReason || data.blocked_reason ||
        agentFlow.blockReason || agentFlow.blockReasonText
      );
      if (hasBlockedReason) {
        enhancedText = "Der Vorschlag kann noch nicht ausgeführt werden, weil keine freigegebene Aktion vorhanden ist.";
      } else {
        enhancedText = "Die Datei ist im kontrollierten A3-Testbereich erlaubt. Ich kann einen sicheren Repair-Vorschlag vorbereiten. Es wird noch nichts ausgeführt.";
      }
    }
    enhancedDisplayText = enhancedText;
  }

  return {
    text: enhancedText, fullText: enhancedText, displayText: enhancedDisplayText, phase, sessionId, executionReady, claudeChecked, openAiApproved,
    events, statusLine, intent, track, mode, autonomyLevel, trackLabel, awareness,
    workflowActive, workflowType, workflowPhase: workflowPhaseRaw, progressLabel,
    currentStep, workflowSteps,
    agentFlow,
    canonicalRoute: agentFlow.canonicalRoute,
    isCanonical: agentFlow.isCanonical,
    legacyRouteUsed: agentFlow.legacyRouteUsed,
    routeType: agentFlow.routeType,
    flowType: agentFlow.flowType,
    executionPath: agentFlow.executionPath,
    sourceStage: agentFlow.sourceStage,
    sourceRoute: agentFlow.sourceRoute,
    route: agentFlow.route,
    endpoint: agentFlow.endpoint,
    routeLabel: agentFlow.routeLabel,
    blockReason: agentFlow.blockReason,
    blockReasonText: agentFlow.blockReasonText,
    safetyGateStatus: agentFlow.safetyGateStatus,
    safetyGateActive: agentFlow.safetyGateActive,
    requiresApproval: agentFlow.requiresApproval,
    nextRequiredStep: agentFlow.nextRequiredStep,
    nextRequiredStepText: agentFlow.nextRequiredStepText,
    controlled_real_repair_status, latest_controlled_repair_run,
    latest_pr_url, latest_branch, latest_commit_sha,
    latest_changed_file, latest_work_order_id, latest_token_status,
    latest_result_status, latest_safety_status,
    ready_for_dashboard_e2e_test, next_recommended_action,
    // Step 11B additions
    issueCount, routedCount, nextAction, toolResults, detailsSections,
    // Step 13: Pure Engine / Provider Call fields
    providerCall, source, fallbackUsed, errorCode, errorCategory, traceId, retryable,
    provider: providerName,
    // Step 15: Pending Actions
    pendingAction,
    // Step 16: Workflow State & Execution Verification
    workflowState, workflowRunId, workflowMessage,
    executionClaimVerified, realExecutionPerformed, nextUserAction,
    // Follow-up Context UX V1
    followUpResolved, selectedImprovement, selectedIndexes, selectedImprovementText, actionableProposal,
    // Step 18: Full-context display fields
    diagnosticTrace, toolResultsSummary, agentName,
    // Step 19: Agent System Context Snapshot
    systemContextUsed, systemContextSummary, evidenceRefs, missingContext,
    // v0 Timeout structured fields
    timeout: timeoutFlag, contextType, reason: timeoutReason, suggestion: timeoutSuggestion,
    // v0 screenshot / visual / design-image metadata
    designContextUsed,
    screenshotContextUsed,
    screenshotImageForwarded,
    screenshotsMissingContent,
    screenshotProviderCapability,
    visualContextUsed,
    visualContextUsedByV0,
    hasScreenshotDataUrl,
    supportsDirectImageInput,
    supportsVisualContextSummary,
    designProposal,
    generatedImage,
    imageGenerationAvailable,
    imageGenerationError,
  };
}

// ── Step 19: Agent System Context Snapshot ───────────────────────────────────

/**
 * Category keys and their display labels for the evidence refs grouping.
 */
export const EVIDENCE_REF_CATEGORY_LABELS_DE = {
  tables:   "Tabellen",
  jobs:     "Jobs",
  services: "Services / Dateien",
  routes:   "Routes / API",
  frontend: "Frontend / UI",
  other:    "Sonstiges",
};

/**
 * Maps a raw type/kind string from an evidence ref entry to one of the
 * canonical category keys used in EVIDENCE_REF_CATEGORY_LABELS_DE.
 *
 * @param {string} rawType - e.g. "table", "job", "service", "route", "frontend"
 * @returns {"tables"|"jobs"|"services"|"routes"|"frontend"|"other"}
 */
function classifyEvidenceRefType(rawType) {
  const t = typeof rawType === "string" ? rawType.trim().toLowerCase() : "";
  if (t === "table" || t === "tabelle" || t === "db" || t === "database" || t === "model") return "tables";
  if (t === "job" || t === "task" || t === "cron") return "jobs";
  if (t === "service" || t === "file" || t === "datei" || t === "module" || t === "class") return "services";
  if (t === "route" || t === "api" || t === "endpoint" || t === "path") return "routes";
  if (t === "frontend" || t === "ui" || t === "component" || t === "komponente" || t === "view" || t === "page") return "frontend";
  return "other";
}

/**
 * Normalise a raw evidenceRefs array from the backend into a grouped
 * display-ready structure.
 *
 * Input shape (each item may use camelCase or snake_case):
 *   { type/kind, name/label/id, description? }
 *
 * Output:
 *   {
 *     total:    number,
 *     tables:   string[],
 *     jobs:     string[],
 *     services: string[],
 *     routes:   string[],
 *     frontend: string[],
 *     other:    string[],
 *   }
 *
 * Never throws; malformed entries are dropped silently.
 *
 * @param {any} raw - raw evidenceRefs / evidence_refs from the backend response
 * @returns {{ total: number, tables: string[], jobs: string[], services: string[], routes: string[], frontend: string[], other: string[] }}
 */
export function normalizeEvidenceRefs(raw) {
  const empty = { total: 0, tables: [], jobs: [], services: [], routes: [], frontend: [], other: [] };
  const list = Array.isArray(raw) ? raw : null;
  if (!list || list.length === 0) return empty;

  const grouped = { tables: [], jobs: [], services: [], routes: [], frontend: [], other: [] };

  for (const item of list) {
    if (!item || typeof item !== "object") continue;
    const rawType = item.type || item.kind || item.category || "";
    const category = classifyEvidenceRefType(rawType);
    const name = (
      typeof item.name === "string" ? item.name :
      typeof item.label === "string" ? item.label :
      typeof item.id === "string" ? item.id :
      typeof item.ref === "string" ? item.ref :
      typeof item.description === "string" ? item.description : ""
    ).trim();
    if (!name) continue;
    // Deduplicate within category
    if (!grouped[category].includes(name)) {
      grouped[category].push(name);
    }
  }

  const total = Object.values(grouped).reduce((sum, arr) => sum + arr.length, 0);
  return { total, ...grouped };
}

/**
 * Build the short discreet hint text shown at the bottom of an agent chat bubble
 * when system context was used.
 *
 * Examples:
 *   "Systemkontext genutzt · 8 Hinweise"
 *   "Systemkontext teilweise · 3 Quellen fehlen"
 *   "Systemkontext nicht geliefert"
 *   null  (when no context data present at all)
 *
 * @param {boolean|null} systemContextUsed
 * @param {{ total: number }} evidenceRefs  - normalizeEvidenceRefs result
 * @param {string[]} missingContext
 * @returns {string|null}
 */
export function buildSystemContextHint(systemContextUsed, evidenceRefs, missingContext) {
  const total = evidenceRefs?.total ?? 0;
  const missingCount = Array.isArray(missingContext) ? missingContext.length : 0;

  // systemContextUsed=false (explicit backend signal) or no refs and not used → not delivered
  if (systemContextUsed === false && total === 0) {
    return "Systemkontext nicht geliefert";
  }

  if (systemContextUsed !== true && total === 0 && missingCount === 0) {
    return null;
  }

  if (missingCount > 0) {
    const hint = total > 0
      ? `Systemkontext teilweise · ${missingCount} ${missingCount === 1 ? "Quelle fehlt" : "Quellen fehlen"}`
      : `Systemkontext nicht vollständig · ${missingCount} ${missingCount === 1 ? "Quelle fehlt" : "Quellen fehlen"}`;
    return hint;
  }

  if (total > 0) {
    return `Systemkontext genutzt · ${total} ${total === 1 ? "Hinweis" : "Hinweise"}`;
  }

  if (systemContextUsed === true) {
    return "Systemkontext genutzt";
  }

  return null;
}

// ── Step 13: Pure Engine / Pure KI ───────────────────────────────────────────

/**
 * Error bubble text shown when the backend did not complete a real provider call.
 * Used by buildProviderErrorBubble and the UI layer.
 */
export const PROVIDER_ERROR_BUBBLE_TEXT =
  "Der Agent ist konfiguriert, aber der Modellaufruf ist gerade fehlgeschlagen.";

const KNOWN_NON_PROVIDER_CHAT_TEXTS = [
  "Ich starte jetzt die Systemprüfung und untersuche Datenfluss, Tabellen, Jobs und Routen.",
  "Ich starte jetzt die Systemprüfung und untersuche Datenfluss …",
  AGENT_SELF_DISCOVERY_TEXT,
  "Hey, ich bin da!",
  "Hey, ich bin da! Du kannst mir normale Fragen stellen.",
  "Hey, ich bin da! Du kannst mir normale Fragen stellen ...",
  "Du kannst mir normale Fragen stellen.",
  "Du kannst mir normale Fragen stellen ...",
  // Meta-role texts / self-description phrases that must never appear as real
  // provider answers. Checked via prefix match so minor continuations are caught.
  "Als interner HQS-Agent",
  "Mein Modus ist",
  "Ich habe Zugriff auf",
  "Ich ermittle die betroffenen",
  ...Object.values(AGENT_LIVE_PROGRESS_AGENT_TEXTS_DE),
];

function normalizeBlockedBubbleText(text) {
  if (typeof text !== "string") return "";
  return text
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/…/g, " ")
    .replace(/\.\.\./g, " ")
    .replace(/[!?.,:;·]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isKnownNonProviderChatText(text) {
  const normalized = normalizeBlockedBubbleText(text);
  if (!normalized) return false;
  return KNOWN_NON_PROVIDER_CHAT_TEXTS.some((candidate) => {
    const normalizedCandidate = normalizeBlockedBubbleText(candidate);
    return normalized === normalizedCandidate || normalized.startsWith(normalizedCandidate);
  });
}

/**
 * Returns true when the chat response represents a genuine LLM provider answer
 * that is safe to display as a normal agent bubble.
 *
 * Positive confirmation (all four required):
 *   - providerCall === true   – backend actually called the LLM
 *   - source === "provider"   – answer comes from the LLM provider
 *   - fallbackUsed === false  – no local fallback was used
 *   - text is present and not one of the known local workflow/fallback texts
 *
 * Failure cases (any one is sufficient to return false):
 *   - source === "error"      – backend reported an error
 *   - providerCall === false  – no LLM call was made
 *   - fallbackUsed === true   – a local/static fallback was used
 *
 * executionReady is NEVER derived from this function.
 *
 * @param {object|null} chatResp – result of normalizeAgentWorkChatResponse
 * @returns {boolean}
 */
export function isTrueProviderResponse(chatResp) {
  if (!chatResp || typeof chatResp !== "object") return false;
  const text = typeof chatResp.text === "string" ? chatResp.text.trim() : "";
  if (!text || isKnownNonProviderChatText(text)) return false;
  if (chatResp.source === "error" || chatResp.source === "fallback") return false;
  if (chatResp.providerCall === false) return false;
  if (chatResp.fallbackUsed === true) return false;
  return chatResp.providerCall === true && chatResp.source === "provider";
}

/**
 * Build an honest German error chat bubble for cases where no real provider
 * answer was returned (fallback, error, or missing provider call).
 *
 * Rules:
 *   - Never shows raw JSON or field names
 *   - Max ~120 chars
 *   - No secrets
 *
 * @param {object|null} chatResp – result of normalizeAgentWorkChatResponse
 * @returns {string}
 */
export function buildProviderErrorBubble(chatResp) {
  if (!chatResp || typeof chatResp !== "object") {
    return PROVIDER_ERROR_BUBBLE_TEXT;
  }
  const text = typeof chatResp.text === "string" ? sanitizeAgentChatText(chatResp.text) : "";
  const isHonestErrorText = text
    && !isKnownNonProviderChatText(text)
    && (chatResp.source === "error" || chatResp.errorCode || chatResp.providerCall === false);
  if (isHonestErrorText) {
    return text;
  }
  if (chatResp.source === "error" || chatResp.providerCall === false || chatResp.fallbackUsed === true) {
    return PROVIDER_ERROR_BUBBLE_TEXT;
  }
  return PROVIDER_ERROR_BUBBLE_TEXT;
}

// ── Step 8: Notification & History Layer ──────────────────────────────────────

/**
 * Normalise the raw response from GET .../notifications into a stable,
 * display-safe summary shape for the sentinel bar notification line.
 *
 * Returns:
 *   {
 *     available:    boolean  – false when endpoint returned nothing useful
 *     openCount:    number   – total open issues
 *     criticalCount: number  – critical/high-risk issues
 *     waitingCount: number   – waiting for approval
 *     readyCount:   number   – ready_for_execution
 *   }
 *
 * executionReady is NEVER derived here. The caller must not use this to
 * activate any execution button.
 *
 * @param {any} data - raw API response body (may be null/undefined)
 */
export function normalizeNotificationSummary(data) {
  const empty = { available: false, openCount: 0, criticalCount: 0, waitingCount: 0, readyCount: 0 };
  if (!data || typeof data !== "object") return empty;
  if (data.unavailable) return empty;

  // openCount: accepts both direct count fields and an issues array length.
  const rawOpen = data.openCount ?? data.open_count ?? data.open ?? data.total ??
    (Array.isArray(data.issues) ? data.issues.length : null);
  const openCount = typeof rawOpen === "number" && rawOpen >= 0 ? Math.floor(rawOpen) : 0;

  const rawCritical = data.criticalCount ?? data.critical_count ?? data.critical ?? 0;
  const criticalCount = typeof rawCritical === "number" && rawCritical >= 0 ? Math.floor(rawCritical) : 0;

  const rawWaiting = data.waitingCount ?? data.waiting_count ?? data.waiting ?? 0;
  const waitingCount = typeof rawWaiting === "number" && rawWaiting >= 0 ? Math.floor(rawWaiting) : 0;

  // ready_for_execution count – ONLY from backend; never inferred
  const rawReady = data.readyCount ?? data.ready_count ?? data.ready_for_execution ?? 0;
  const readyCount = typeof rawReady === "number" && rawReady >= 0 ? Math.floor(rawReady) : 0;

  return { available: true, openCount, criticalCount, waitingCount, readyCount };
}

/**
 * Build the compact single-line notification summary text for the sentinel bar.
 *
 * Examples:
 *   "Wächter: 2 offene Punkte · 1 kritisch"
 *   "Wächter: keine offenen Punkte"
 *   "Wächter: Status nicht verfügbar"
 *   "Wächter: 1 offener Punkt · bereit zur Ausführung"
 *
 * Rules:
 *   - Max ~80 chars (mobile-first)
 *   - No raw JSON, no tables, no animation
 *   - executionReady is NEVER set or inferred here
 *
 * @param {object} summary – from normalizeNotificationSummary
 * @returns {string}
 */
export function buildNotificationSummaryLine(summary) {
  if (!summary || typeof summary !== "object") return "Wächter: Status nicht verfügbar";
  if (!summary.available) return "Wächter: Status nicht verfügbar";

  const { openCount, criticalCount, readyCount } = summary;

  if (openCount === 0 && readyCount === 0) return "Wächter: keine offenen Punkte";

  const countText = openCount === 1 ? "1 offener Punkt" : `${openCount} offene Punkte`;
  let line = `Wächter: ${countText}`;

  if (criticalCount > 0) {
    line += ` · ${criticalCount === 1 ? "1 kritisch" : `${criticalCount} kritisch`}`;
  }

  if (readyCount > 0) {
    line += readyCount === 1 ? " · bereit zur Ausführung" : ` · ${readyCount} bereit zur Ausführung`;
  }

  return line.length <= 80 ? line : line.slice(0, 78) + "…";
}

/**
 * Build a short German chat bubble when the notification summary shows a
 * meaningful status change (new critical items or new ready-for-execution).
 *
 * Returns null when:
 *   - Nothing changed (dedup guard – no bubble on every poll tick)
 *   - Only non-critical change (avoids notification spam)
 *   - Data unavailable
 *
 * Rules:
 *   - Max one bubble per status change
 *   - executionReady is NEVER set or inferred here
 *   - Execute button must NEVER be activated from this output
 *
 * @param {object|null} prevSummary – previous normalizeNotificationSummary result
 * @param {object}      nextSummary – current normalizeNotificationSummary result
 * @returns {string|null}
 */
export function buildNotificationChatBubble(prevSummary, nextSummary) {
  if (!nextSummary || !nextSummary.available) return null;

  const prev = prevSummary || { available: false, openCount: 0, criticalCount: 0, waitingCount: 0, readyCount: 0 };

  // New ready_for_execution items appeared → show once
  if (nextSummary.readyCount > 0 && nextSummary.readyCount > (prev.readyCount || 0)) {
    return "Eine freigegebene Änderung ist bereit zur Ausführung.";
  }

  // New critical items appeared
  if (nextSummary.criticalCount > 0 && nextSummary.criticalCount > (prev.criticalCount || 0)) {
    const n = nextSummary.criticalCount;
    return n === 1
      ? "Ich habe 1 kritischen Punkt erkannt. Details findest du im Drawer."
      : `Ich habe ${n} kritische Punkte erkannt. Details findest du im Drawer.`;
  }

  // No more open issues (all resolved)
  if (nextSummary.openCount === 0 && (prev.openCount || 0) > 0) {
    return "Aktuell sind keine offenen Sentinel-Punkte vorhanden.";
  }

  return null;
}

/**
 * Short German labels for Sentinel issue history event types.
 * Used by normalizeIssueHistory() and the timeline renderer.
 */
export const SENTINEL_HISTORY_EVENT_LABELS_DE = {
  created:          "Erkannt",
  updated:          "Aktualisiert",
  dismissed:        "Ausgeblendet",
  repair_started:   "Reparatur gestartet",
  repair_prepared:  "Reparatur vorbereitet",
  claude_reviewed:  "Claude hat geprüft",
  openai_decided:   "OpenAI hat entschieden",
  safety_passed:    "SafetyGate grün",
  safety_blocked:   "SafetyGate blockiert",
  approved:         "Freigegeben",
  execution_ready:  "Bereit zur Ausführung",
  executed:         "Ausgeführt",
  failed:           "Fehlgeschlagen",
};

// ── Step 9: Sentinel Escalation & Manual Override ─────────────────────────────

/**
 * German labels for escalation level values.
 * Used by normalizeEscalationSummary and the escalation drawer.
 */
export const SENTINEL_ESCALATION_LEVEL_LABELS_DE = {
  none:          "Keine Eskalation",
  watch:         "Beobachten",
  elevated:      "Erhöht",
  critical:      "Kritisch",
  paused:        "Pausiert",
  manual_review: "Manuelle Prüfung",
};

/**
 * Sanitize a raw escalation level string to a known key (or "none").
 */
function normalizeEscalationLevel(raw) {
  if (typeof raw !== "string") return "none";
  const key = raw.trim().toLowerCase().replace(/[\s-]/g, "_");
  if (Object.prototype.hasOwnProperty.call(SENTINEL_ESCALATION_LEVEL_LABELS_DE, key)) return key;
  return "none";
}

/**
 * Normalise the raw response from GET /api/admin/agent-sentinel/escalations.
 *
 * Returns:
 *   {
 *     available:         boolean
 *     issues:            Array<{
 *       id:                string|null
 *       title:             string|null
 *       area:              string|null
 *       risk:              string|null
 *       escalationLevel:   string        – canonical key ("none"|"watch"|…)
 *       escalationLevelLabel: string     – German label
 *       reason:            string|null
 *       recommendedAgent:  string|null
 *       nextStep:          string|null
 *     }>
 *     criticalCount:     number
 *     pausedCount:       number
 *     manualReviewCount: number
 *     totalCount:        number
 *   }
 *
 * Rules:
 *   - No raw JSON, no secrets, no executionReady derivation
 *   - Unknown/null input → available: false, empty issues
 *   - Max 20 items (mobile-friendly)
 */
export function normalizeEscalationSummary(data) {
  if (!data || typeof data !== "object") {
    return { available: false, issues: [], criticalCount: 0, pausedCount: 0, manualReviewCount: 0, totalCount: 0 };
  }

  const rawList =
    Array.isArray(data.escalations) ? data.escalations :
    Array.isArray(data.issues)      ? data.issues :
    Array.isArray(data.data?.escalations) ? data.data.escalations :
    Array.isArray(data)             ? data :
    [];

  const issues = rawList.slice(0, 20).map((item) => {
    if (!item || typeof item !== "object") return null;
    const id   = sanitizeShortText(item.id   || item.issueId   || item.issue_id   || null, 64);
    const title = sanitizeShortText(item.title || item.name || item.summary || null, 80);
    const area  = sanitizeShortText(item.area  || item.component || item.scope || null, 60);
    const risk  = sanitizeShortText(item.risk  || item.severity || null, 20);
    const escalationLevel = normalizeEscalationLevel(
      item.escalationLevel ?? item.escalation_level ?? item.level ?? "none",
    );
    const escalationLevelLabel = SENTINEL_ESCALATION_LEVEL_LABELS_DE[escalationLevel] || "Keine Eskalation";
    const reason          = sanitizeShortText(item.reason || item.description || null, 120);
    const recommendedAgent = sanitizeShortText(item.recommendedAgent || item.recommended_agent || null, 30);
    const nextStep         = sanitizeShortText(item.nextStep || item.next_step || null, 100);
    return { id, title, area, risk, escalationLevel, escalationLevelLabel, reason, recommendedAgent, nextStep };
  }).filter(Boolean);

  const criticalCount     = issues.filter((i) => i.escalationLevel === "critical").length;
  const pausedCount       = issues.filter((i) => i.escalationLevel === "paused").length;
  const manualReviewCount = issues.filter((i) => i.escalationLevel === "manual_review").length;
  const totalCount        = issues.length;

  return { available: true, issues, criticalCount, pausedCount, manualReviewCount, totalCount };
}

/**
 * Build the compact single-line escalation summary text for the sentinel bar.
 *
 * Examples:
 *   "Wächter: 1 kritische Eskalation · 2 pausiert"
 *   "Wächter: keine aktiven Eskalationen"
 *
 * Returns null when summary is unavailable (so caller can fall through to next priority).
 *
 * Rules:
 *   - Max ~80 chars
 *   - No raw JSON, no tables
 *   - executionReady is NEVER set or inferred here
 *   - Execute button must NEVER be activated from this output
 *
 * @param {object} summary – from normalizeEscalationSummary
 * @returns {string|null}
 */
export function buildEscalationSummaryLine(summary) {
  if (!summary || typeof summary !== "object" || !summary.available) return null;

  const { criticalCount, pausedCount, manualReviewCount, totalCount } = summary;

  // No active escalations
  if (criticalCount === 0 && pausedCount === 0 && manualReviewCount === 0 && totalCount === 0) {
    return null; // nothing to show; sentinel bar keeps its regular line
  }

  const parts = [];
  if (criticalCount > 0) {
    parts.push(criticalCount === 1 ? "1 kritische Eskalation" : `${criticalCount} kritische Eskalationen`);
  }
  if (pausedCount > 0) {
    parts.push(pausedCount === 1 ? "1 pausiert" : `${pausedCount} pausiert`);
  }
  if (manualReviewCount > 0) {
    parts.push(manualReviewCount === 1 ? "1 manuelle Prüfung" : `${manualReviewCount} manuelle Prüfungen`);
  }
  // Remaining active (watch/elevated) that weren't counted above
  const otherCount = totalCount - criticalCount - pausedCount - manualReviewCount;
  if (otherCount > 0 && parts.length === 0) {
    parts.push(otherCount === 1 ? "1 aktive Eskalation" : `${otherCount} aktive Eskalationen`);
  }

  if (parts.length === 0) return null;

  const line = `Wächter: ${parts.join(" · ")}`;
  return line.length <= 80 ? line : line.slice(0, 78) + "…";
}

/**
 * Build a short German chat bubble confirming a manual override action.
 *
 * @param {"pause"|"resume"|"manual_review"|"clear"|"escalate"} action
 * @returns {string}
 */
export function buildEscalationActionChatBubble(action) {
  switch (action) {
    case "pause":         return "Ich habe die Eskalation pausiert.";
    case "resume":        return "Die Eskalation wurde fortgesetzt.";
    case "manual_review": return "Die manuelle Prüfung wurde angefordert.";
    case "clear":         return "Die Eskalation wurde zurückgesetzt.";
    case "escalate":      return "Die Eskalation wurde hochgestuft.";
    default:              return "Die Aktion wurde ausgeführt.";
  }
}

// ── Agent Autonomy Policy (Step 10) ──────────────────────────────────────────

/**
 * Normalise the raw response from GET /api/admin/agents/policies/:agentName
 * (or GET /api/admin/agents/policies) into a compact, display-safe policy
 * object that the UI can consume safely.
 *
 * Shape:
 *   {
 *     available:          bool       – false when no data / API error
 *     blocked:            bool       – policy says this action is blocked
 *     allowed:            bool       – policy explicitly allows the action
 *     requiresApproval:   bool       – needs full approval chain before execution
 *     nextAction:         string|null – e.g. "prepare_patch"
 *     allowedActions:     string[]   – max 20
 *     approvalActions:    string[]   – actions that need approval
 *     blockedActions:     string[]   – blocked action keys
 *     claudeRequired:     bool
 *     openAiRequired:     bool
 *     safetyGateRequired: bool
 *     agentRole:          string|null
 *     agentName:          string|null
 *   }
 *
 * Rules:
 *   - No raw JSON
 *   - executionReady is NEVER derived from policy data
 *   - Max 20 items in action arrays
 */
export function normalizeAgentPolicy(data) {
  const empty = {
    available: false, blocked: false, allowed: false,
    requiresApproval: false, nextAction: null,
    allowedActions: [], approvalActions: [], blockedActions: [],
    claudeRequired: false, openAiRequired: false, safetyGateRequired: false,
    agentRole: null, agentName: null,
  };
  if (!data || typeof data !== "object") return empty;

  // Support both top-level and data.policy / data.data wrapper
  const raw = data.policy || data.data || data;
  if (!raw || typeof raw !== "object") return empty;

  const blocked          = Boolean(raw.blocked === true || raw.status === "blocked");
  const allowed          = Boolean(raw.allowed === true || raw.status === "allowed");
  const requiresApproval = Boolean(raw.requiresApproval === true || raw.requires_approval === true);

  const rawNextAction = raw.nextAction || raw.next_action || null;
  const nextAction = typeof rawNextAction === "string"
    ? sanitizeShortText(rawNextAction, 40) : null;

  const toStringArray = (val) => {
    if (!Array.isArray(val)) return [];
    return val.filter((v) => typeof v === "string").map((v) => v.trim()).filter(Boolean).slice(0, 20);
  };
  const allowedActions  = toStringArray(raw.allowedActions  || raw.allowed_actions);
  const approvalActions = toStringArray(raw.approvalActions || raw.approval_actions);
  const blockedActions  = toStringArray(raw.blockedActions  || raw.blocked_actions);

  const claudeRequired     = Boolean(raw.claudeRequired     === true || raw.claude_required     === true);
  const openAiRequired     = Boolean(raw.openAiRequired     === true || raw.openai_required     === true);
  const safetyGateRequired = Boolean(raw.safetyGateRequired === true || raw.safety_gate_required === true);

  const rawRole = raw.agentRole || raw.agent_role || raw.role || null;
  const agentRole = typeof rawRole === "string" ? sanitizeShortText(rawRole, 60) : null;
  const rawName = raw.agentName || raw.agent_name || raw.agent || null;
  const agentName = typeof rawName === "string" ? sanitizeShortText(rawName, 40) : null;

  return {
    available: true,
    blocked,
    allowed,
    requiresApproval,
    nextAction,
    allowedActions,
    approvalActions,
    blockedActions,
    claudeRequired,
    openAiRequired,
    safetyGateRequired,
    agentRole,
    agentName,
  };
}

/**
 * Build a short German policy status sub-line for the sentinel bar.
 * Returned as a plain string (never JSX).
 *
 * Examples:
 *   "Policy: Qwen darf Patch vorbereiten, Ausführung nur nach Freigabe"
 *   "Policy: Diese Aktion ist blockiert – ENV/Secrets/Deploy sind geschützt."
 *   null  (when no policy data)
 *
 * @param {object} policy    – from normalizeAgentPolicy
 * @param {string} [agentName]
 * @returns {string|null}
 */
export function buildPolicyStatusLine(policy, agentName) {
  if (!policy?.available) return null;
  const name = (typeof agentName === "string" && agentName.trim())
    ? agentName.trim()
    : (policy.agentName || "Agent");

  if (policy.blocked) {
    return "Policy: Diese Aktion ist blockiert – ENV/Secrets/Deploy sind geschützt.";
  }
  if (policy.requiresApproval) {
    return `Policy: ${name} darf Patch vorbereiten, Ausführung nur nach Freigabe`;
  }
  if (policy.allowed && policy.nextAction) {
    return `Policy: ${name} · ${policy.nextAction}`;
  }
  if (policy.allowed) {
    return `Policy: ${name} aktiv`;
  }
  return null;
}

/**
 * Build a friendly German chat bubble explaining why an action is blocked.
 *
 * @param {object} policy    – from normalizeAgentPolicy
 * @param {string} [agentName]
 * @returns {string}
 */
export function buildPolicyBlockedChatBubble(policy, agentName) {
  if (!policy?.available) {
    return "Die Freigaberegeln konnten gerade nicht geprüft werden.";
  }
  const name = (typeof agentName === "string" && agentName.trim())
    ? agentName.trim()
    : (policy.agentName || "Agent");

  if (policy.blocked) {
    const approvers = [];
    if (policy.claudeRequired)     approvers.push("Claude");
    if (policy.openAiRequired)     approvers.push("OpenAI");
    if (policy.safetyGateRequired) approvers.push("SafetyGate");
    if (approvers.length > 0) {
      return `Diese Aktion ist geschützt. ${name} darf den Patch vorbereiten, aber erst nach ${approvers.join("-, ")}-Freigabe ausführen.`;
    }
    return `Diese Aktion ist nach den Sicherheitsregeln blockiert.`;
  }
  return "Die Freigaberegeln wurden geprüft.";
}

/**
 * Normalise the raw response from GET /api/admin/agents/policies into a
 * map of agentName → normalizedPolicy, for displaying an overview.
 *
 * @param {any} data - raw API response body
 * @returns {Record<string, object>}  map of agentName → normalizeAgentPolicy result
 */
export function normalizeAgentPolicies(data) {
  if (!data || typeof data !== "object") return {};

  // Support list: [{ agentName, … }, …] or map { agentName: { … }, … }
  if (Array.isArray(data.policies)) {
    const out = {};
    for (const item of data.policies) {
      if (!item || typeof item !== "object") continue;
      const name = (item.agentName || item.agent_name || item.agent || "").toLowerCase().trim();
      if (name) out[name] = normalizeAgentPolicy(item);
    }
    return out;
  }
  if (Array.isArray(data)) {
    const out = {};
    for (const item of data) {
      if (!item || typeof item !== "object") continue;
      const name = (item.agentName || item.agent_name || item.agent || "").toLowerCase().trim();
      if (name) out[name] = normalizeAgentPolicy(item);
    }
    return out;
  }
  // Object map
  const out = {};
  for (const [key, val] of Object.entries(data)) {
    if (val && typeof val === "object") out[key] = normalizeAgentPolicy(val);
  }
  return out;
}

// ── Step 11: Agent Live-Health Check ─────────────────────────────────────────

/**
 * German labels for the per-agent live-health status codes returned by
 * POST /api/admin/agents/live-health/run.
 *
 * Rules (spec):
 *   - live=true                   → "Live"
 *   - status="missing_api_key"    → "Nicht konfiguriert"
 *   - status="provider_error"     → "Provider-Fehler"
 *   - status="model_or_endpoint_error" → "Modell-/Endpunkt-Fehler"
 *   - status="auth_error"         → "Auth-Fehler"
 *   - status="timeout"            → "Timeout"
 *   - status="rate_limit"         → "Rate Limit"
 *   - live=false (other/unknown)  → "Fehler"
 *
 * IMPORTANT: "nicht konfiguriert" MUST only appear when the backend truly
 * returns status="missing_api_key". Any other failure maps to a specific
 * error label, never to "nicht konfiguriert".
 */
export const AGENT_LIVE_HEALTH_STATUS_LABELS_DE = {
  live:                    "Live",
  missing_api_key:         "Nicht konfiguriert",
  provider_error:          "Provider-Fehler",
  model_or_endpoint_error: "Modell-/Endpunkt-Fehler",
  auth_error:              "Auth-Fehler",
  timeout:                 "Timeout",
  rate_limit:              "Rate Limit",
  error:                   "Fehler",
};

/**
 * Build the short chip label for a single agent's live-health result.
 *
 * @param {{ live?: boolean, status?: string }} result – normalised per-agent result
 * @returns {string}
 */
export function buildLiveHealthChipLabel(result) {
  if (!result || typeof result !== "object") return AGENT_LIVE_HEALTH_STATUS_LABELS_DE.error;
  if (result.live === true) return AGENT_LIVE_HEALTH_STATUS_LABELS_DE.live;
  const s = typeof result.status === "string" ? result.status.trim().toLowerCase() : "";
  return AGENT_LIVE_HEALTH_STATUS_LABELS_DE[s] || AGENT_LIVE_HEALTH_STATUS_LABELS_DE.error;
}

/**
 * Normalise a single agent's raw live-health result object.
 *
 * Returns a safe, display-ready shape with no secrets:
 *   {
 *     agentId:    string         – lowercase agent id
 *     provider:   string|null
 *     model:      string|null
 *     configured: boolean
 *     live:       boolean
 *     status:     string          – raw status key from backend
 *     latencyMs:  number|null
 *     errorReason: string|null    – short human-readable error cause (max 120 chars, no JSON)
 *     checkedAt:  string|null     – ISO timestamp of last check
 *   }
 *
 * Rules: no api keys, no raw JSON blocks, no secrets.
 *
 * @param {string} agentId  – lowercase agent id ("deepseek", "claude", …)
 * @param {any}    raw      – raw per-agent object from the backend
 */
export function normalizeAgentLiveHealthResult(agentId, raw) {
  if (!raw || typeof raw !== "object") {
    return {
      agentId: String(agentId || "").toLowerCase(),
      provider: null, model: null, configured: false, live: false,
      status: "error", latencyMs: null, errorReason: null, checkedAt: null,
    };
  }

  const live = raw.live === true;
  const configured = raw.configured === true || raw.connected === true || raw.available === true;

  // Status: prefer explicit backend status; fall back based on live flag
  let status = typeof raw.status === "string" ? raw.status.trim().toLowerCase() : "";
  if (!status) status = live ? "live" : "error";

  // latencyMs: accept latencyMs / latency_ms / durationMs – only if a finite number
  const rawMs = raw.latencyMs ?? raw.latency_ms ?? raw.durationMs ?? null;
  const latencyMs = typeof rawMs === "number" && isFinite(rawMs) ? rawMs : null;

  // errorReason: short human-readable text, no JSON blobs, max 120 chars
  const rawReason = raw.errorReason || raw.error_reason || raw.error || raw.message || null;
  let errorReason = null;
  if (typeof rawReason === "string") {
    const trimmed = rawReason.trim();
    // Reject JSON-like blobs
    if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
      errorReason = trimmed.slice(0, 120) || null;
    }
  }

  // checkedAt: ISO timestamp
  const rawTs = raw.checkedAt || raw.checked_at || raw.timestamp || raw.ts || null;
  const checkedAt = typeof rawTs === "string" ? rawTs : null;

  // provider / model: safe string fields (strip any token-looking values)
  const provider = typeof raw.provider === "string" ? raw.provider.slice(0, 60) : null;
  const model    = typeof raw.model === "string"    ? raw.model.slice(0, 80)    : null;

  return {
    agentId: String(agentId || "").toLowerCase(),
    provider,
    model,
    configured,
    live,
    status,
    latencyMs,
    errorReason: live ? null : errorReason,   // no error reason when live
    checkedAt,
  };
}

/**
 * Normalise the full response from POST /api/admin/agents/live-health/run.
 *
 * Returns:
 *   {
 *     available: boolean                      – false on network/server error
 *     checkedAt: string|null                  – ISO timestamp of the overall check
 *     results:   Record<agentId, normalised>  – per-agent normalised results
 *   }
 *
 * @param {any} data – raw API response body
 */
export function normalizeAgentLiveHealth(data) {
  if (!data || typeof data !== "object") {
    return { available: false, checkedAt: null, results: {} };
  }

  const rawResults =
    data.results && typeof data.results === "object" ? data.results :
    data.agents  && typeof data.agents  === "object" ? data.agents  :
    data.data    && typeof data.data    === "object" && !Array.isArray(data.data)
      ? data.data : null;

  if (!rawResults) {
    return { available: false, checkedAt: null, results: {} };
  }

  const checkedAt = typeof data.checkedAt === "string" ? data.checkedAt :
    typeof data.checked_at === "string" ? data.checked_at :
    typeof data.timestamp  === "string" ? data.timestamp  : null;

  const results = {};
  for (const [key, val] of Object.entries(rawResults)) {
    const agentId = key.trim().toLowerCase();
    results[agentId] = normalizeAgentLiveHealthResult(agentId, val);
  }

  return { available: true, checkedAt, results };
}

/**
 * Normalise the raw response from GET .../issues/:id/history into a compact,
 * display-safe timeline array.
 *
 * Each item:
 *   {
 *     ts:      string|null  – ISO timestamp
 *     labelDe: string       – German event label (never raw type key)
 *     agent:   string|null  – agent name (max 20 chars)
 *     message: string|null  – short message (max 100 chars, no raw JSON)
 *   }
 *
 * Rules:
 *   - Max 20 items
 *   - No raw JSON, no full dumps
 *   - No secrets
 *   - executionReady is NEVER derived from history
 *
 * @param {any} data – raw API response body
 */
export function normalizeIssueHistory(data) {
  if (!data || typeof data !== "object") return [];
  const rawList =
    Array.isArray(data.history)      ? data.history :
    Array.isArray(data.events)        ? data.events :
    Array.isArray(data.data?.history) ? data.data.history :
    Array.isArray(data)               ? data :
    [];

  return rawList.slice(0, 20).map((item) => {
    if (!item || typeof item !== "object") return null;

    const ts = typeof item.ts === "string" ? item.ts :
      typeof item.timestamp === "string" ? item.timestamp :
      typeof item.created_at === "string" ? item.created_at :
      typeof item.updatedAt === "string" ? item.updatedAt : null;

    const rawType = typeof item.type === "string" ? item.type.trim().toLowerCase() :
      typeof item.event === "string" ? item.event.trim().toLowerCase() :
      typeof item.eventType === "string" ? item.eventType.trim().toLowerCase() : "";

    const labelDe = SENTINEL_HISTORY_EVENT_LABELS_DE[rawType] || sanitizeShortText(rawType, 40) || "Ereignis";

    const rawAgent = item.agent || item.agentName || item.agent_name || null;
    const agent = sanitizeShortText(rawAgent, 20);

    const rawMessage = item.message || item.description || item.summary || item.reason || null;
    const rawStr = typeof rawMessage === "string" ? rawMessage : null;
    // Strip any JSON-like content for safety
    const message = rawStr && !rawStr.trim().startsWith("{") && !rawStr.trim().startsWith("[")
      ? rawStr.slice(0, 100)
      : null;

    return { ts, labelDe, agent, message };
  }).filter(Boolean);
}

// ── Step 11B: Workflow Progress, Sanitize, Error Bubbles ─────────────────────

/**
 * Map workflowType / intent to a short German prefix for the progress line.
 */
const WORKFLOW_PROGRESS_PREFIX_DE = {
  diagnosis_request:       "Analyse läuft",
  repair_planning_request: "Vorschlag wird vorbereitet",
  execution_request:       "Ausführung läuft",
  system_question:         "Systemfrage wird geprüft",
  general_chat:            "Prüfung läuft",
};

/**
 * Build the calm single-line progress text shown under the last agent message
 * when the backend signals `workflowActive=true`.
 *
 * Spec examples:
 *   "Prüfung läuft · Datenfluss wird geprüft"
 *   "Analyse läuft · Komponenten werden gesucht"
 *   "Agentenstatus wird geprüft · 3 von 6 abgeschlossen"
 *   "Ergebnis bereit · Details im Drawer"
 *
 * Rules:
 *   - Returns null when workflowActive is not true (caller shows nothing).
 *   - Always ≤ 80 chars (mobile-first).
 *   - No raw JSON, no secrets.
 *
 * @param {object} response – result of normalizeAgentWorkChatResponse
 * @returns {string|null}
 */
export function buildWorkflowProgressLine(response) {
  if (!response || response.workflowActive !== true) return null;

  const type  = response.workflowType || response.intent || "general_chat";
  const prefix = WORKFLOW_PROGRESS_PREFIX_DE[type] || "Prüfung läuft";

  // progressLabel or currentStep provide the detail
  const detail = response.progressLabel || response.currentStep || null;
  if (!detail) return prefix;

  const line = `${prefix} · ${detail}`;
  return line.length <= 80 ? line : line.slice(0, 78) + "…";
}

/**
 * Strip raw Markdown formatting from agent reply text so it renders cleanly
 * in the main chat bubble without "###", "**" and similar visual noise.
 *
 * Specifically:
 *   - `### Heading` → "Heading"
 *   - `## Heading`  → "Heading"
 *   - `# Heading`   → "Heading"
 *   - `**bold**`    → "bold"
 *   - `*italic*`    → "italic"
 *   - `__underline__` → "underline"
 *   - `---` / `===` separator lines → ""
 *   - `` `code` ``  → "code"
 *   - Leading/trailing whitespace trimmed
 *   - Cap at MAX_CHAT_TEXT_LENGTH chars; longer text is truncated with "…"
 *
 * Rules:
 *   - Non-string input → returns empty string
 *   - Always a pure, framework-free string operation
 *   - Does NOT modify German umlauts or special characters
 *
 * @param {string} text - raw text that may contain Markdown
 * @param {number} [maxLength=400] - max output length (including ellipsis)
 * @returns {string}
 */
export const MAX_CHAT_TEXT_LENGTH = 400;

/**
 * Build a compact preview for the chat bubble while preserving the full text.
 *
 * The chat bubble should show a short, readable preview, but the full
 * backend response must NEVER be overwritten. This function returns both:
 *   - previewText: sanitised Markdown + truncated to MAX_CHAT_TEXT_LENGTH
 *   - fullText:    the original trimmed text (no truncation)
 *   - isTruncated: true when the preview is shorter than the full text
 *
 * Rules:
 *   - fullText is always the trimmed but otherwise unmodified input.
 *   - previewText is produced by sanitizeAgentChatText (strips Markdown, caps length).
 *   - isTruncated is true ONLY when the preview ends with "…" due to length capping.
 *     Markdown-stripping alone does not set isTruncated to true.
 *
 * @param {string} text - full agent response text (from normalizeAgentWorkChatResponse.text)
 * @returns {{ previewText: string, fullText: string, isTruncated: boolean }}
 */
export function buildChatBubblePreview(text) {
  if (typeof text !== "string") {
    return { previewText: "", fullText: "", isTruncated: false };
  }
  const fullText = text.trim();
  const previewText = sanitizeAgentChatText(fullText);
  const isTruncated = previewText.endsWith("…") && previewText.length < fullText.length;
  return { previewText, fullText, isTruncated };
}

export function sanitizeAgentChatText(text, maxLength = MAX_CHAT_TEXT_LENGTH) {
  if (typeof text !== "string") return "";
  const safeMax = typeof maxLength === "number" && maxLength > 0 ? maxLength : MAX_CHAT_TEXT_LENGTH;

  let out = text
    // Remove ATX headings (### / ## / #)
    .replace(/^#{1,6}\s+/gm, "")
    // Bold + italic (must come before single * / _)
    .replace(/\*{3}([^*\n]+)\*{3}/g, "$1")
    .replace(/_{3}([^_\n]+)_{3}/g, "$1")
    // Bold
    .replace(/\*{2}([^*\n]+)\*{2}/g, "$1")
    .replace(/_{2}([^_\n]+)_{2}/g, "$1")
    // Italic
    .replace(/\*([^*\n]+)\*/g, "$1")
    .replace(/_([^_\n]+)_/g, "$1")
    // Inline code
    .replace(/`([^`]+)`/g, "$1")
    // Horizontal rules (---, === etc.)
    .replace(/^[-=]{3,}\s*$/gm, "")
    // Trim each line
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .join("\n");

  out = out.trim();
  if (out.length > safeMax) out = out.slice(0, safeMax - 1) + "…";
  return out;
}

/**
 * Build a short German chat bubble summarising a live-health check result.
 *
 * Spec example:
 *   "Ich habe 6 Agents geprüft: 4 live, 1 Provider-Fehler, 1 Modellfehler."
 *
 * Rules:
 *   - Uses only `available`, `results` from normalizeAgentLiveHealth
 *   - No API keys, no raw JSON, no secrets
 *   - Max ~180 chars (mobile-first)
 *   - Returns a fallback when data is unavailable
 *
 * @param {object} liveHealth – result of normalizeAgentLiveHealth
 * @returns {string}
 */
export function buildLiveHealthSummaryBubble(liveHealth) {
  if (!liveHealth || !liveHealth.available || typeof liveHealth.results !== "object") {
    return "Der Live-Test konnte gerade nicht durchgeführt werden.";
  }

  const results = Object.values(liveHealth.results);
  const total  = results.length;
  const live   = results.filter((r) => r.live === true).length;

  if (total === 0) {
    return "Keine Agents für den Live-Test gefunden.";
  }

  if (live === total) {
    return `Ich habe ${total} ${total === 1 ? "Agent" : "Agents"} geprüft: alle live.`;
  }

  // Count error types among non-live agents
  const errorCounts = {};
  for (const r of results) {
    if (r.live) continue;
    const s = r.status || "error";
    const label = AGENT_LIVE_HEALTH_STATUS_LABELS_DE[s] || AGENT_LIVE_HEALTH_STATUS_LABELS_DE.error;
    errorCounts[label] = (errorCounts[label] || 0) + 1;
  }

  const errorParts = Object.entries(errorCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3)
    .map(([label, count]) => `${count} ${label}`)
    .join(", ");

  const agentWord = total === 1 ? "Agent" : "Agents";
  const liveWord  = live === 1  ? "1 live" : `${live} live`;
  const base = `Ich habe ${total} ${agentWord} geprüft: ${liveWord}${errorParts ? `, ${errorParts}` : ""}.`;
  return base.length <= 180 ? base : base.slice(0, 178) + "…";
}

/**
 * Build a short German error chat bubble for API failures.
 *
 * Maps HTTP status codes / error indicators to safe, user-friendly German text.
 * The "Details im Drawer" reference guides the user without exposing raw errors.
 *
 * Status mapping:
 *   401 / 403     → "Keine Berechtigung – bitte Admin-Zugang prüfen."
 *   0             → "Verbindung zum Backend nicht möglich. Bitte prüfe deine Verbindung."
 *   504 / timeout → "Die Prüfung hat zu lange gedauert (Timeout). Bitte erneut versuchen."
 *   500+          → "Backend-Fehler – bitte später erneut versuchen."
 *   default       → AGENT_WORK_CHAT_ERROR_TEXT
 *
 * @param {number|string|null} [status]   – HTTP status code
 * @param {"timeout"|"network"|null} [kind] – special error kind
 * @returns {string}
 */
export function buildApiErrorChatBubble(status, kind) {
  if (kind === "timeout" || status === 504 || status === 408) {
    return "Die Prüfung hat zu lange gedauert (Timeout). Bitte erneut versuchen.";
  }
  if (kind === "network" || status === 0) {
    return "Verbindung zum Backend nicht möglich. Bitte prüfe deine Verbindung.";
  }
  const s = typeof status === "number" ? status : 0;
  if (s === 401 || s === 403) {
    return "Keine Berechtigung – bitte Admin-Zugang prüfen.";
  }
  if (s >= 500) {
    return "Backend-Fehler – bitte später erneut versuchen.";
  }
  return AGENT_WORK_CHAT_ERROR_TEXT;
}

/**
 * Build a plain-data object describing the "Aktueller Arbeitslauf" (current
 * workflow run) for the Details Drawer.  Returns plain data (no JSX) so it can
 * be unit-tested in Node without a DOM.
 *
 * Shape returned:
 *   {
 *     id:         "workflow-run"
 *     title:      "Aktueller Arbeitslauf"
 *     steps:      string[]         – phase labels (e.g. ["Anfrage empfangen", …])
 *     currentStep: string|null     – label of the step currently running
 *     sessionId:  string|null      – backend session ID (safe to show)
 *     sources:    string[]         – diagnostic sources used (safe labels only)
 *     nextStep:   string|null      – recommended next action (max 100 chars)
 *   }
 *
 * Returns null when no meaningful workflow data is present (run is null or idle).
 *
 * @param {object|null} run      – activeWorkRun shape (from createActiveWorkRun)
 * @param {object|null} [chatResponse] – latest normalizeAgentWorkChatResponse result
 * @returns {object|null}
 */
export function buildWorkflowDrawerSectionData(run, chatResponse) {
  if (!run || typeof run !== "object") return null;
  if (run.status === "idle" || (!run.steps && !run.phase)) return null;

  const steps  = Array.isArray(run.steps) ? run.steps.map((s) => (typeof s === "string" ? s : s.label || "")).filter(Boolean) : [];
  const currentStep = run.phaseLabel || run.backendProgressLabel || null;
  const sessionId = run.backendSessionId || (chatResponse && chatResponse.sessionId) || null;

  // Diagnostic sources from awareness targets (chip labels – already sanitized)
  const awarenessTargets = Array.isArray(chatResponse?.awareness?.targets)
    ? chatResponse.awareness.targets.slice(0, 5)
    : [];
  const sources = awarenessTargets.length > 0 ? awarenessTargets : [];

  // nextStep: prefer chatResponse.nextAction, fall back to run.backendHint
  const rawNext = (chatResponse && typeof chatResponse.nextAction === "string" && chatResponse.nextAction)
    ? chatResponse.nextAction
    : (typeof run.backendHint === "string" ? run.backendHint : null);
  const nextStep = rawNext ? rawNext.slice(0, 100) : null;

  return {
    id: "workflow-run",
    title: "Aktueller Arbeitslauf",
    steps,
    currentStep,
    sessionId,
    sources,
    nextStep,
  };
}

/**
 * Build a plain-data object for displaying tool results and detail sections
 * in the Details Drawer.  Returns an array of plain-data section descriptors
 * (no JSX) that the view layer renders as lists.
 *
 * Supported section types derived from `toolResults` / `detailsSections`:
 *   "checked_areas"  → "Geprüfte Bereiche"
 *   "found_issues"   → "Gefundene Punkte"
 *   "agent_status"   → "Agentenstatus"
 *   "next_step"      → "Empfohlener nächster Schritt"
 *
 * Rules:
 *   - No secrets (no api_key, token, secret fields rendered)
 *   - No raw-JSON sections
 *   - Max 5 items per section
 *   - Non-string values coerced to label strings
 *
 * @param {any}   toolResults    – raw toolResults from backend (array or object)
 * @param {any}   detailsSections – raw detailsSections from backend
 * @returns {Array<{ id: string, title: string, items: string[] }>}
 */
export function buildToolResultsDrawerSectionData(toolResults, detailsSections) {
  const out = [];

  function safeLabel(raw) {
    if (typeof raw === "string") {
      const t = raw.trim();
      return t.startsWith("{") || t.startsWith("[") ? null : t.slice(0, 120) || null;
    }
    if (raw && typeof raw === "object") {
      const label = raw.label || raw.title || raw.name || raw.text || raw.message || null;
      return typeof label === "string" ? label.slice(0, 120) : null;
    }
    return null;
  }

  function buildSection(id, title, rawItems) {
    if (!Array.isArray(rawItems) || rawItems.length === 0) return null;
    const items = rawItems.slice(0, 5).map(safeLabel).filter(Boolean);
    if (items.length === 0) return null;
    return { id, title, items };
  }

  // detailsSections: array of { type, items } or { id, title, items }
  if (Array.isArray(detailsSections)) {
    for (const sec of detailsSections) {
      if (!sec || typeof sec !== "object") continue;
      const type  = sec.type || sec.id || "";
      const items = Array.isArray(sec.items) ? sec.items : Array.isArray(sec.content) ? sec.content : [];
      const title =
        type === "checked_areas" ? "Geprüfte Bereiche" :
        type === "found_issues"  ? "Gefundene Punkte" :
        type === "agent_status"  ? "Agentenstatus" :
        type === "next_step"     ? "Empfohlener nächster Schritt" :
        typeof sec.title === "string" ? sec.title : null;
      if (!title) continue;
      const built = buildSection(`tool-${type || sec.id || out.length}`, title, items);
      if (built) out.push(built);
    }
  }

  // toolResults: array of result objects with name/output/label/result
  if (Array.isArray(toolResults) && toolResults.length > 0) {
    const items = toolResults.slice(0, 5).map((r) => {
      if (typeof r === "string") return r.slice(0, 120) || null;
      if (r && typeof r === "object") {
        const text = r.label || r.name || r.result || r.output || r.message || null;
        return typeof text === "string" ? text.slice(0, 120) : null;
      }
      return null;
    }).filter(Boolean);
    if (items.length > 0) {
      out.push({ id: "tool-results", title: "Geprüfte Bereiche", items });
    }
  }

  return out;
}

// ── Step 12: Issue Action Run State ───────────────────────────────────────────

/**
 * Short German progress labels for each possible Sentinel issue action type.
 * Shown inline in the issue card when an action is actively running.
 */
export const SENTINEL_ACTION_TYPE_LABELS_DE = {
  prepare_repair:    "Vorschlag wird vorbereitet …",
  start_approval:    "Freigabe wird gestartet …",
  claude_review:     "Claude prüft den Vorschlag …",
  openai_decision:   "OpenAI entscheidet über die Freigabe …",
  evaluate_approval: "SafetyGate prüft die Sicherheitsgrenzen …",
  execute_issue:     "Ausführung läuft …",
  refresh_status:    "Status wird aktualisiert …",
};

/**
 * Build the compact progress line shown inside the issue card while an action runs.
 * Returns null when actionType is not a recognized sentinel action type.
 *
 * @param {string} actionType – one of SENTINEL_ACTION_TYPE_LABELS_DE keys
 * @returns {string|null}
 */
export function buildSentinelIssueActionProgressLine(actionType) {
  if (typeof actionType !== "string") return null;
  return SENTINEL_ACTION_TYPE_LABELS_DE[actionType.trim().toLowerCase()] || null;
}

/**
 * Build the "Aktiver Vorgang" / "Letzter Schritt" display data from an
 * issue action state object for the Details Drawer.
 *
 * Returns `{ activeLine, stepLine }` or null when no active action.
 * No raw JSON, no technical data – only short human-readable German text.
 *
 * @param {object|null} issueActionState – { type, startedAt, step, error }
 * @returns {{ activeLine: string, stepLine: string|null } | null}
 */
export function buildSentinelIssueActiveDrawerLine(issueActionState) {
  if (!issueActionState || typeof issueActionState !== "object") return null;
  const { type, step, error } = issueActionState;
  const progressLabel = buildSentinelIssueActionProgressLine(type);
  if (!progressLabel && !error) return null;
  const activeLine = typeof error === "string" && error
    ? `Fehler: ${error.slice(0, 80)}`
    : `Aktiver Vorgang: ${progressLabel}`;
  const stepLine = typeof step === "string" && step
    ? `Letzter Schritt: ${step}`
    : null;
  return { activeLine, stepLine };
}

/**
 * Build the immediate "starting" chat bubble text when a Sentinel drawer
 * action is initiated, so the user sees feedback before the API responds.
 *
 * Returns null for action types that already produce their own start bubbles
 * (start_approval uses buildApprovalChatBubble("start", null);
 *  prepare_repair uses normalizePrepareRepairResult).
 *
 * Each returned string is ≤ 80 chars and safe for the main chat view.
 *
 * @param {string} actionType
 * @returns {string|null}
 */
export function buildSentinelActionStartChatBubble(actionType) {
  const t = typeof actionType === "string" ? actionType.trim().toLowerCase() : "";
  if (t === "claude_review")     return "Claude prüft jetzt den Vorschlag.";
  if (t === "openai_decision")   return "OpenAI entscheidet jetzt über die Freigabe.";
  if (t === "evaluate_approval") return "SafetyGate prüft jetzt die Sicherheitsgrenzen.";
  if (t === "execute_issue")     return "Die Ausführung wird jetzt gestartet.";
  // start_approval → buildApprovalChatBubble("start", null)
  // prepare_repair → result text from normalizePrepareRepairResult
  return null;
}

// ── Step 15: Pending Actions ──────────────────────────────────────────────────

/**
 * German button labels for each pending-action type.
 * Shown on the "Problem lösen" button in the PendingActionCard.
 */
export const PENDING_ACTION_BUTTON_LABELS_DE = {
  prepare_repair:       "Fix vorbereiten",
  system_diagnosis:     "Analyse starten",
  table_refresh_check:  "Tabelle prüfen",
};

/**
 * Returns the German button label for the given pending-action type.
 * Falls back to "Problem lösen" for unknown/missing action types.
 *
 * @param {string|null} action – raw action string from the backend
 * @returns {string}
 */
export function buildPendingActionButtonLabel(action) {
  if (typeof action !== "string") return "Problem lösen";
  return PENDING_ACTION_BUTTON_LABELS_DE[action.trim()] || "Problem lösen";
}

/**
 * Normalise a raw pendingAction object returned by the backend into a stable,
 * display-safe shape.
 *
 * Returns null when the raw value is missing or carries no useful content.
 *
 * Shape:
 *   {
 *     id:               string|null  – stable identifier for dedup / payload reference
 *     summary:          string|null  – short human-readable problem description (≤200 chars)
 *     action:           string|null  – machine-readable action key (e.g. "prepare_repair")
 *     targetType:       string|null  – type of the affected entity (e.g. "table")
 *     targetName:       string|null  – name of the affected entity (e.g. "techradarentries")
 *     issue:            string|null  – short issue label (e.g. "stale/yellow")
 *     suggestedWorkflow: string|null – suggested workflow identifier
 *   }
 *
 * Never throws; missing/malformed fields resolve to null.
 *
 * @param {any} raw – raw pendingAction from backend (may be null/undefined)
 * @returns {object|null}
 */
export function normalizePendingAction(raw) {
  if (!raw || typeof raw !== "object") return null;
  const id       = typeof raw.id       === "string" && raw.id       ? raw.id.slice(0, 128)       : null;
  const summary  = typeof raw.summary  === "string" && raw.summary  ? raw.summary.slice(0, 200)  : null;
  const action   = typeof raw.action   === "string" && raw.action   ? raw.action.slice(0, 100)   : null;
  const targetType = typeof raw.targetType  === "string" ? raw.targetType  :
    typeof raw.target_type  === "string"    ? raw.target_type  : null;
  const targetName = typeof raw.targetName  === "string" ? raw.targetName  :
    typeof raw.target_name  === "string"    ? raw.target_name  : null;
  const issue    = typeof raw.issue    === "string" ? raw.issue.slice(0, 100)    : null;
  const suggestedWorkflow = typeof raw.suggestedWorkflow  === "string" ? raw.suggestedWorkflow :
    typeof raw.suggested_workflow === "string"            ? raw.suggested_workflow : null;
  
  // A3 pending action fields
  const status = typeof raw.status === "string" ? raw.status : null;
  const approvalState = typeof raw.approvalState === "string" ? raw.approvalState :
    typeof raw.approval_state === "string" ? raw.approval_state : null;
  const approved = raw.approved === true || raw.approved === "true";

  // A pending action must carry at least an action key or a summary to be useful.
  if (!action && !summary) return null;
  return { id, summary, action, targetType, targetName, issue, suggestedWorkflow, status, approvalState, approved };
}

/**
 * Returns true when the user's text indicates a question about live agent health.
 *
 * Phrase examples:
 *   "Welche Agents funktionieren?"
 *   "Agent-Status prüfen"
 *   "Sind alle Agents live?"
 *   "Live-Health"
 *
 * @param {string} text
 * @returns {boolean}
 */
export function isLiveHealthIntent(text) {
  if (typeof text !== "string") return false;
  const t = text.trim().toLowerCase();
  if (!t) return false;
  return (
    /\bwelche\s+agents?\s+(funktionier|lauf|live|aktiv)\w*\b/.test(t) ||
    /\bagent[\s-]?status\b/.test(t) ||
    /\bsind\s+alle?\s+agents?\s+live\b/.test(t) ||
    /\blive[\s-]?health\b/.test(t) ||
    /\bfunktionieren\s+alle?\s+agents?\b/.test(t) ||
    /\bagent\s+gesundheit\b/.test(t)
  );
}

// ── Step 16: Workflow State Visibility & Fake-Success Blocking ─────────────────

/**
 * German labels for each workflowState value sent by the backend.
 * Used by buildWorkflowStateStatusLine() and the details drawer.
 *
 * Spec:
 *   pending_action   → "Aktion bereit – wartet auf Start"
 *   user_confirmed   → "Agent bereitet Ausführung vor"
 *   preparing        → "Agent bereitet Ausführung vor"
 *   started          → "Workflow gestartet"
 *   running          → "Workflow läuft"
 *   completed        → "Ausführung abgeschlossen"
 *   blocked          → "Ausführung blockiert"
 *   failed           → "Ausführung fehlgeschlagen"
 */
export const WORKFLOW_STATE_LABELS_DE = {
  pending_action:  "Aktion bereit – wartet auf Start",
  user_confirmed:  "Agent bereitet Ausführung vor",
  preparing:       "Agent bereitet Ausführung vor",
  started:         "Workflow gestartet",
  running:         "Workflow läuft",
  completed:       "Ausführung abgeschlossen",
  done:            "Ausführung abgeschlossen",
  confirmed:       "Ausführung bestätigt",
  blocked:         "Ausführung blockiert",
  failed:          "Ausführung fehlgeschlagen",
  cancelled:       "Ausführung abgebrochen",
  none:            "Kein aktiver Workflow",
};

/**
 * Terminal workflow states – when the backend sends any of these, all
 * pending-action and workflow-preparing UI must be cleared.
 */
export const WORKFLOW_TERMINAL_STATES = [
  "completed", "done", "confirmed", "failed", "cancelled",
];

/**
 * Non-terminal workflow states that indicate the workflow is explicitly
 * active and ongoing. When the backend sends one of these, a pending action
 * must NOT be auto-cleared even if the agent has replied.
 *
 * States like "pending_action" re-opens or replaces a pending action card,
 * so keeping it alive is correct until the user acts or a terminal state arrives.
 */
export const WORKFLOW_ACTIVE_NON_TERMINAL_STATES = new Set([
  "preparing", "user_confirmed", "started", "running", "pending_action",
]);

/**
 * Short text constants for pending-action completion signals shown briefly
 * before the PendingActionCompletionCard auto-hides.
 */
export const ACTION_COMPLETED_TEXT = "Aktion abgeschlossen";
export const ACTION_FAILED_TEXT    = "Aktion nicht abgeschlossen";

/**
 * Returns true when the given workflowState value is a terminal state
 * (the workflow has ended: completed, done, confirmed, failed, or cancelled).
 * Returns false for active/preparing states and for null/empty.
 *
 * @param {string|null} workflowState
 * @returns {boolean}
 */
export function isWorkflowTerminalState(workflowState) {
  if (typeof workflowState !== "string" || !workflowState) return false;
  const key = workflowState.trim().toLowerCase().replace(/[\s-]/g, "_");
  return WORKFLOW_TERMINAL_STATES.includes(key);
}

/**
 * Build a short German status line from the backend workflowState field.
 *
 * Returns null when workflowState is absent or not a known value, so the
 * caller can fall back to the existing phase/progressLabel mechanism.
 *
 * @param {string|null} workflowState – raw workflowState key (already lower-cased)
 * @param {string|null} [workflowMessage] – optional human-readable backend message
 * @returns {string|null}
 */
export function buildWorkflowStateStatusLine(workflowState, workflowMessage) {
  if (typeof workflowState !== "string" || !workflowState) return null;
  const key = workflowState.trim().toLowerCase().replace(/[\s-]/g, "_");
  const label = WORKFLOW_STATE_LABELS_DE[key];
  if (!label) return null;
  if (typeof workflowMessage === "string" && workflowMessage.trim()) {
    const msg = workflowMessage.trim().slice(0, 80);
    return `${label} – ${msg}`;
  }
  return label;
}

/**
 * Phrases that an LLM might use to claim it performed a real execution.
 * Checked (case-insensitively) against the agent reply text to detect
 * unconfirmed execution claims.
 *
 * When any of these phrases appear in the agent text AND the backend did NOT
 * set realExecutionPerformed=true and executionClaimVerified=true, the UI
 * MUST NOT present the response as a successful execution.
 */
export const FAKE_SUCCESS_CLAIM_PHRASES = [
  "patch wurde angewendet",
  "patch angewendet",
  "audit-eintrag wurde erstellt",
  "audit eintrag wurde erstellt",
  "änderung ist aktiv",
  "änderungen sind aktiv",
  "change is active",
  "patch applied",
  "audit entry created",
  "patch has been applied",
  "change has been applied",
  "eintrag wurde gespeichert",
  "wurde erfolgreich ausgeführt",
  "wurde ausgeführt",
  "ausführung abgeschlossen",
  "patch erfolgreich",
];

/**
 * Warning text shown to the user when a fake-success response is detected.
 * Never contains raw JSON, field names, or technical details.
 */
export const FAKE_SUCCESS_WARNING_TEXT =
  "Ausführung nicht bestätigt";

export const FAKE_SUCCESS_WARNING_SUBTEXT =
  "Der Agent hat eine Ausführung beschrieben, aber das Backend hat keine echte Ausführung bestätigt.";

/**
 * Returns true when the agent reply text contains one of the FAKE_SUCCESS_CLAIM_PHRASES.
 *
 * Comparison is case-insensitive and accent-normalised so minor punctuation
 * differences don't defeat the check.
 *
 * @param {string} text – agent reply text
 * @returns {boolean}
 */
export function hasFakeSuccessClaimText(text) {
  if (typeof text !== "string" || !text.trim()) return false;
  const normalized = text
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ß/g, "ss");
  return FAKE_SUCCESS_CLAIM_PHRASES.some((phrase) => {
    const normalizedPhrase = phrase
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/ß/g, "ss");
    return normalized.includes(normalizedPhrase);
  });
}

/**
 * Returns true when the backend chat response claims a successful execution
 * (agent text contains execution-claim phrases) but did NOT confirm it via
 * realExecutionPerformed=true AND executionClaimVerified=true.
 *
 * This is the guard against "fake-success" responses where an LLM describes
 * an action it did not actually perform.
 *
 * Rules:
 *   - Only applies when hasFakeSuccessClaimText returns true.
 *   - If realExecutionPerformed is explicitly false → fake.
 *   - If executionClaimVerified is explicitly false → fake.
 *   - If either field is null / missing (backend didn't send it) → treat as
 *     unconfirmed (fake) because we have no positive confirmation.
 *   - Both must be explicitly true to pass.
 *
 * executionReady is NOT checked here – it is gate-controlled separately.
 *
 * @param {object|null} chatResp – result of normalizeAgentWorkChatResponse
 * @returns {boolean}
 */
export function isFakeSuccessResponse(chatResp) {
  if (!chatResp || typeof chatResp !== "object") return false;
  const text = typeof chatResp.text === "string" ? chatResp.text : "";
  if (!hasFakeSuccessClaimText(text)) return false;
  // Both fields must be explicitly true to clear the check.
  if (chatResp.realExecutionPerformed !== true) return true;
  if (chatResp.executionClaimVerified !== true) return true;
  return false;
}

/**
 * Regex patterns that suggest the user is confirming / starting a pending action
 * via free text (e.g. "okay löse das Problem", "ja, mach das", "starte es").
 *
 * Used in the view layer to detect messages that LOOK like they want to trigger
 * a pending action, so we can show a helpful hint when no pending action is stored.
 */
export const PENDING_ACTION_CONFIRMATION_RE =
  /^(?:(?:okay|ok|ja|yes|go|run)\s*(?:[.!?,]\s*)?$|(?:okay|ok|ja|yes|go|run)[\s,]+(?:starte?|löse?|loese?|fix|mach|tu\s+das|ausführ|ausfuehr|führ|fuehr|behebe|repariere|setze|umsetzen)\b|(?:starte?|löse?|loese?|fix|mach|tu\s+das|ausführ|ausfuehr|führ|fuehr|behebe|repariere)\b)/i;

// ── Long-Running Agent Tasks (async task queue) ──────────────────────────────
//
// When POST /api/admin/agent-work/chat returns { accepted: true, taskId, status }
// instead of an immediate provider response, the frontend must poll for the result.
// These helpers support that async-task flow without breaking the existing sync path.

/**
 * Polling interval for long-running agent task status checks.
 * Tasks are polled at this rate while status is "queued" or "running".
 */
export const LONG_RUNNING_TASK_POLL_INTERVAL_MS = 4_000;

/**
 * Chat bubble text shown immediately after the backend accepts an async task.
 */
export const TASK_ACCEPTED_BUBBLE_TEXT =
  "Auftrag angenommen – Agent arbeitet weiter.";

/**
 * Terminal task statuses. A task MUST NOT be ended for any other reason.
 * HTTP timeouts, network errors, or long wait times are NOT terminal.
 */
export const TASK_TERMINAL_STATUSES = ["completed", "failed", "cancelled"];

/**
 * Status bubble text shown when a poll request fails due to a network/HTTP error.
 * The task is kept alive; the next poll will retry.
 */
export const TASK_POLL_ERROR_BUBBLE_TEXT =
  "Verbindung kurz unterbrochen – Auftrag läuft weiter.";

/**
 * Backoff delay schedule (ms) applied after consecutive poll failures.
 * Index 0 = after 1st failure, index 1 = after 2nd, etc.
 * The last value is repeated for all subsequent failures.
 */
export const TASK_POLL_RETRY_BACKOFF_MS = [5_000, 10_000, 15_000];

/**
 * German status labels for each task status value.
 */
export const TASK_STATUS_LABELS_DE = {
  queued:    "Auftrag wartet",
  running:   "Agent arbeitet",
  analyzing: "Agent analysiert",
  working:   "Agent arbeitet",
  completed: "Abgeschlossen",
  failed:    "Fehlgeschlagen",
  cancelled: "Abgebrochen",
};

/**
 * Returns true when the given task status is terminal (completed / failed / cancelled).
 * Only terminal statuses should end the polling loop.
 *
 * @param {string|null|undefined} status
 * @returns {boolean}
 */
export function isTaskTerminalStatus(status) {
  return typeof status === "string" && TASK_TERMINAL_STATUSES.includes(status);
}

// ── Provider Retry State ────────────────────────────────────────────────────

/**
 * Status text shown in the live-progress area while the backend is retrying
 * the model call. Replaces the generic "Agent arbeitet …" line so the user
 * immediately understands that a transient model error is being handled.
 */
export const V0_PROVIDER_RETRY_STATUS_TEXT =
  "Provider antwortet langsam";

/**
 * Appended to the live status when nextRetryAt is present so the user knows
 * a retry is imminent without seeing a countdown.
 */
export const V0_RETRY_NEXT_ATTEMPT_TEXT =
  "Nächster Versuch in wenigen Sekunden";

/**
 * Subtle hint shown in the completed message bubble when the answer was
 * obtained after one or more provider retries.
 */
export const V0_RETRY_AFTER_SUCCESS_HINT =
  "Antwort nach erneutem Modellaufruf erhalten";

/**
 * Returns true when a task is currently in a provider-retry state and must
 * NOT be displayed as a final failure.
 *
 * Conditions (any one is sufficient):
 *   - taskData.phase === "provider_retry"
 *   - taskData.retryCount > 0 AND status is not a terminal status
 *
 * @param {object|null} taskData – result of normalizeTaskStatusResponse
 * @returns {boolean}
 */
export function isProviderRetryState(taskData) {
  if (!taskData || typeof taskData !== "object") return false;
  if (taskData.phase === "provider_retry") return true;
  if (
    typeof taskData.retryCount === "number" &&
    taskData.retryCount > 0 &&
    !isTaskTerminalStatus(taskData.status)
  ) return true;
  return false;
}

/**
 * Builds a concise, human-readable German status line for the provider-retry
 * state shown in the live-progress card.
 *
 * Examples:
 *   "v0 versucht den Modellaufruf erneut · Versuch 2 von 3"
 *   "v0 versucht den Modellaufruf erneut · Versuch 1"
 *   "v0 versucht den Modellaufruf erneut"
 *
 * @param {object|null} taskData – result of normalizeTaskStatusResponse
 * @returns {string}
 */
export function buildProviderRetryStatusLine(taskData) {
  if (!taskData || typeof taskData !== "object") {
    return V0_PROVIDER_RETRY_STATUS_TEXT;
  }
  const attempt = typeof taskData.retryCount === "number" ? taskData.retryCount + 1 : null;
  const max = typeof taskData.maxRetries === "number" && taskData.maxRetries > 0
    ? taskData.maxRetries
    : null;
  if (attempt !== null && max !== null) {
    return `${V0_PROVIDER_RETRY_STATUS_TEXT} – Versuch ${attempt} von ${max}`;
  }
  if (attempt !== null) {
    return `${V0_PROVIDER_RETRY_STATUS_TEXT} – Versuch ${attempt}`;
  }
  return V0_PROVIDER_RETRY_STATUS_TEXT;
}

// ── VisualContext V1 ──────────────────────────────────────────────────────────
// Provides honest status tracking and summary normalization for the visual
// context feature that allows a user to pass a real visual website context
// to v0/Gemini/Qwen agents.
//
// Design principle: No fake visual analysis.
// – analysisPerformed is only true when the backend confirms image analysis.
// – If the backend only processes metadata, VISUAL_CONTEXT_STATUS_UNAVAILABLE
//   is shown and VISUAL_CONTEXT_NO_ANALYSIS_TEXT is displayed.

/** Status: no visual context has been attached or analyzed yet. */
export const VISUAL_CONTEXT_STATUS_NONE = "none";

/** Status: screenshot attached locally; only metadata available (no analysis yet). */
export const VISUAL_CONTEXT_STATUS_SCREENSHOT_ONLY = "screenshot_only";

/** Status: analysis request sent to backend; waiting for result. */
export const VISUAL_CONTEXT_STATUS_ANALYZING = "analyzing";

/** Status: backend returned a confirmed real visual analysis. */
export const VISUAL_CONTEXT_STATUS_AVAILABLE = "available";

/** Status: backend could not perform a real visual analysis (no multimodal provider). */
export const VISUAL_CONTEXT_STATUS_UNAVAILABLE = "unavailable";

/** Human-readable German labels for each visual context status. */
export const VISUAL_CONTEXT_STATUS_LABELS_DE = {
  [VISUAL_CONTEXT_STATUS_NONE]:             "Kein visueller Kontext",
  [VISUAL_CONTEXT_STATUS_SCREENSHOT_ONLY]:  "Screenshot angehängt",
  [VISUAL_CONTEXT_STATUS_ANALYZING]:        "Visuelle Analyse läuft",
  [VISUAL_CONTEXT_STATUS_AVAILABLE]:        "v0 nutzt visuelle Zusammenfassung (verfügbar)",
  [VISUAL_CONTEXT_STATUS_UNAVAILABLE]:      "v0 kann Bild nicht direkt sehen",
};

/** Section title shown in the design-context panel. */
export const VISUAL_CONTEXT_SECTION_TITLE = "Visueller Kontext";

/** Button label to trigger the visual context analysis. */
export const VISUAL_CONTEXT_ANALYZE_BUTTON_TEXT = "Screenshot für v0 analysieren";

/**
 * Honest label shown in the v0 response meta-footer when a VisualContextSummary
 * was attached to the request. Indicates v0 used a summary, not direct file access.
 */
export const VISUAL_CONTEXT_USES_SUMMARY_TEXT = "v0 nutzt visuelle Zusammenfassung.";

/**
 * Honest message when the backend could not perform a real image analysis.
 * Never claim image analysis was performed when only metadata was processed.
 */
export const VISUAL_CONTEXT_NO_ANALYSIS_TEXT = "v0 kann Bild nicht direkt sehen";

/**
 * Hint shown when the user tries to start a visual analysis but no screenshot
 * is attached yet.
 */
export const VISUAL_CONTEXT_NO_SCREENSHOT_HINT =
  "Bitte Screenshot anhängen oder später Live-Capture verwenden";

/**
 * UI status label: screenshot is being sent as a real image attachment in
 * the chat payload. Honest – does not claim analysis has already occurred.
 */
export const CHAT_SCREENSHOT_SENDING_STATUS = "Screenshot wird mitgesendet";

/**
 * UI status label: screenshot payload contains only metadata (no image data),
 * because the image was too large or the dataUrl was not available.
 */
export const CHAT_SCREENSHOT_METADATA_STATUS = "Nur Metadaten";

/**
 * German keyword phrases that indicate the user wants visual analysis of an
 * attached screenshot. When a message contains any of these phrases AND at
 * least one screenshot is attached, the chat send path should include the
 * image data in the payload and optionally auto-trigger VisualContext.
 *
 * Matching is case-insensitive and partial (substring), so "analysiere das
 * Bild" matches "Bitte analysiere das Bild im Screenshot".
 */
export const VISUAL_ANALYSIS_TRIGGER_KEYWORDS = [
  "screenshot für v0 analysieren",
  "analysiere das bild",
  "bild analysieren",
  "screenshot analysieren",
  "analysiere den screenshot",
  "bewerte den screenshot",
  "was siehst du",
  "verbessere das design",
  "wie können wir das design verbessern",
  "erstelle einen designvorschlag",
  "mach ein mockup",
  "wie sieht das aus",
  "bildanalyse",
  "screenshotanalyse",
];

export const DESIGN_PROPOSAL_TRIGGER_KEYWORDS = [
  "analysiere den screenshot",
  "verbessere das design",
  "wie können wir das design verbessern",
  "erstelle einen designvorschlag",
  "mach ein mockup",
  "designvorschlag",
  "design verbessern",
];

export const GENERATED_IMAGE_TRIGGER_KEYWORDS = [
  "designbild erstellen",
  "mockup generieren",
  "erstelle ein bild",
];

/**
 * Returns true when `text` contains at least one visual-analysis trigger
 * phrase. Case-insensitive substring match.
 *
 * @param {string} text – the chat message text to test
 * @returns {boolean}
 */
export function isVisualAnalysisTriggerText(text) {
  if (typeof text !== "string" || !text) return false;
  const lower = text.toLowerCase();
  return VISUAL_ANALYSIS_TRIGGER_KEYWORDS.some((kw) => lower.includes(kw));
}

export function isDesignProposalTriggerText(text) {
  if (typeof text !== "string" || !text) return false;
  const lower = text.toLowerCase();
  return DESIGN_PROPOSAL_TRIGGER_KEYWORDS.some((kw) => lower.includes(kw));
}

export function isGeneratedImageTriggerText(text) {
  if (typeof text !== "string" || !text) return false;
  const lower = text.toLowerCase();
  return GENERATED_IMAGE_TRIGGER_KEYWORDS.some((kw) => lower.includes(kw));
}

/**
 * Normalize a VisualContextSummary response from the backend.
 *
 * Returns a stable shape:
 *   {
 *     status:           VISUAL_CONTEXT_STATUS_AVAILABLE | VISUAL_CONTEXT_STATUS_UNAVAILABLE
 *     analysisPerformed: boolean  – true only when backend confirms real image analysis
 *     view:             string|null  – e.g. "Dashboard", "Login"
 *     regions:          string[]     – recognized UI regions
 *     layoutIssues:     string[]     – detected layout problems
 *     recommendedFocus: string|null  – suggested focus area for v0
 *     raw:              object|null  – original backend response
 *   }
 *
 * analysisPerformed is only set true when the backend explicitly sets
 * `analysisPerformed: true` or `imageAnalyzed: true`. Any other response
 * is treated as unavailable to avoid fake visual analysis claims.
 *
 * @param {object|null} data – raw backend response body
 * @returns {object}
 */
export function normalizeVisualContextSummary(data) {
  if (!data || typeof data !== "object") {
    return {
      status:            VISUAL_CONTEXT_STATUS_UNAVAILABLE,
      analysisPerformed: false,
      view:              null,
      regions:           [],
      layoutIssues:      [],
      recommendedFocus:  null,
      raw:               null,
    };
  }

  const analysisPerformed =
    data.analysisPerformed === true || data.imageAnalyzed === true;

  return {
    status:            analysisPerformed ? VISUAL_CONTEXT_STATUS_AVAILABLE : VISUAL_CONTEXT_STATUS_UNAVAILABLE,
    analysisPerformed,
    view:              typeof data.view === "string" && data.view            ? data.view            : null,
    regions:           Array.isArray(data.regions)       ? data.regions       : [],
    layoutIssues:      Array.isArray(data.layoutIssues)  ? data.layoutIssues  : [],
    recommendedFocus:  typeof data.recommendedFocus === "string" && data.recommendedFocus
      ? data.recommendedFocus
      : null,
    raw: data,
  };
}

function normalizeOptionalBoolean(value) {
  return value === true ? true : value === false ? false : null;
}

function normalizeAgentFlowKey(value) {
  if (typeof value !== "string") return null;
  const key = value.trim().toLowerCase().replace(/[\s-]+/g, "_");
  return key || null;
}

function normalizeAgentFlowTypeValue(value) {
  const key = normalizeAgentFlowKey(value);
  if (!key) return null;
  if (key === "canonical") return "canonical";
  if (key === "legacy") return "legacy";
  if (key === "unknown") return "unknown";
  return null;
}

function truncateAgentFlowValue(value, maxLength = 160) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.length > maxLength ? trimmed.slice(0, maxLength) : trimmed;
}

function normalizeAgentFlowBoolean(value) {
  if (value === true) return true;
  if (value === false) return false;
  if (typeof value !== "string") return null;
  const key = normalizeAgentFlowKey(value);
  if (!key) return null;
  if (["true", "yes", "1", "canonical", "enabled", "active"].includes(key)) return true;
  if (["false", "no", "0", "disabled", "inactive", "legacy"].includes(key)) return false;
  return null;
}

function normalizeAgentFlowRouteLabel(value) {
  const route = truncateAgentFlowValue(value, 240);
  if (!route) return null;
  const normalized = route.toLowerCase();
  if (normalized.includes("agent-work")) return "Agent Work";
  if (normalized.includes("direct-conference")) return "direct-conference";
  if (normalized.includes("orchestrator") && normalized.includes("legacy")) return "orchestrator-legacy";
  if (normalized.includes("canonical") && normalized.includes("matrix")) return "Canonical Matrix";
  if (normalized.includes("dispatcher")) return "Dispatcher";
  if (normalized.includes("executor")) return "Executor";
  const clean = route.replace(/^https?:\/\/[^/]+/i, "").split("?")[0].replace(/\/+$/, "");
  const lastSegment = clean.split("/").filter(Boolean).pop() || route;
  return lastSegment.length > 48 ? `${lastSegment.slice(0, 45)}…` : lastSegment;
}

function isCanonicalAgentFlowHint(value) {
  if (typeof value !== "string") return false;
  const key = value.trim().toLowerCase();
  return key.includes("agent-work")
    || key.includes("dispatcher")
    || key.includes("executor")
    || key.includes("canonical")
    || key.includes("matrix");
}

function isLegacyAgentFlowHint(value) {
  if (typeof value !== "string") return false;
  const key = value.trim().toLowerCase();
  return key.includes("legacy")
    || key.includes("direct-conference")
    || key.includes("orchestrator")
    || key.includes("alias");
}

export const AGENT_FLOW_TYPE_LABELS_DE = {
  canonical: "Canonical",
  legacy: "Legacy",
  unknown: "Pfad unklar",
};

export const AGENT_FLOW_SEVERITY_LABELS = {
  canonical: "ok",
  legacy: "warning",
  unknown: "neutral",
};

export const AGENT_BLOCK_REASON_LABELS_DE = {
  requires_approval: "Wartet auf deine Freigabe.",
  approval_required: "Wartet auf deine Freigabe.",
  invalid_repository_target: "Repository wurde vom Sicherheitsfilter blockiert.",
  invalid_patch_target: "Die Datei liegt außerhalb des erlaubten Arbeitsbereichs.",
  missing_github_token: "GitHub-Zugriff ist nicht korrekt konfiguriert.",
  pull_request_not_verified: "Der Pull Request wurde nicht durch GitHub bestätigt.",
  safety_gate_blocked: "SafetyGate hat den Lauf blockiert.",
};

export function resolveAgentBlockReasonText(blockReason, errorCode = null, requiresApproval = false, hasA3Data = false) {
  const reason = truncateAgentFlowValue(blockReason, 120);
  const code = normalizeAgentFlowKey(errorCode);
  const key = normalizeAgentFlowKey(reason);

  if (key === "live_health_not_loaded" || code === "live_health_not_loaded") {
    if (hasA3Data) {
      return "Live-Health ist nicht geladen, A3-Status wird aus Dashboard-/Result-Daten gelesen.";
    } else {
      return "Live-Health ist nicht geladen, und keine A3-Daten sind verfügbar. Die A3.5 Controlled Repair Prüfung fehlt.";
    }
  }

  if (key && AGENT_BLOCK_REASON_LABELS_DE[key]) return AGENT_BLOCK_REASON_LABELS_DE[key];
  if (!reason && code && AGENT_BLOCK_REASON_LABELS_DE[code]) return AGENT_BLOCK_REASON_LABELS_DE[code];
  if (!reason && requiresApproval === true) return AGENT_BLOCK_REASON_LABELS_DE.requires_approval;
  if (!reason) return null;
  return `Blockiert: ${reason}`;
}

export function resolveAgentNextRequiredStepText(nextRequiredStep, blockReason = null, requiresApproval = false) {
  const stepKey = normalizeAgentFlowKey(nextRequiredStep);
  if (requiresApproval === true || stepKey === "approval" || stepKey === "approve" || stepKey === "review") {
    return "Freigeben oder ablehnen";
  }
  if (stepKey === "retry" || stepKey === "rerun") return "Erneut ausführen";
  if (stepKey === "verify_pull_request" || stepKey === "pull_request_verification") return "Pull Request in GitHub prüfen";
  if (stepKey === "fix_repository_target") return "Ziel-Repository prüfen";
  if (stepKey === "fix_patch_target") return "Dateipfad im Arbeitsbereich korrigieren";
  const reasonText = resolveAgentBlockReasonText(blockReason, null, requiresApproval);
  if (reasonText === AGENT_BLOCK_REASON_LABELS_DE.requires_approval) return "Freigeben oder ablehnen";
  if (!stepKey) return null;
  return nextRequiredStep;
}

function normalizeAgentFlowSignals(payload) {
  const rawCanonicalRoute = payload?.canonicalRoute ?? payload?.canonical_route ?? null;
  const canonicalRoutePath = typeof rawCanonicalRoute === "string" && rawCanonicalRoute.trim()
    ? rawCanonicalRoute.trim()
    : null;
  const canonicalRoute = rawCanonicalRoute === true
    ? true
    : rawCanonicalRoute === false
      ? false
      : canonicalRoutePath
        ? true
        : normalizeAgentFlowBoolean(rawCanonicalRoute);
  const isCanonical = normalizeAgentFlowBoolean(payload?.isCanonical ?? payload?.is_canonical ?? null);
  const legacyRouteUsed = normalizeAgentFlowBoolean(payload?.legacyRouteUsed ?? payload?.legacy_route_used ?? null);
  const routeType = normalizeAgentFlowTypeValue(payload?.routeType ?? payload?.route_type ?? null);
  const declaredFlowType = normalizeAgentFlowTypeValue(payload?.flowType ?? payload?.flow_type ?? null);
  const executionPath = normalizeAgentFlowTypeValue(payload?.executionPath ?? payload?.execution_path ?? null);
  const sourceStage = truncateAgentFlowValue(payload?.sourceStage ?? payload?.source_stage ?? null);
  const sourceRoute = truncateAgentFlowValue(payload?.sourceRoute ?? payload?.source_route ?? null, 240);
  const route = truncateAgentFlowValue(payload?.route ?? null, 240);
  const endpoint = truncateAgentFlowValue(payload?.endpoint ?? null, 240);
  const routeLabel = normalizeAgentFlowRouteLabel(
    route || sourceRoute || endpoint || canonicalRoutePath || sourceStage,
  );
  return {
    canonicalRoute,
    canonicalRoutePath,
    isCanonical,
    legacyRouteUsed,
    routeType,
    declaredFlowType,
    executionPath,
    sourceStage,
    sourceRoute,
    route,
    endpoint,
    routeLabel,
  };
}

function resolveAgentFlowTypeFromSignals(signals) {
  const {
    canonicalRoute,
    isCanonical,
    legacyRouteUsed,
    routeType,
    declaredFlowType,
    executionPath,
    sourceStage,
    sourceRoute,
    route,
    endpoint,
    routeLabel,
  } = signals;
  const routeCandidates = [route, sourceRoute, endpoint].filter(Boolean);
  const hasLegacyHint =
    legacyRouteUsed === true
    || routeType === "legacy"
    || declaredFlowType === "legacy"
    || executionPath === "legacy"
    || routeCandidates.some(isLegacyAgentFlowHint)
    || isLegacyAgentFlowHint(sourceStage);
  const hasCanonicalHint =
    canonicalRoute === true
    || isCanonical === true
    || routeType === "canonical"
    || declaredFlowType === "canonical"
    || executionPath === "canonical"
    || routeCandidates.some(isCanonicalAgentFlowHint)
    || isCanonicalAgentFlowHint(sourceStage);

  if (hasLegacyHint) {
    return {
      flowType: "legacy",
      label: "Legacy",
      severity: "warning",
      reason: "Kompatibler Altpfad aktiv.",
      routeLabel,
    };
  }
  if (hasCanonicalHint) {
    return {
      flowType: "canonical",
      label: "Canonical",
      severity: "ok",
      reason: "Stabiler Hauptpfad erkannt.",
      routeLabel,
    };
  }
  return {
    flowType: "unknown",
    label: "Unklar",
    severity: "neutral",
    reason: "Kein eindeutiger Flow-Marker vorhanden.",
    routeLabel,
  };
}

export function resolveAgentFlowType(payload) {
  return resolveAgentFlowTypeFromSignals(normalizeAgentFlowSignals(payload));
}

export function normalizeAgentFlowMeta(payload) {
  const signals = normalizeAgentFlowSignals(payload);
  const resolution = resolveAgentFlowTypeFromSignals(signals);
  const blockReason = truncateAgentFlowValue(payload?.blockReason ?? payload?.block_reason ?? null, 120);
  const errorCode = normalizeAgentFlowKey(payload?.errorCode ?? payload?.error_code ?? null);
  const hasA3Data = Boolean(
    payload?.controlled_real_repair_status ||
    payload?.controlledRealRepairStatus ||
    payload?.latest_controlled_repair_run ||
    payload?.latestControlledRepairRun ||
    payload?.latest_work_order_id ||
    payload?.latestWorkOrderId
  );
  const requiresApproval = payload?.requiresApproval === true
    ? true
    : payload?.requiresApproval === false
      ? false
      : payload?.requires_approval === true
        ? true
        : payload?.requires_approval === false
          ? false
          : null;
  const safetyGateStatus = normalizeAgentFlowKey(
    payload?.safetyGateStatus
    ?? payload?.safety_gate_status
    ?? payload?.safetyGate?.status
    ?? payload?.safety_gate?.status
    ?? null,
  );
  const nextRequiredStep = truncateAgentFlowValue(
    payload?.nextRequiredStep ?? payload?.next_required_step ?? null,
    120,
  );
  const safetyGateActive =
    requiresApproval === true || Boolean(blockReason)
      ? true
      : safetyGateStatus == null
        ? null
        : ["inactive", "disabled", "open", "passed", "ok", "approved", "clear"].includes(safetyGateStatus)
          ? false
          : true;

  return {
    ...signals,
    ...resolution,
    flowLabel: resolution.label,
    flowSeverity: resolution.severity,
    flowReason: resolution.reason,
    blockReason,
    blockReasonText: resolveAgentBlockReasonText(blockReason, errorCode, requiresApproval === true, hasA3Data),
    safetyGateStatus,
    safetyGateActive,
    requiresApproval,
    nextRequiredStep,
    nextRequiredStepText: resolveAgentNextRequiredStepText(nextRequiredStep, blockReason, requiresApproval === true),
    errorCode,
  };
}

/**
 * Returns true when an async task has completed with a real assistant answer.
 *
 * This is the primary guard used by the polling completed-branch to decide
 * whether to display the task result as a real agent chat bubble (true) or to
 * fall back to the generic normalizeAgentWorkChatResponse path (false).
 *
 * Detection criteria (all must be true):
 *   - taskData.status === "completed"
 *   - taskData.text is a non-empty string (extracted from assistantMessage or text)
 *
 * Unlike isTrueProviderResponse, this function does NOT require providerCall/source
 * because async task status responses are not wrapped in the sync provider contract.
 *
 * @param {object|null} taskData – result of normalizeTaskStatusResponse
 * @returns {boolean}
 */
export function isTaskCompletedWithAnswer(taskData) {
  if (!taskData || typeof taskData !== "object") return false;
  if (taskData.status !== "completed") return false;
  const text = typeof taskData.text === "string" ? taskData.text.trim() : "";
  if (!text) return false;
  // Known error/fallback texts must never be treated as real provider answers.
  // Showing these as "completed" would produce a contradictory "v0 fertig ·
  // Antwort bereit" card while the error text appears in the chat bubble.
  if (text === PROVIDER_ERROR_BUBBLE_TEXT) return false;
  return true;
}

/**
 * Returns true when the backend has accepted a task asynchronously instead of
 * returning an immediate provider response.
 *
 * Detection criteria (all must be true):
 *   - data.accepted === true
 *   - data.taskId is a non-empty string
 *
 * @param {any} data – raw response body from POST …/agent-work/chat
 * @returns {boolean}
 */
export function isAcceptedResponse(data) {
  if (!data || typeof data !== "object") return false;
  return data.accepted === true && typeof data.taskId === "string" && data.taskId.length > 0;
}

/**
 * Normalise a task status response from
 * GET /api/admin/agent-work-chat/tasks/:taskId
 * into a stable, display-safe shape.
 *
 * Returned shape:
 *   {
 *     taskId:          string|null  – stable task identifier
 *     status:          "queued"|"running"|"completed"|"failed"
 *     phase:           string|null  – current processing phase
 *     mode:            string|null  – backend AgentRunMode key
 *     progressMessage: string|null  – human-readable progress hint
 *     errorMessage:    string|null  – set only on failed status
 *     traceId:         string|null  – backend trace ID (shown in details on failure)
 *     systemContextUsed: boolean|null
 *     evidenceRefs:    object|null
 *     missingContext:  string[]|null
 *     text:            string|null  – final assistant message (set on completed)
 *     sessionId:       string|null
 *     retryCount:      number|null  – how many provider retries have been attempted
 *     maxRetries:      number|null  – maximum retries the backend will attempt
 *     providerErrorType: string|null – e.g. "timeout", "rate_limit"
 *     lastProviderError: string|null – human-readable last provider error
 *     nextRetryAt:     string|null  – ISO timestamp of the next scheduled retry
 *   }
 *
 * Never throws. Missing or invalid fields resolve to null.
 *
 * @param {any} data – raw response body from the task-status endpoint
 * @returns {object}
 */
export function normalizeTaskStatusResponse(data) {
  if (!data || typeof data !== "object") {
    // Do NOT return "failed" here – a null/invalid response means the poll
    // request itself failed (network/HTTP error), not that the task failed.
    // Callers must check result.error first; this path is a safety fallback only.
    return {
      taskId: null,
      status: "running",
      phase: null,
      mode: null,
      progressMessage: null,
      errorMessage: null,
      traceId: null,
      systemContextUsed: null,
      evidenceRefs: null,
      missingContext: null,
      text: null,
      sessionId: null,
      updatedAt: null,
      retryCount: null,
      maxRetries: null,
      providerErrorType: null,
      lastProviderError: null,
      nextRetryAt: null,
      designContextUsed: null,
      screenshotContextUsed: null,
      screenshotImageForwarded: null,
      screenshotsMissingContent: null,
      screenshotProviderCapability: null,
      visualContextUsed: null,
      visualContextUsedByV0: null,
      hasScreenshotDataUrl: null,
      supportsDirectImageInput: null,
      supportsVisualContextSummary: null,
      designProposal: null,
      generatedImage: null,
      imageGenerationAvailable: null,
      imageGenerationError: null,
      agentFlow: normalizeAgentFlowMeta(null),
      canonicalRoute: null,
      isCanonical: null,
      legacyRouteUsed: null,
      routeType: null,
      flowType: "unknown",
      executionPath: null,
      sourceStage: null,
      sourceRoute: null,
      route: null,
      endpoint: null,
      routeLabel: null,
      blockReason: null,
      blockReasonText: null,
      safetyGateStatus: null,
      safetyGateActive: null,
      requiresApproval: null,
      nextRequiredStep: null,
      nextRequiredStepText: null,
    };
  }

  const rawStatus = typeof data.status === "string" ? data.status.toLowerCase().trim() : "";
  // Canonical non-terminal statuses. "analyzing" and "working" are treated as
  // aliases for "running" so any backend wording keeps the task alive.
  const CANONICAL_STATUSES = ["queued", "running", "analyzing", "working", "completed", "failed", "cancelled"];
  const status = CANONICAL_STATUSES.includes(rawStatus) ? rawStatus : "running";

  const errorMessage =
    typeof data.errorMessage === "string" && data.errorMessage
      ? data.errorMessage
      : typeof data.error === "string" && data.error
        ? data.error
        : null;
  const agentFlow = normalizeAgentFlowMeta(data);

  return {
    taskId:          typeof data.taskId === "string" && data.taskId ? data.taskId : null,
    status,
    phase:           typeof data.phase === "string" && data.phase ? data.phase : null,
    mode:            normalizeAgentRunMode(
      (typeof data.mode === "string" && data.mode)
        ? data.mode
        : (typeof data.agentRunMode === "string" && data.agentRunMode)
          ? data.agentRunMode
          : (typeof data.agent_run_mode === "string" && data.agent_run_mode)
            ? data.agent_run_mode
            : (typeof data.runMode === "string" && data.runMode)
              ? data.runMode
              : data.run_mode,
    ),
    progressMessage: typeof data.progressMessage === "string" && data.progressMessage
      ? data.progressMessage
      : null,
    errorMessage,
    traceId:         typeof data.traceId === "string" && data.traceId ? data.traceId : null,
    systemContextUsed: data.systemContextUsed != null ? Boolean(data.systemContextUsed) : null,
    evidenceRefs:    (data.evidenceRefs && typeof data.evidenceRefs === "object")
      ? data.evidenceRefs
      : null,
    missingContext:  Array.isArray(data.missingContext) ? data.missingContext : null,
    text:            typeof data.assistantMessage === "string" && data.assistantMessage
      ? data.assistantMessage
      : typeof data.text === "string" && data.text
        ? data.text
        : null,
    sessionId:       typeof data.sessionId === "string" && data.sessionId ? data.sessionId : null,
    updatedAt:       typeof data.updatedAt === "string" && data.updatedAt ? data.updatedAt : null,
    retryCount:      typeof data.retryCount === "number" ? data.retryCount : null,
    maxRetries:      typeof data.maxRetries === "number" ? data.maxRetries : null,
    providerErrorType: typeof data.providerErrorType === "string" && data.providerErrorType
      ? data.providerErrorType
      : null,
    lastProviderError: typeof data.lastProviderError === "string" && data.lastProviderError
      ? data.lastProviderError
      : null,
    nextRetryAt:     typeof data.nextRetryAt === "string" && data.nextRetryAt
      ? data.nextRetryAt
      : null,
    designContextUsed: normalizeOptionalBoolean(
      data.designContextUsed !== undefined ? data.designContextUsed : data.design_context_used,
    ),
    screenshotContextUsed: normalizeOptionalBoolean(
      data.screenshotContextUsed !== undefined ? data.screenshotContextUsed : data.screenshot_context_used,
    ),
    screenshotImageForwarded: normalizeOptionalBoolean(
      data.screenshotImageForwarded !== undefined ? data.screenshotImageForwarded : data.screenshot_image_forwarded,
    ),
    screenshotsMissingContent: Array.isArray(data.screenshotsMissingContent)
      ? data.screenshotsMissingContent
      : Array.isArray(data.screenshots_missing_content)
        ? data.screenshots_missing_content
        : null,
    screenshotProviderCapability: data.screenshotProviderCapability !== undefined
      ? data.screenshotProviderCapability
      : data.screenshot_provider_capability !== undefined
        ? data.screenshot_provider_capability
        : null,
    visualContextUsed: normalizeOptionalBoolean(
      data.visualContextUsed !== undefined
        ? data.visualContextUsed
        : data.visual_context_used !== undefined
          ? data.visual_context_used
          : data.visualContextUsedByV0 !== undefined
            ? data.visualContextUsedByV0
            : data.visual_context_used_by_v0,
    ),
    visualContextUsedByV0: normalizeOptionalBoolean(
      data.visualContextUsedByV0 !== undefined ? data.visualContextUsedByV0 : data.visual_context_used_by_v0,
    ),
    hasScreenshotDataUrl: normalizeOptionalBoolean(
      data.hasScreenshotDataUrl !== undefined ? data.hasScreenshotDataUrl : data.has_screenshot_data_url,
    ),
    supportsDirectImageInput: normalizeOptionalBoolean(
      data.supportsDirectImageInput !== undefined ? data.supportsDirectImageInput : data.supports_direct_image_input,
    ),
    supportsVisualContextSummary: normalizeOptionalBoolean(
      data.supportsVisualContextSummary !== undefined ? data.supportsVisualContextSummary : data.supports_visual_context_summary,
    ),
    designProposal: (data.designProposal && typeof data.designProposal === "object" && !Array.isArray(data.designProposal))
      ? data.designProposal
      : (data.design_proposal && typeof data.design_proposal === "object" && !Array.isArray(data.design_proposal))
        ? data.design_proposal
        : null,
    generatedImage: (data.generatedImage && typeof data.generatedImage === "object" && !Array.isArray(data.generatedImage))
      ? data.generatedImage
      : (data.generated_image && typeof data.generated_image === "object" && !Array.isArray(data.generated_image))
        ? data.generated_image
        : null,
    imageGenerationAvailable: normalizeOptionalBoolean(
      data.imageGenerationAvailable !== undefined ? data.imageGenerationAvailable : data.image_generation_available,
    ),
    imageGenerationError: typeof data.imageGenerationError === "string" && data.imageGenerationError
      ? data.imageGenerationError
      : typeof data.image_generation_error === "string" && data.image_generation_error
        ? data.image_generation_error
        : null,
    agentFlow,
    canonicalRoute: agentFlow.canonicalRoute,
    isCanonical: agentFlow.isCanonical,
    legacyRouteUsed: agentFlow.legacyRouteUsed,
    routeType: agentFlow.routeType,
    flowType: agentFlow.flowType,
    executionPath: agentFlow.executionPath,
    sourceStage: agentFlow.sourceStage,
    sourceRoute: agentFlow.sourceRoute,
    route: agentFlow.route,
    endpoint: agentFlow.endpoint,
    routeLabel: agentFlow.routeLabel,
    blockReason: agentFlow.blockReason,
    blockReasonText: agentFlow.blockReasonText,
    safetyGateStatus: agentFlow.safetyGateStatus,
    safetyGateActive: agentFlow.safetyGateActive,
    requiresApproval: agentFlow.requiresApproval,
    nextRequiredStep: agentFlow.nextRequiredStep,
    nextRequiredStepText: agentFlow.nextRequiredStepText,
  };
}

// ── GeneratedImage V2 – status constants ─────────────────────────────────

/** Card title shown above the generated image block. */
export const GENERATED_IMAGE_CARD_TITLE = "v0 Designbild";

/** Status shown while image generation is in progress. */
export const IMAGE_GENERATION_PENDING_TEXT = "Designbild wird erstellt ...";

/** Status shown after image generation succeeds. */
export const IMAGE_GENERATION_SUCCESS_TEXT = "Designbild erstellt";

/** Status shown when no image-generation provider is configured. */
export const IMAGE_GENERATION_UNAVAILABLE_TEXT = "Designbild-Erstellung nicht verfügbar";

/** Status shown when image generation fails (DesignProposal stays visible). */
export const IMAGE_GENERATION_ERROR_TEXT =
  "Designbild konnte nicht erstellt werden – DesignProposal bleibt verfügbar";

// ── DesignProposal V1 ──────────────────────────────────────────────────────

/** Section title for the DesignProposal card. */
export const DESIGN_PROPOSAL_CARD_TITLE = "Design-Vorschlag";

/** Badge shown when the proposal was derived from a prior visual-context analysis. */
export const DESIGN_PROPOSAL_VISUAL_CONTEXT_BADGE = "basiert auf visuellem Kontext";

/** Badge shown when the proposal was derived from design-context only (no screenshot analysis). */
export const DESIGN_PROPOSAL_DESIGN_CONTEXT_BADGE = "von v0";

/** Label for the "hand off to Qwen" quick-action button. */
export const DESIGN_PROPOSAL_QWEN_HANDOFF_LABEL = "An Qwen zur Umsetzung übergeben";

/** Disabled state hint when uiChangeset / handoffReady is missing. */
export const DESIGN_PROPOSAL_HANDOFF_NOT_READY_HINT =
  "Umsetzungs-Übergabe erst verfügbar wenn UIChangeset und handoffReady vorhanden sind";

/** Label for the optional "refine design" secondary action. */
export const DESIGN_PROPOSAL_REFINE_LABEL = "Design verfeinern";

/** Label for the optional "create alternative" secondary action. */
export const DESIGN_PROPOSAL_ALTERNATIVE_LABEL = "Alternative erstellen";

/**
 * Normalise the raw `designProposal` object returned by the backend.
 *
 * Returns a safe, stable shape ready for rendering:
 *   {
 *     title:             string|null
 *     concept:           string|null
 *     layout:            string|null
 *     colors:            string[]
 *     components:        string[]
 *     mobileNotes:       string|null
 *     implementationNotes: string|null
 *     basedOnVisual:     boolean        – true when derived from a visual-context analysis
 *     handoffReady:      boolean        – true when Qwen can take over immediately
 *     uiChangeset:       object|null    – forwarded as-is for the Qwen handoff
 *   }
 *
 * Returns null for non-object input so callers can guard with a simple null-check.
 */
export function normalizeDesignProposal(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;

  const strOrNull = (v) => (typeof v === "string" && v.trim() ? v.trim() : null);
  const strArray  = (v) => Array.isArray(v) ? v.filter((s) => typeof s === "string" && s.trim()) : [];
  const uiChangeset = (raw.uiChangeset && typeof raw.uiChangeset === "object" && !Array.isArray(raw.uiChangeset)) ? raw.uiChangeset : null;
  const handoffReady = raw.handoffReady === true && uiChangeset !== null;

  return {
    title:               strOrNull(raw.title),
    concept:             strOrNull(raw.concept),
    layout:              strOrNull(raw.layout),
    colors:              strArray(raw.colors),
    components:          strArray(raw.components),
    mobileNotes:         strOrNull(raw.mobileNotes),
    implementationNotes: strOrNull(raw.implementationNotes),
    basedOnVisual:       raw.basedOnVisual === true || raw.basedOnVisualContext === true || raw.visualContextUsed === true,
    handoffReady,
    uiChangeset,
  };
}

/**
 * Normalise the raw `generatedImage` object returned by the backend.
 *
 * Returns a safe shape:
 *   {
 *     url:    string|null  – absolute URL ready for <img src>; null when missing
 *     assetId: string|null
 *     prompt:       string|null
 *     model:        string|null
 *     provider:     string|null – image-generation provider name (e.g. "dall-e-3")
 *     basedOnVisual: boolean    – true when the image was generated from a VisualContext analysis
 *   }
 *
 * IMPORTANT: returns null when neither `url` nor `assetId` is present so the UI
 * never renders a fake image placeholder.
 */
export function normalizeGeneratedImage(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;

  const url     = typeof raw.url === "string" && raw.url.trim() ? raw.url.trim() : null;
  const assetId = typeof raw.assetId === "string" && raw.assetId.trim() ? raw.assetId.trim() : null;

  // No real image reference → caller must not show any image element.
  if (!url && !assetId) return null;

  return {
    url,
    assetId,
    prompt:        typeof raw.prompt    === "string" && raw.prompt.trim()    ? raw.prompt.trim()    : null,
    model:         typeof raw.model     === "string" && raw.model.trim()     ? raw.model.trim()     : null,
    provider:      typeof raw.provider  === "string" && raw.provider.trim()  ? raw.provider.trim()  : null,
    basedOnVisual: raw.basedOnVisual === true,
  };
}

// ── Handoff Card V1 ────────────────────────────────────────────────────────

/** Title shown on the handoff-start card. */
export const HANDOFF_STARTED_TITLE = "Übergabe gestartet";

/** Status text shown on the handoff-start card while the task is running. */
export const HANDOFF_RUNNING_STATUS = "läuft";

/** Security note always shown on the handoff-start card. */
export const HANDOFF_SECURITY_NOTE = "Kein Auto-Merge, kein Auto-Deploy";

/** Title shown on the handoff-completion card. */
export const HANDOFF_COMPLETED_TITLE = "Umsetzung abgeschlossen";

/** Label prefix for open items in the completion card. */
export const HANDOFF_OPEN_LABEL = "Noch offen";

/** Label shown when user approval is required. */
export const HANDOFF_USER_APPROVAL_REQUIRED_TEXT = "Nutzerfreigabe erforderlich";

/** Label shown when no user approval is needed. */
export const HANDOFF_USER_APPROVAL_NOT_NEEDED_TEXT = "Keine Freigabe erforderlich";

/** Status text shown while Qwen is actively working on the implementation. */
export const HANDOFF_IN_PROGRESS_TEXT = "arbeitet an der Umsetzung";

/** Status text shown while files are being checked during implementation. */
export const HANDOFF_CHECKING_FILES_TEXT = "Dateien werden geprüft";

/** Status text shown while the implementation is being prepared. */
export const HANDOFF_PREPARING_TEXT = "Umsetzung wird vorbereitet";

/** Status text shown while a patch is being created. */
export const HANDOFF_PATCH_CREATING_TEXT = "Patch wird erstellt";

/** Status text shown while tests are running during implementation. */
export const HANDOFF_TESTS_RUNNING_TEXT = "Tests laufen";

/** Status text shown while the result is being prepared for delivery. */
export const HANDOFF_RESULT_PREPARING_TEXT = "Ergebnis wird vorbereitet";

/** Error text shown when no actionable proposal was found. */
export const HANDOFF_NO_PROPOSAL_TEXT = "Kein umsetzbarer Vorschlag gefunden";

/** Error text shown when the last proposal lacks sufficient structure. */
export const HANDOFF_INVALID_PROPOSAL_TEXT =
  "Der letzte Vorschlag ist nicht strukturiert genug";

/** Error text shown when the target agent is temporarily unavailable. */
export const HANDOFF_AGENT_UNAVAILABLE_TEXT =
  "Ziel-Agent aktuell nicht verfügbar";

/** Error text shown when the handoff task could not be started. */
export const HANDOFF_TASK_START_FAILED_TEXT =
  "Übergabe konnte nicht gestartet werden";

/** Badge text shown in the completion card when the branch was merged to main. */
export const HANDOFF_MERGED_TO_MAIN_TEXT = "Auf main gemerged";

/** Link label for the pull-request URL shown in the completion card. */
export const HANDOFF_PR_LINK_LABEL = "Pull Request öffnen";

// ── PR-Status & Fake-Link-Guard V1 ────────────────────────────────────────────

/** Allowed GitHub repos for valid PR links. */
export const ALLOWED_PR_REPOS = [
  "Davidhuecker-bit/hqs-backend",
  "Davidhuecker-bit/hqs-private-quant",
];

/** Human-readable PR state labels (German). */
export const PR_STATE_LABELS_DE = {
  draft:    "Entwurf",
  open:     "Offen",
  merged:   "Gemerged",
  closed:   "Geschlossen",
  failed:   "Fehlgeschlagen",
  unknown:  "Unbekannt",
};

/** Warning text shown when a PR link is detected as invalid/fake. */
export const PR_LINK_INVALID_WARNING_TEXT = "Link ungültig oder nicht vertrauenswürdig";

/** Label for the PR number badge. */
export const PR_NUMBER_LABEL = "PR";

/**
 * Regular expression that matches a valid GitHub pull request URL from an allowed repo.
 * Captures: [1] = owner/repo, [2] = PR number.
 */
const VALID_GITHUB_PR_RE = /^https:\/\/github\.com\/(Davidhuecker-bit\/hqs-(?:backend|private-quant))\/pull\/(\d+)$/;

/**
 * Returns true when the given URL is a valid GitHub pull request link
 * from one of the allowed repositories.
 *
 * @param {any} url
 * @returns {boolean}
 */
export function isValidGithubPullRequestUrl(url) {
  if (!url || typeof url !== "string") return false;
  return VALID_GITHUB_PR_RE.test(url.trim());
}

/**
 * Extracts the PR number from a valid GitHub PR URL.
 * Returns null for invalid URLs.
 *
 * @param {any} url
 * @returns {number|null}
 */
export function extractPrNumberFromUrl(url) {
  if (!url || typeof url !== "string") return null;
  const m = url.trim().match(VALID_GITHUB_PR_RE);
  return m ? parseInt(m[2], 10) : null;
}

/**
 * Defensively normalizes PR number from backend data.
 * Accepts: pullRequestNumber, prNumber, number, pull_request_number.
 *
 * @param {any} data
 * @returns {number|null}
 */
export function normalizePullRequestNumber(data) {
  if (!data || typeof data !== "object") return null;
  const raw = data.pullRequestNumber ?? data.prNumber ?? data.number ?? data.pull_request_number;
  if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) return raw;
  if (typeof raw === "string") {
    const n = parseInt(raw, 10);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

/**
 * Defensively normalizes PR URL from backend data.
 * Accepts: pullRequestUrl, prUrl, html_url, pull_request_url.
 *
 * @param {any} data
 * @returns {string|null}
 */
export function normalizePullRequestUrl(data) {
  if (!data || typeof data !== "object") return null;
  const raw = data.pullRequestUrl ?? data.prUrl ?? data.html_url ?? data.pull_request_url;
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  return null;
}

/**
 * Defensively normalizes PR state from backend data.
 * Accepts: pullRequestState, prState, state, status (mapped).
 * Returns one of: draft | open | merged | closed | failed | unknown | null.
 *
 * @param {any} data
 * @returns {string|null}
 */
export function normalizePullRequestState(data) {
  if (!data || typeof data !== "object") return null;
  const raw = data.pullRequestState ?? data.prState ?? data.state ?? data.status;
  if (!raw || typeof raw !== "string") return null;
  const val = raw.trim().toLowerCase();
  const KNOWN = ["draft", "open", "merged", "closed", "failed"];
  if (KNOWN.includes(val)) return val;
  // Map some common backend variants
  if (val === "completed" || val === "merged_to_main") return "merged";
  if (val === "error" || val === "errored") return "failed";
  return "unknown";
}

/**
 * Human-readable display label for a PR state value.
 *
 * @param {string|null|undefined} state
 * @returns {string}
 */
export function resolvePrStateLabel(state) {
  if (!state || typeof state !== "string") return PR_STATE_LABELS_DE.unknown;
  return PR_STATE_LABELS_DE[state] ?? PR_STATE_LABELS_DE.unknown;
}

/**
 * Human-readable display name for an agent ID.
 * Used in handoff cards to show "Von: v0" / "An: Qwen/Sven".
 */
export const HANDOFF_AGENT_DISPLAY_NAMES = {
  v0:       "v0",
  qwen:     "Qwen/Sven",
  sven:     "Qwen/Sven",
  gemini:   "Gemini",
  deepseek: "DeepSeek",
  openai:   "OpenAI",
  claude:   "Claude",
};

/**
 * Returns a display-safe agent name for a handoff card.
 * Falls back to the raw value with first-letter capitalised.
 *
 * @param {string|null|undefined} agentId
 * @returns {string}
 */
export function resolveHandoffAgentLabel(agentId) {
  if (!agentId || typeof agentId !== "string") return "Agent";
  const key = agentId.trim().toLowerCase();
  if (HANDOFF_AGENT_DISPLAY_NAMES[key]) return HANDOFF_AGENT_DISPLAY_NAMES[key];
  return key.charAt(0).toUpperCase() + key.slice(1);
}

/**
 * Maps a backend handoff phase key to a human-readable German live-status line.
 * Returns null when the phase is unknown so the caller can fall back to the
 * generic status label.
 *
 * Supported phase values:
 *   preparing / in_progress / working / checking_files / patch_creating /
 *   tests_running / result_preparing
 *
 * @param {string|null|undefined} phase
 * @returns {string|null}
 */
export function buildHandoffLiveStatusLine(phase) {
  if (typeof phase !== "string") return null;
  switch (phase.trim().toLowerCase().replace(/[-\s]/g, "_")) {
    case "preparing":
    case "preparing_implementation":
      return HANDOFF_PREPARING_TEXT;
    case "in_progress":
    case "working":
      return HANDOFF_IN_PROGRESS_TEXT;
    case "checking_files":
    case "files_checking":
      return HANDOFF_CHECKING_FILES_TEXT;
    case "patch_creating":
    case "creating_patch":
      return HANDOFF_PATCH_CREATING_TEXT;
    case "tests_running":
    case "running_tests":
      return HANDOFF_TESTS_RUNNING_TEXT;
    case "result_preparing":
    case "preparing_result":
      return HANDOFF_RESULT_PREPARING_TEXT;
    default:
      return null;
  }
}

/**
 * Returns true when the given value is a known handoff error code that should
 * be surfaced as a specific user-facing hint instead of a generic error bubble.
 *
 * @param {string|null|undefined} code
 * @returns {boolean}
 */
export function isHandoffErrorCode(code) {
  return typeof code === "string" &&
    ["no_proposal", "invalid_proposal", "agent_unavailable", "task_start_failed"].includes(
      code.trim().toLowerCase(),
    );
}

/**
 * Returns a German user-facing error text for a known handoff error code.
 * Falls back to HANDOFF_TASK_START_FAILED_TEXT for unknown codes.
 *
 * @param {string|null|undefined} code
 * @returns {string}
 */
export function buildHandoffErrorText(code) {
  switch (typeof code === "string" ? code.trim().toLowerCase() : "") {
    case "no_proposal":       return HANDOFF_NO_PROPOSAL_TEXT;
    case "invalid_proposal":  return HANDOFF_INVALID_PROPOSAL_TEXT;
    case "agent_unavailable": return HANDOFF_AGENT_UNAVAILABLE_TEXT;
    case "task_start_failed": return HANDOFF_TASK_START_FAILED_TEXT;
    default:                  return HANDOFF_TASK_START_FAILED_TEXT;
  }
}

/**
 * Returns true when the user's natural-language text is asking for
 * the implementation of a previously proposed change – e.g. "Setze das um".
 *
 * These commands trigger the same visible handoff-start card as the button.
 *
 * @param {string} text
 * @returns {boolean}
 */
export function isImplementationHandoffTriggerText(text) {
  if (typeof text !== "string") return false;
  const t = text.trim().toLowerCase();
  if (!t) return false;
  const patterns = [
    // "Setze das um" / "setze es um" / "setze um" (direct, short form)
    /\bsetze?\s+(?:das|es|die|bitte)?\s*um\b/,
    // "Setze diesen Vorschlag mit Qwen um" – "setze" followed by a determiner
    // then arbitrary content and ending with "um" (the root cause fix)
    /\bsetze?\s+(?:das|diesen?|den|die|einen?|solche?n?|ihn|sie|jetzt|bitte)\b.{0,100}\bum\b/,
    // "mit Qwen/Sven/… umsetzen"
    /\bmit\s+(?:qwen|sven|gemini|claude|deepseek|openai)\s+umsetzen\b/,
    /\bumsetzen\b/,
    /\bimplementiere?\b/,
    /\bbitte\s+umsetzen\b/,
    /\bbitte\s+implementier/,
    /\bwende\s+die\s+änder/,
    /\bbaue\s+das\s+um\b/,
    /\bbaue\s+(?:das|es)\s+jetzt\b/,
    // "Baue das ein" / "baue es ein" / "baue das ein und merge auf main"
    /\bbaue?\s+(?:das|es)\s+ein\b/,
    /\bmach\s+das\b/,
    /\bverbessere\s+das\b/,
    /\bbehebe\s+das\b/,
    /\b(?:setze|setz|mach)\s+punkt\s*\d+\b/,
    /\bpunkt\s*\d+\b.{0,40}\b(?:umsetzen|mach|bitte)\b/,
    /\b\d+(?:\s*(?:,|und|&)\s*\d+)+\b.{0,40}\bumsetzen\b/,
    // "Mach daraus einen PR" / "mach einen PR" / "erstelle einen PR"
    /\b(?:mach|erstell|bau)\w*\s+(?:daraus\s+)?(?:einen?\s+)?pr\b/,
    /\bpr\s+vorbereiten\b/,
    // "merge auf/nach/in main/master"
    /\bmerge\s+(?:auf|nach|in|to)\s+(?:main|master|develop)\b/,
    // "Löse das Problem, das du gefunden hast" / "behebe die gefundenen Fehler"
    /(?:^|\s)(?:löse?|loese?|behebe|fixe?|repariere?)\s+(?:das|dies(?:es)?|den|die|alle)?\s*(?:problem|probleme|fehler|bug|bugs|issue|issues|punkte?)\b/,
    /(?:^|\s)(?:löse?|loese?|behebe|fixe?|repariere?)\s+(?:das|dies(?:es)?|den|die|alle)?\s*(?:gefundenen?|erkannten?|analysierten?|vorgeschlagenen?)\s+(?:problem|probleme|fehler|bug|bugs|issue|issues|punkte?)\b/,
    /(?:^|\s)(?:löse?|loese?|behebe|fixe?|repariere?)\b.{0,80}\b(?:gefunden|gefundenen|erkannt|erkannten|analysiert|vorgeschlagen)\b/,
    /(?:^|\s)übergib\s+(?:das|es)\s+an\s+(?:qwen|sven)/,
    /(?:^|\s)übergib\s+(?:das|es)(?:\s|$)/,
    /(?:^|\s)übergib\s+an\s+(?:qwen|sven)\b/,
    /(?:^|\s)übergib\s+(?:das|es)\s+an\s+den\s+agenten/,
    /^\s*freigegeben\s*[.!]?\s*$/,
    /\bich\s+gebe\s+dir\s+die\s+freigabe\b/,
    /\bfang\s+damit\s+an\b/,
  ];
  return patterns.some((re) => re.test(t));
}

/**
 * Normalise the metadata from a message or task result that signals a
 * handoff has been started.
 *
 * Accepts any of:
 *   { handoffStarted: true, fromAgent, toAgent, sourceProposalType, taskId, summaryText }
 *
 * Returns null when the input is not a valid handoff-start payload.
 *
 * @param {any} data
 * @returns {{ fromAgent: string|null, toAgent: string|null, sourceProposalType: string|null, taskId: string|null, summaryText: string|null }|null}
 */
export function normalizeHandoffStart(data) {
  if (!data || typeof data !== "object") return null;
  const fromAgent = typeof data.fromAgent === "string" && data.fromAgent.trim()
    ? data.fromAgent.trim().toLowerCase()
    : null;
  const toAgent   = typeof data.toAgent   === "string" && data.toAgent.trim()
    ? data.toAgent.trim().toLowerCase()
    : null;
  if (!fromAgent && !toAgent) return null;
  return {
    fromAgent,
    toAgent,
    sourceProposalType: typeof data.sourceProposalType === "string" && data.sourceProposalType.trim()
      ? data.sourceProposalType.trim()
      : null,
    taskId: typeof data.taskId === "string" && data.taskId.trim()
      ? data.taskId.trim()
      : null,
    summaryText: typeof data.summaryText === "string" && data.summaryText.trim()
      ? data.summaryText.trim()
      : null,
  };
}

/**
 * Normalise the metadata from a task result that signals a handoff has
 * been completed.
 *
 * Returns null when the input does not contain any handoff-completion data.
 *
 * @param {any} data
 * @returns {{
 *   summary: string|null,
 *   changedFiles: string[],
 *   implementedChanges: string[],
 *   tests: string|null,
 *   buildStatus: string|null,
 *   openItems: string[],
 *   requiresUserApproval: boolean,
 *   pullRequestUrl: string|null,
 *   pullRequestNumber: number|null,
 *   pullRequestState: string|null,
 *   pullRequestValid: boolean,
 *   mergedToMain: boolean,
 *   targetOwner: string|null,
 *   targetRepo: string|null,
 *   targetBranch: string|null,
 *   repositoryValid: boolean|null,
 *   errorCode: string|null,
 *   nextRequiredStep: string|null,
 * }|null}
 */
export function normalizeHandoffCompletion(data) {
  if (!data || typeof data !== "object") return null;
  const hasCompletion =
    data.handoffCompleted === true ||
    (Array.isArray(data.changedFiles) && data.changedFiles.length > 0) ||
    (Array.isArray(data.implementedChanges) && data.implementedChanges.length > 0);
  if (!hasCompletion) return null;
  const strArray = (v) =>
    Array.isArray(v) ? v.filter((s) => typeof s === "string" && s.trim()) : [];
  const strOrNull = (v) =>
    typeof v === "string" && v.trim() ? v.trim() : null;

  // Defensive PR URL normalization (accept multiple backend key variants)
  const prUrl = normalizePullRequestUrl(data);
  // Defensive PR number normalization; fallback: extract from URL
  const prNumber = normalizePullRequestNumber(data) ?? extractPrNumberFromUrl(prUrl);
  // PR state normalization
  const prState = normalizePullRequestState(data);
  // Determine if the PR link is a valid/trusted one
  const prValid = prUrl ? isValidGithubPullRequestUrl(prUrl) : false;
  const agentFlow = normalizeAgentFlowMeta(data);

  return {
    summary: strOrNull(data.summary),
    changedFiles:         strArray(data.changedFiles),
    implementedChanges:   strArray(data.implementedChanges),
    tests: strOrNull(data.tests),
    buildStatus: strOrNull(data.buildStatus),
    openItems: strArray(data.openItems),
    requiresUserApproval: data.requiresUserApproval === true,
    pullRequestUrl: prUrl,
    pullRequestNumber: prNumber,
    pullRequestState: prState,
    pullRequestValid: prValid,
    mergedToMain: data.mergedToMain === true,
    targetOwner: strOrNull(data.targetOwner),
    targetRepo: strOrNull(data.targetRepo),
    targetBranch: strOrNull(data.targetBranch),
    repositoryValid: typeof data.repositoryValid === "boolean" ? data.repositoryValid : null,
    errorCode: agentFlow.errorCode,
    nextRequiredStep: agentFlow.nextRequiredStep,
    nextRequiredStepText: agentFlow.nextRequiredStepText,
    agentFlow,
    canonicalRoute: agentFlow.canonicalRoute,
    isCanonical: agentFlow.isCanonical,
    legacyRouteUsed: agentFlow.legacyRouteUsed,
    routeType: agentFlow.routeType,
    flowType: agentFlow.flowType,
    executionPath: agentFlow.executionPath,
    sourceStage: agentFlow.sourceStage,
    sourceRoute: agentFlow.sourceRoute,
    route: agentFlow.route,
    endpoint: agentFlow.endpoint,
    routeLabel: agentFlow.routeLabel,
    blockReason: agentFlow.blockReason,
    blockReasonText: agentFlow.blockReasonText,
    safetyGateStatus: agentFlow.safetyGateStatus,
    safetyGateActive: agentFlow.safetyGateActive,
    requiresApproval: agentFlow.requiresApproval,
  };
}

// ── Natural Handoff – Context Extraction V1 ────────────────────────────────

/** Status text shown when the previous agent proposal was found. */
export const NATURAL_HANDOFF_PROPOSAL_DETECTED_TEXT = "Vorheriger Vorschlag erkannt";

/** Status text shown while the natural handoff is being prepared. */
export const NATURAL_HANDOFF_PREPARING_TEXT = "Übergabe wird vorbereitet";

/** Label prefix for the target agent shown in the UI status. */
export const NATURAL_HANDOFF_TARGET_LABEL = "Ziel-Agent";

/** Label prefix for the task ID shown in the UI status. */
export const NATURAL_HANDOFF_TASK_ID_LABEL = "Task-ID";

/**
 * Error text shown when a natural handoff command was detected but no
 * actionable prior proposal was found in the conversation.
 */
export const NATURAL_HANDOFF_NO_PROPOSAL_TEXT =
  "Kein umsetzbarer Vorschlag gefunden. Lass den Agenten zuerst einen Vorschlag erstellen.";

/**
 * Error text shown when "Freigegebenes ausführen" is clicked but no
 * actionable prior proposal exists in the conversation.
 */
export const FREIGEGEBENES_EXECUTE_NO_PROPOSAL_TEXT =
  "Kein Vorschlag zum Ausführen vorhanden. Bitte erst einen Vorschlag erstellen lassen.";

// ── Actionable Proposal Detection V1 ─────────────────────────────────────────

/** Status text shown below an agent reply that contains actionable content. */
export const ACTIONABLE_PROPOSAL_DETECTED_TEXT = "Umsetzbarer Vorschlag erkannt";

/** Status text indicating the user may approve and execute the proposal. */
export const ACTIONABLE_PROPOSAL_RELEASE_POSSIBLE_TEXT = "Freigabe möglich";

/**
 * SafetyGate hint shown when the frontend detects an actionable proposal but
 * the backend has not yet provided a real safety verdict.
 */
export const ACTIONABLE_PROPOSAL_SAFETY_GATE_PENDING_TEXT =
  "SafetyGate wird bei Übergabe geprüft";

// ── Follow-up Context UX V1 ────────────────────────────────────────────────

/** Shown when backend confirms the follow-up was resolved against the last agent reply. */
export const FOLLOW_UP_RESOLVED_TEXT = "Bezug erkannt: letzte Claude-Antwort";

/** Prefix for the selected-improvement status line. */
export const FOLLOW_UP_IMPROVEMENT_PREFIX_TEXT = "Gestartet mit:";

/** Backend error code sent when no prior actionable proposal exists. */
export const NO_PREVIOUS_ACTIONABLE_PROPOSAL_ERROR_CODE = "no_previous_actionable_proposal";

/**
 * Guidance text shown when the backend signals no prior proposal was found.
 * Replaces the confusing red "Ich brauche zuerst…" backend text.
 */
export const NO_PREVIOUS_PROPOSAL_GUIDANCE_TEXT =
  "Ich finde noch keinen vorherigen umsetzbaren Vorschlag. Bitte frage zuerst z.\u00a0B.: \u201eWas muss verbessert werden?\u201c oder nenne direkt eine Aufgabe.";

/**
 * Quick actions shown in the composer after the agent delivers an actionable
 * proposal (post-proposal mode).  The list is intentionally compact so the
 * composer stays mobile-friendly.
 */
export const POST_PROPOSAL_QUICK_ACTIONS = [
  "Punkt 1 umsetzen",
  "PR vorbereiten",
  "Risiko prüfen",
  "Tests ergänzen",
  "Alles als Plan strukturieren",
];

/**
 * Builds a compact one-line follow-up context hint for the UI.
 * Returns null when neither signal is present.
 *
 * @param {boolean} followUpResolved  true when backend resolved the follow-up
 * @param {string|null} selectedImprovement  label of the resolved improvement
 * @returns {string|null}
 */
export function buildFollowUpContextHint({
  followUpResolved = false,
  selectedImprovement = null,
  selectedIndexes = [],
  selectedImprovementText = null,
} = {}) {
  const improvementLabel = resolveSelectedImprovementLabel(
    selectedImprovement,
    selectedImprovementText,
    normalizeSelectedIndexes(selectedIndexes),
  );
  if (!followUpResolved && !improvementLabel) return null;
  const parts = [];
  if (followUpResolved) parts.push(FOLLOW_UP_RESOLVED_TEXT);
  if (improvementLabel) parts.push(`${FOLLOW_UP_IMPROVEMENT_PREFIX_TEXT} ${improvementLabel}`);
  return parts.join(" · ");
}

export function buildFollowUpHintText(followUpResolved, selectedImprovement) {
  return buildFollowUpContextHint({ followUpResolved, selectedImprovement });
}

/**
 * Returns true when an agent reply text contains patterns that indicate a
 * concrete, actionable analysis or proposal that can be forwarded to the
 * Dispatcher / Qwen for execution.
 *
 * Matches German and English patterns such as:
 *   – "Offene UX-Probleme"
 *   – "Produkt-/Funktionsfehler"
 *   – "stale" / "empty"
 *   – "provider_error"
 *   – "muss getestet werden"
 *   – "sollte reduziert werden"
 *   – "beeinträchtigt"
 *   – "v0→Qwen Handoff" style items
 *   – bullet-point lists with 2+ concrete action items
 *
 * @param {string} text  raw agent reply text
 * @returns {boolean}
 */
export function isActionableAgentReply(text) {
  if (typeof text !== "string") return false;
  const t = text.trim().toLowerCase();
  // Too short to be a substantive proposal.
  if (t.length < 30) return false;
  const patterns = [
    // UX / product issues explicitly listed
    /offene\s+ux.{0,4}probleme/,
    /produkt.{0,4}funktionsfehler/,
    /offene\s+probleme/,
    // Common staleness / emptiness markers
    /\bstale\b/,
    /\bempty\b/,
    // Provider errors
    /provider[_\s]?error/,
    /\bprovider\b.{0,20}\bfehler\b/,
    // Action-required German phrases
    /muss\s+getestet\s+werden/,
    /sollte\s+reduziert\s+werden/,
    /beeintr[äa]chtigt/,
    // Handoff items
    /v0\s*[→\-–>]+\s*qwen/,
    /handoff.*end.{0,4}to.{0,4}end/,
    // Portfolio / guardian staleness
    /portfolio\s+stale/,
    /guardian\s+stale/,
    // Forecasts issue
    /forecasts?\s+empty/,
    // Gemini offline / provider
    /gemini\s+provider/,
    /gemini\s+offline/,
    // Mobile / agent overview issues
    /mobile\s+agenten[üu]bersicht/,
    /agenten[üu]bersicht\s+beruhig/,
    // Numbered or bulleted list with 2+ items (indicates a structured proposal)
    /(?:^|\n)\s*(?:\d+\.|[-•*])\s+.{10,}\n\s*(?:\d+\.|[-•*])\s+/,
  ];
  return patterns.some((re) => re.test(t));
}

export function resolveActionableProposalFollowUpContext(message, proposal) {
  if (!proposal || typeof proposal !== "object" || proposal.isActionable !== true) return null;
  if (!isActionableProposalFollowUpText(message) && !isImplementationHandoffTriggerText(message)) return null;
  const proposalText = proposal.fullText ?? proposal.text ?? null;
  const numberedItems = extractNumberedImprovementItems(proposalText);
  const selectedIndexes = extractSelectedImprovementIndexes(message);
  let selectedImprovement = normalizeSelectedImprovement(proposal.selectedImprovement);
  let selectedImprovementText =
    typeof proposal.selectedImprovementText === "string" && proposal.selectedImprovementText.trim()
      ? proposal.selectedImprovementText.trim()
      : null;
  if (selectedIndexes.length > 0) {
    const matchedItems = numberedItems.filter((item) => selectedIndexes.includes(item.index));
    if (matchedItems.length > 0) {
      selectedImprovement = {
        index: matchedItems[0].index,
        title: matchedItems[0].title,
      };
      selectedImprovementText = matchedItems.map((item) => item.text).join("\n\n");
    } else if (!selectedImprovement) {
      selectedImprovement = {
        index: selectedIndexes[0],
        title: `Punkt ${selectedIndexes[0]}`,
      };
    }
  } else if (!selectedImprovement && numberedItems.length > 0) {
    selectedImprovement = {
      index: numberedItems[0].index,
      title: numberedItems[0].title,
    };
    selectedImprovementText = numberedItems[0].text;
  } else if (!selectedImprovementText && proposalText) {
    selectedImprovementText = proposalText;
  }
  return {
    followUpResolved: true,
    selectedImprovement,
    selectedIndexes,
    selectedImprovementText,
    actionableProposal: proposal.actionableProposal ?? true,
  };
}

/**
 * Extract the last actionable agent proposal from a message array.
 *
 * Scans messages from the end to find the most recent agent text message that
 * contains one of:
 *   – uiChangeset (structured v0 changeset, highest priority)
 *   – designProposal (design proposal card)
 *   – repairPlan (sentinel repair plan)
 *   – freetext (any substantial agent answer, used as fallback)
 *
 * Returns:
 *   { type, uiChangeset?, designProposal?, repairPlan?, text, fullText, agentName, messageId }
 * or null when no suitable message is found.
 *
 * @param {Array} messages
 * @returns {{ type: string, uiChangeset?: object|null, designProposal?: object|null, repairPlan?: object|null, text: string|null, fullText: string|null, agentName: string|null, messageId: string|null }|null}
 */
function buildProposalCandidateFromMessage(msg) {
  if (!msg || msg.role !== "agent" || msg.kind !== "text") return null;
  const rawUiChangeset =
    (msg.raw?.uiChangeset && typeof msg.raw.uiChangeset === "object" && !Array.isArray(msg.raw.uiChangeset))
      ? msg.raw.uiChangeset
      : (msg.uiChangeset && typeof msg.uiChangeset === "object" && !Array.isArray(msg.uiChangeset))
        ? msg.uiChangeset
        : null;
  const rawDesignProposal =
    (msg.designProposal && typeof msg.designProposal === "object" && !Array.isArray(msg.designProposal))
      ? msg.designProposal
      : (msg.raw?.designProposal && typeof msg.raw.designProposal === "object" && !Array.isArray(msg.raw.designProposal))
        ? msg.raw.designProposal
        : null;
  const rawRepairPlan =
    (msg.repairPlan && typeof msg.repairPlan === "object" && !Array.isArray(msg.repairPlan))
      ? msg.repairPlan
      : (msg.raw?.repairPlan && typeof msg.raw.repairPlan === "object" && !Array.isArray(msg.raw.repairPlan))
        ? msg.raw.repairPlan
        : null;
  const text = typeof msg.text === "string" && msg.text.trim() ? msg.text : null;
  const fullText = typeof msg.fullText === "string" && msg.fullText.trim() ? msg.fullText : null;
  const selectedImprovement = normalizeSelectedImprovement(
    msg.selectedImprovement ?? msg.raw?.selectedImprovement ?? null,
  );
  const selectedIndexes = normalizeSelectedIndexes(
    Array.isArray(msg.selectedIndexes) ? msg.selectedIndexes : msg.raw?.selectedIndexes,
  );
  const selectedImprovementText =
    typeof msg.selectedImprovementText === "string" && msg.selectedImprovementText.trim()
      ? msg.selectedImprovementText.trim()
      : typeof msg.raw?.selectedImprovementText === "string" && msg.raw.selectedImprovementText.trim()
        ? msg.raw.selectedImprovementText.trim()
        : null;
  const actionableProposal =
    msg.actionableProposal !== undefined ? msg.actionableProposal :
    msg.raw?.actionableProposal !== undefined ? msg.raw.actionableProposal :
    null;
  const requiresApproval = msg.requiresApproval === true || msg.raw?.requiresApproval === true;
  const followUpResolved = msg.followUpResolved === true || msg.raw?.followUpResolved === true;
  const candidate = {
    type: rawUiChangeset ? "uiChangeset" : rawDesignProposal ? "designProposal" : rawRepairPlan ? "repairPlan" : "text",
    uiChangeset: rawUiChangeset,
    designProposal: rawDesignProposal,
    repairPlan: rawRepairPlan,
    text,
    fullText,
    agentName: typeof msg.agent === "string" && msg.agent ? msg.agent : null,
    messageId: typeof msg.id === "string" && msg.id ? msg.id : null,
    followUpResolved,
    selectedImprovement,
    selectedIndexes,
    selectedImprovementText,
    actionableProposal,
    requiresApproval,
  };
  candidate.isActionable = Boolean(
    rawUiChangeset
    || rawDesignProposal
    || rawRepairPlan
    || followUpResolved
    || selectedImprovement
    || selectedIndexes.length > 0
    || selectedImprovementText
    || requiresApproval
    || actionableProposal
    || isActionableAgentReply(fullText || text || "")
    || extractNumberedImprovementItems(fullText || text || "").length > 0
  );
  return candidate;
}

export function extractLastAgentProposal(messages) {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const candidate = buildProposalCandidateFromMessage(messages[i]);
    if (!candidate) continue;
    if (candidate.type !== "text" || (candidate.text && candidate.text.trim().length > 20)) {
      return candidate;
    }
  }
  return null;
}

export function extractLastActionableAgentProposal(messages) {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const candidate = buildProposalCandidateFromMessage(messages[i]);
    if (candidate?.isActionable) return candidate;
  }
  return null;
}

/**
 * Build a clean conversation tail from the message array, trimmed to the last
 * `maxCount` meaningful messages.  System status and handoff-card messages are
 * excluded so the payload stays concise.
 *
 * @param {Array} messages
 * @param {number} [maxCount=8]
 * @returns {Array<{ role: string, agent: string|null, text: string|null, kind: string }>}
 */
export function buildConversationTail(messages, maxCount = 8) {
  if (!Array.isArray(messages)) return [];
  const filtered = messages.filter((m) => {
    if (!m || typeof m !== "object") return false;
    // Exclude internal system/status bubbles that carry no conversation context
    if (m.role === "agent" && m.kind === "status") return false;
    if (m.kind === "error") return false;
    if (m.kind === "handoff_started" || m.kind === "handoff_completed") return false;
    return true;
  });
  return filtered.slice(-maxCount).map((m) => ({
    role: m.role ?? "agent",
    agent: m.agent ?? null,
    text: m.text ?? null,
    kind: m.kind ?? "text",
  }));
}

export const CHAT_CONTEXT_TAIL_MAX_MESSAGES = 12;
export const CHAT_CONTEXT_TEXT_MAX_CHARS = 1600;
export const CHAT_TOPIC_CONTINUITY_SAME = "same_topic";
export const CHAT_TOPIC_CONTINUITY_NEW = "new_topic";

/**
 * Decide whether a chat turn should continue the current topic or start a new
 * one. The default with prior context is intentionally "same_topic" so normal
 * chat behaves like ChatGPT/Gemini: short follow-ups such as "mach das" or
 * "was meinst du damit" keep using the visible conversation unless the user
 * explicitly asks for a new topic.
 *
 * @param {string} message
 * @param {boolean} hasPriorContext
 * @returns {{ topicContinuity: string, shouldContinueCurrentTopic: boolean, isExplicitTopicChange: boolean, reason: string }}
 */
export function classifyChatTopicContinuity(message, hasPriorContext = false) {
  const t = typeof message === "string" ? message.trim().toLowerCase() : "";
  if (!hasPriorContext) {
    return {
      topicContinuity: CHAT_TOPIC_CONTINUITY_NEW,
      shouldContinueCurrentTopic: false,
      isExplicitTopicChange: false,
      reason: "no_prior_context",
    };
  }

  const explicitTopicChange = [
    /\bneues?\s+thema\b/,
    /\banderes?\s+thema\b/,
    /\bthema\s+wechseln\b/,
    /\bwechseln\s+wir\s+(?:das\s+)?thema\b/,
    /\bunabhängig\s+davon\b/,
    /\bseparate?s?\s+thema\b/,
  ].some((re) => re.test(t));
  if (explicitTopicChange) {
    return {
      topicContinuity: CHAT_TOPIC_CONTINUITY_NEW,
      shouldContinueCurrentTopic: false,
      isExplicitTopicChange: true,
      reason: "explicit_topic_change",
    };
  }

  const followUpReference = [
    /\b(?:das|dies(?:es|en|er)?|es|ihn|sie|oben|eben|vorher|gerade|weiter|nochmal|noch\s+mal|damit|daran|daraus|dazu)\b/,
    /\b(?:setze?|mach|baue?|behebe|fixe?|löse?|loese?|repariere?)\b/,
    /\b(?:kontext|gespräch|chat|antwort|vorschlag|fazit|patch|freigabe|freigegeben\w*)\b/,
    /^(?:und|aber|also|ok|okay|ja|nein)\b/,
  ].some((re) => re.test(t));

  return {
    topicContinuity: CHAT_TOPIC_CONTINUITY_SAME,
    shouldContinueCurrentTopic: true,
    isExplicitTopicChange: false,
    reason: followUpReference ? "follow_up_reference" : "default_prior_context",
  };
}

function trimChatContextText(text, maxLength = CHAT_CONTEXT_TEXT_MAX_CHARS) {
  if (typeof text !== "string") return null;
  const trimmed = text.trim();
  if (!trimmed) return null;
  return trimmed.length > maxLength ? `${trimmed.slice(0, maxLength)}…` : trimmed;
}

function normalizeSelectedImprovementIndex(value) {
  const n = typeof value === "number" ? value : Number.parseInt(value, 10);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function normalizeSelectedIndexes(value) {
  if (!Array.isArray(value)) return [];
  return [...new Set(
    value
      .map((entry) => normalizeSelectedImprovementIndex(entry))
      .filter((entry) => entry !== null),
  )].sort((a, b) => a - b);
}

function normalizeSelectedImprovement(value) {
  if (!value) return null;
  if (typeof value === "string" && value.trim()) {
    return {
      index: null,
      title: value.trim().slice(0, 200),
    };
  }
  if (typeof value !== "object" || Array.isArray(value)) return null;
  const title = pickFirstNonEmptyString(value, ["title", "label", "name", "text", "summary", "message"]);
  const index = normalizeSelectedImprovementIndex(
    value.index ?? value.number ?? value.point ?? value.selectedIndex ?? value.selected_index,
  );
  if (!title && index === null) return null;
  return {
    index,
    title: title ? title.slice(0, 200) : (index !== null ? `Punkt ${index}` : null),
  };
}

function resolveSelectedImprovementLabel(selectedImprovement, selectedImprovementText = null, selectedIndexes = []) {
  const normalized = normalizeSelectedImprovement(selectedImprovement);
  if (normalized?.title) return normalized.title;
  if (normalized && normalized.index !== null) return `Punkt ${normalized.index}`;
  if (selectedIndexes.length > 1) return `Punkte ${selectedIndexes.join(", ")}`;
  if (selectedIndexes.length === 1) return `Punkt ${selectedIndexes[0]}`;
  if (typeof selectedImprovementText === "string" && selectedImprovementText.trim()) {
    return selectedImprovementText.trim().split("\n")[0].slice(0, 200);
  }
  return null;
}

export function extractNumberedImprovementItems(text) {
  if (typeof text !== "string") return [];
  const matches = [...text.matchAll(/(?:^|\n)\s*(\d+)[.)]\s+(.+?)(?=(?:\n\s*\d+[.)]\s+)|$)/gs)];
  return matches
    .map((match) => {
      const index = normalizeSelectedImprovementIndex(match[1]);
      const body = typeof match[2] === "string" ? match[2].trim() : "";
      if (index === null || !body) return null;
      return {
        index,
        title: body.split("\n")[0].trim().slice(0, 200),
        text: body,
      };
    })
    .filter(Boolean);
}

export function extractSelectedImprovementIndexes(text) {
  if (typeof text !== "string") return [];
  const t = text.trim().toLowerCase();
  if (!t) return [];
  const explicit = [...t.matchAll(/\bpunkt\s*(\d+)\b/g)]
    .map((match) => normalizeSelectedImprovementIndex(match[1]))
    .filter((match) => match !== null);
  if (explicit.length > 0) return [...new Set(explicit)];
  if (!/\b(?:umsetzen|umsetze|setz(?:e|t)?|mach|mache|behebe|verbessere|pr|pull\s*request|freigabe|freigegeben|fang)\b/.test(t)) {
    return [];
  }
  const groupedDigits = t.match(/\b\d+(?:\s*(?:,|und|&)\s*\d+)+\b/);
  if (!groupedDigits) return [];
  return [...new Set(
    groupedDigits[0]
      .match(/\d+/g)
      ?.map((entry) => normalizeSelectedImprovementIndex(entry))
      .filter((entry) => entry !== null) || [],
  )];
}

export function isActionableProposalFollowUpText(text) {
  if (typeof text !== "string") return false;
  const t = text.trim().toLowerCase();
  if (!t) return false;
  return [
    /^(?:behebe|verbessere|mach)\s+das\b/,
    /^(?:setze|setz)\s+das\s+um\b/,
    /^(?:setze|setz|mach)\s+punkt\s*\d+\b/,
    /^punkt\s*\d+\b.*(?:bitte|umsetzen|mach|setzen)?/,
    /^\d+(?:\s*(?:,|und|&)\s*\d+)+\b.*\bumsetzen\b/,
    /\b(?:erstelle|mach)\s+daraus\s+einen?\s+pr\b/,
    /\bpr\s+vorbereiten\b/,
    /^\s*freigegeben\s*[.!]?\s*$/,
    /\bich\s+gebe\s+dir\s+die\s+freigabe\b/,
    /\bfang\s+damit\s+an\b/,
  ].some((re) => re.test(t));
}

/**
 * Build a compact context snapshot for normal chat turns.
 *
 * Unlike Natural Handoff, this is attached to every free chat / quick-action
 * turn so the backend can resolve follow-up references like "das", "wie eben"
 * or "setz es um" without asking the user to repeat the prior answer.
 *
 * @param {Array} messages
 * @param {{ agentName?: string, message?: string, maxCount?: number }} [options]
 * @returns {object|null}
 */
export function buildChatContextSnapshot(messages, options = {}) {
  const maxCount = typeof options.maxCount === "number" && options.maxCount > 0
    ? Math.min(options.maxCount, CHAT_CONTEXT_TAIL_MAX_MESSAGES)
    : CHAT_CONTEXT_TAIL_MAX_MESSAGES;
  const previousMessages = buildConversationTail(messages, maxCount)
    .map((entry) => ({
      ...entry,
      text: trimChatContextText(entry.text),
    }))
    .filter((entry) => entry.text || entry.kind !== "text");

  const lastAgentProposal = extractLastAgentProposal(messages);
  const lastUserMessage = [...previousMessages].reverse().find((entry) => entry.role === "user") ?? null;
  const lastAgentMessage = lastAgentProposal
    ? {
        agent: lastAgentProposal.agentName ?? null,
        text: trimChatContextText(lastAgentProposal.fullText ?? lastAgentProposal.text),
        kind: lastAgentProposal.type ?? "text",
        messageId: lastAgentProposal.messageId ?? null,
      }
    : [...previousMessages].reverse().find((entry) => entry.role === "agent") ?? null;

  const currentMessage = trimChatContextText(options.message);
  const hasContext = previousMessages.length > 0 || Boolean(lastAgentMessage);
  const continuity = classifyChatTopicContinuity(currentMessage, hasContext);
  if (!hasContext && !currentMessage) return null;

  return {
    schemaVersion: 1,
    kind: "chat_context_snapshot",
    agentName: typeof options.agentName === "string" && options.agentName ? options.agentName : null,
    currentMessage,
    previousMessages,
    lastUserMessage,
    lastAgentMessage,
    hasPriorContext: hasContext,
    hasActionableProposal: Boolean(lastAgentProposal),
    actionableProposalType: lastAgentProposal?.type ?? null,
    topicContinuity: continuity.topicContinuity,
    shouldContinueCurrentTopic: continuity.shouldContinueCurrentTopic,
    isExplicitTopicChange: continuity.isExplicitTopicChange,
    continuityReason: continuity.reason,
    instruction: continuity.shouldContinueCurrentTopic
      ? "Behandle diese Nachricht als Fortsetzung des bestehenden Chats. Nutze previousMessages und lastAgentMessage als Gesprächskontext für Folgefragen und Umsetzungswünsche."
      : "Behandle diese Nachricht als neues Thema, aber nutze previousMessages nur falls der Nutzer ausdrücklich darauf verweist.",
  };
}

/**
 * Resolve the target agent for a natural handoff from the user's message text.
 *
 * Recognises agent names mentioned inline, e.g.:
 *   "Setze das mit Qwen um" → "qwen"
 *   "Sven soll das bauen"   → "qwen"   (Sven is the Qwen alias)
 *   "Übergib das an Gemini" → "gemini"
 *
 * Defaults to "qwen" (implementation agent) when no specific agent is found.
 *
 * @param {string} text
 * @returns {string}  lowercase agent id
 */
export function resolveNaturalHandoffTarget(text) {
  if (typeof text !== "string") return "qwen";
  const t = text.trim().toLowerCase();
  if (/\bgemini\b/.test(t)) return "gemini";
  if (/\bdeepseek\b/.test(t)) return "deepseek";
  if (/\bclaude\b/.test(t)) return "claude";
  if (/\bopenai\b/.test(t)) return "openai";
  // Sven is the human-readable alias for Qwen in the UI
  if (/\bsven\b/.test(t) || /\bqwen\b/.test(t)) return "qwen";
  return "qwen";
}

// ── Autonomy Foundation V1 ─────────────────────────────────────────────────

/**
 * Human-readable German labels for autonomy run statuses.
 * Maps backend status values to user-facing text.
 */
export const AUTONOMY_RUN_STATUS_LABELS_DE = {
  queued:                "Auftrag wartet",
  running:              "Agent arbeitet",
  waiting_for_approval: "Freigabe erforderlich",
  blocked:              "Durch Sicherheitsregel blockiert",
  completed:            "Abgeschlossen",
  failed:               "Fehlgeschlagen",
  resumed:              "Wieder aufgenommen",
};

/**
 * Human-readable German labels for autonomy levels.
 */
export const AUTONOMY_LEVEL_LABELS_DE = {
  0: "Nur Chat",
  1: "Analyse",
  2: "Vorschlag",
  3: "Übergabe",
  4: "Co-Pilot / PR",
  5: "Sentinel / Diagnose",
};

/**
 * Safety status labels mapped to German UI text.
 */
export const AUTONOMY_SAFETY_STATUS_LABELS_DE = {
  active:           "Sicherheitsprüfung aktiv",
  approval_needed:  "Freigabe erforderlich",
  blocked:          "Merge/Deploy/DB-Änderung blockiert",
  patch_allowed:    "Patch-Vorbereitung erlaubt",
};

/**
 * Error codes from backend mapped to German user-facing descriptions.
 */
export const AUTONOMY_ERROR_LABELS_DE = {
  invalid_input:       "Eingabedaten ungültig",
  no_proposal:         "Kein umsetzbarer Vorschlag gefunden",
  agent_unavailable:   "Ziel-Agent nicht verfügbar",
  safety_blocked:      "Sicherheitsregel blockiert",
  task_start_failed:   "Auftrag konnte nicht gestartet werden",
  provider_error:      "KI-/Providerfehler",
};

/** Checkpoint / resume labels. */
export const AUTONOMY_CHECKPOINT_SAVED_TEXT = "Zwischenstand gespeichert";
export const AUTONOMY_CAN_RESUME_TEXT = "Task kann fortgesetzt werden";
export const AUTONOMY_RESUME_BUTTON_LABEL = "Fortsetzen";

/** Card title for the AutonomyRun card. */
export const AUTONOMY_RUN_CARD_TITLE = "Autonomer Lauf";

/**
 * Normalise an autonomy run payload from the backend.
 *
 * Accepts objects that contain at least `autonomyRunId` or `taskId` plus
 * a `status` field. Returns a stable shape for the UI card.
 *
 * Returns null if data is not a valid autonomy-run payload.
 *
 * @param {any} data
 * @returns {object|null}
 */
export function normalizeAutonomyRun(data) {
  if (!data || typeof data !== "object") return null;
  const autonomyRunId = typeof data.autonomyRunId === "string" && data.autonomyRunId.trim()
    ? data.autonomyRunId.trim()
    : null;
  const taskId = typeof data.taskId === "string" && data.taskId.trim()
    ? data.taskId.trim()
    : null;
  if (!autonomyRunId && !taskId) return null;
  const rawStatus = typeof data.status === "string" ? data.status.trim().toLowerCase().replace(/[\s-]/g, "_") : "";
  const status = AUTONOMY_RUN_STATUS_LABELS_DE[rawStatus] ? rawStatus : "running";
  const autonomyLevel = typeof data.autonomyLevel === "number" && data.autonomyLevel >= 0 && data.autonomyLevel <= 5
    ? data.autonomyLevel
    : null;
  return {
    autonomyRunId,
    taskId,
    status,
    currentStep: typeof data.currentStep === "string" && data.currentStep.trim()
      ? data.currentStep.trim()
      : null,
    autonomyLevel,
    mode: typeof data.mode === "string" && data.mode.trim()
      ? data.mode.trim()
      : null,
    safetyStatus: typeof data.safetyStatus === "string" && data.safetyStatus.trim()
      ? data.safetyStatus.trim().toLowerCase().replace(/[\s-]/g, "_")
      : null,
    requiresUserApproval: data.requiresUserApproval === true,
    checkpointAvailable: data.checkpointAvailable === true,
    canResume: data.canResume === true,
    targetAgent: typeof data.targetAgent === "string" && data.targetAgent.trim()
      ? data.targetAgent.trim().toLowerCase()
      : null,
    sourceAgent: typeof data.sourceAgent === "string" && data.sourceAgent.trim()
      ? data.sourceAgent.trim().toLowerCase()
      : null,
    errorCode: typeof data.errorCode === "string" && data.errorCode.trim()
      ? data.errorCode.trim().toLowerCase().replace(/[\s-]/g, "_")
      : null,
    errorMessage: typeof data.errorMessage === "string" && data.errorMessage.trim()
      ? data.errorMessage.trim()
      : null,
  };
}

/**
 * Build the human-readable status line for an autonomy run card.
 *
 * @param {object} run – result of normalizeAutonomyRun
 * @returns {string}
 */
export function buildAutonomyRunStatusLine(run) {
  if (!run || typeof run !== "object") return "";
  return AUTONOMY_RUN_STATUS_LABELS_DE[run.status] || "Agent arbeitet";
}

/**
 * Build a compact autonomy-level badge text.
 *
 * @param {number|null} level
 * @returns {string|null}
 */
export function buildAutonomyLevelBadge(level) {
  if (level == null || typeof level !== "number") return null;
  const label = AUTONOMY_LEVEL_LABELS_DE[level];
  if (!label) return null;
  return `Level ${level}: ${label}`;
}

/**
 * Build safety status line for the autonomy run card.
 *
 * @param {string|null} safetyStatus
 * @param {boolean} requiresUserApproval
 * @returns {string|null}
 */
export function buildAutonomySafetyLine(safetyStatus, requiresUserApproval) {
  if (requiresUserApproval) return AUTONOMY_SAFETY_STATUS_LABELS_DE.approval_needed;
  if (safetyStatus && AUTONOMY_SAFETY_STATUS_LABELS_DE[safetyStatus]) {
    return AUTONOMY_SAFETY_STATUS_LABELS_DE[safetyStatus];
  }
  return null;
}

/**
 * Build error text for autonomy run from an error code.
 *
 * @param {string|null} errorCode
 * @returns {string|null}
 */
export function buildAutonomyErrorText(errorCode) {
  if (!errorCode || typeof errorCode !== "string") return null;
  const key = errorCode.trim().toLowerCase().replace(/[\s-]/g, "_");
  return AUTONOMY_ERROR_LABELS_DE[key] || null;
}

/**
 * Determine if a message payload represents an autonomy run card.
 *
 * @param {any} raw – raw message data from backend
 * @returns {boolean}
 */
export function isAutonomyRunPayload(raw) {
  if (!raw || typeof raw !== "object") return false;
  return Boolean(
    (typeof raw.autonomyRunId === "string" && raw.autonomyRunId.trim()) ||
    (raw.kind === "autonomy_run")
  );
}

// ── Autonomous Task Dispatcher UI V1 ────────────────────────────────────────

/** Card title for dispatcher status cards. */
export const AGENT_DISPATCH_CARD_TITLE = "Agentenlauf";

/** Small badge text shown when a dispatcher run was started. */
export const AGENT_DISPATCH_STARTED_TEXT = "Auftrag wurde gestartet";

/** Primary button label for approval-gated runs. */
export const AGENT_DISPATCH_APPROVE_BUTTON_TEXT = "Freigeben";

/** Honest API error text when dispatcher polling is unreachable. */
export const AGENT_DISPATCH_API_ERROR_TEXT =
  "Dispatcher-Status ist gerade nicht erreichbar.";

/** Polling timeout text shown after the UI stops auto-refreshing. */
export const AGENT_DISPATCH_POLL_TIMEOUT_TEXT =
  "Statusprüfung pausiert. Bitte später erneut öffnen.";

/** German labels for dispatcher statuses. */
export const AGENT_DISPATCH_STATUS_LABELS_DE = {
  queued:                "Warteschlange",
  running:               "Läuft",
  resumed:               "Fortgesetzt",
  waiting_for_approval:  "Freigabe erforderlich",
  completed:             "Abgeschlossen",
  failed:                "Fehlgeschlagen",
  blocked:               "Blockiert",
  timeout:               "Polling pausiert",
};

/** German labels for dispatcher safety states. */
export const AGENT_DISPATCH_SAFETY_STATUS_LABELS_DE = {
  active:             "SafetyGate aktiv",
  approval_needed:    "Freigabe erforderlich",
  requires_approval:  "Freigabe erforderlich",
  blocked:            "Kritische Aktion blockiert",
  patch_allowed:      "Patch erlaubt",
  allowed:            "Erlaubt",
};

/** German labels for dispatcher error codes. */
export const AGENT_DISPATCH_ERROR_LABELS_DE = {
  invalid_input:       "Eingabe ungültig",
  no_proposal:         "Kein umsetzbarer Auftrag gefunden",
  agent_unavailable:   "Ziel-Agent nicht verfügbar",
  safety_blocked:      "SafetyGate blockiert die Aktion",
  task_start_failed:   "Auftrag konnte nicht gestartet werden",
  provider_error:      "KI-/Providerfehler",
  approval_failed:     "Freigabe konnte nicht verarbeitet werden",
  dispatcher_unavailable: "Dispatcher nicht erreichbar",
};

const AGENT_DISPATCH_ACTIVE_STATUSES = new Set([
  "queued",
  "running",
  "resumed",
  "waiting_for_approval",
]);

function normalizeDispatchStatus(value, dispatched) {
  if (typeof value === "string" && value.trim()) {
    const key = value.trim().toLowerCase().replace(/[\s-]/g, "_");
    if (AGENT_DISPATCH_STATUS_LABELS_DE[key]) return key;
  }
  return dispatched ? "queued" : "running";
}

function normalizeDispatchSafety(value, status, requiresApproval) {
  if (requiresApproval || status === "waiting_for_approval") return "approval_needed";
  if (typeof value !== "string" || !value.trim()) {
    return status === "blocked" ? "blocked" : null;
  }
  const key = value.trim().toLowerCase().replace(/[\s-]/g, "_");
  if (AGENT_DISPATCH_SAFETY_STATUS_LABELS_DE[key]) return key;
  if (key === "requiresapproval") return "approval_needed";
  return key;
}

function normalizeDispatchTaskType(value) {
  if (typeof value !== "string" || !value.trim()) return null;
  return value.trim().toLowerCase().replace(/[\s-]/g, "_");
}

/**
 * Normalize a dispatcher payload from the backend into a stable UI shape.
 *
 * Recognized fields:
 * dispatched, autonomyRunId, taskId, targetAgent, taskType, action, status,
 * currentStep, safetyStatus, requiresApproval, pollUrl, errorCode, summaryText.
 *
 * @param {any} data
 * @returns {object|null}
 */
export function normalizeAutonomousDispatchRun(data) {
  if (!data || typeof data !== "object") return null;

  const autonomyRunId =
    (typeof data.autonomyRunId === "string" && data.autonomyRunId.trim()) ||
    (typeof data.autonomy_run_id === "string" && data.autonomy_run_id.trim()) ||
    null;
  const taskId =
    (typeof data.taskId === "string" && data.taskId.trim()) ||
    (typeof data.task_id === "string" && data.task_id.trim()) ||
    null;
  const pollUrl =
    (typeof data.pollUrl === "string" && data.pollUrl.trim()) ||
    (typeof data.poll_url === "string" && data.poll_url.trim()) ||
    null;
  const dispatched =
    data.dispatched === true ||
    data.dispatchStarted === true ||
    data.dispatch_started === true ||
    Boolean(autonomyRunId || taskId || pollUrl);

  if (!dispatched && !autonomyRunId && !taskId && !pollUrl && data.kind !== "agent_dispatch") {
    return null;
  }

  const requiresApproval =
    data.requiresApproval === true ||
    data.requires_approval === true ||
    data.requiresUserApproval === true ||
    data.requires_user_approval === true;
  const status = normalizeDispatchStatus(
    data.status ?? data.dispatchStatus ?? data.dispatch_status ?? null,
    dispatched,
  );
  const safetyStatus = normalizeDispatchSafety(
    data.safetyStatus ?? data.safety_status ?? null,
    status,
    requiresApproval,
  );

  return {
    dispatched,
    autonomyRunId,
    taskId,
    targetAgent:
      (typeof data.targetAgent === "string" && data.targetAgent.trim().toLowerCase()) ||
      (typeof data.target_agent === "string" && data.target_agent.trim().toLowerCase()) ||
      null,
    taskType: normalizeDispatchTaskType(data.taskType ?? data.task_type ?? null),
    action:
      (typeof data.action === "string" && data.action.trim()) ||
      (typeof data.plannedAction === "string" && data.plannedAction.trim()) ||
      (typeof data.planned_action === "string" && data.planned_action.trim()) ||
      null,
    status,
    currentStep:
      (typeof data.currentStep === "string" && data.currentStep.trim()) ||
      (typeof data.current_step === "string" && data.current_step.trim()) ||
      null,
    safetyStatus,
    requiresApproval:
      requiresApproval ||
      status === "waiting_for_approval" ||
      safetyStatus === "approval_needed" ||
      safetyStatus === "requires_approval",
    pollUrl,
    errorCode:
      (typeof data.errorCode === "string" && data.errorCode.trim().toLowerCase().replace(/[\s-]/g, "_")) ||
      (typeof data.error_code === "string" && data.error_code.trim().toLowerCase().replace(/[\s-]/g, "_")) ||
      null,
    errorMessage:
      (typeof data.errorMessage === "string" && data.errorMessage.trim()) ||
      (typeof data.error_message === "string" && data.error_message.trim()) ||
      null,
    summaryText:
      (typeof data.summaryText === "string" && data.summaryText.trim()) ||
      (typeof data.summary_text === "string" && data.summary_text.trim()) ||
      (typeof data.summary === "string" && data.summary.trim()) ||
      null,
  };
}

/**
 * Returns true when a dispatcher run should continue polling.
 *
 * @param {string|null|undefined} status
 * @returns {boolean}
 */
export function shouldPollAutonomousDispatchStatus(status) {
  if (typeof status !== "string") return false;
  return AGENT_DISPATCH_ACTIVE_STATUSES.has(status.trim().toLowerCase().replace(/[\s-]/g, "_"));
}

/**
 * Resolve a short German label for the dispatcher status.
 *
 * @param {string|null|undefined} status
 * @param {boolean} [dispatched]
 * @returns {string}
 */
export function resolveAutonomousDispatchStatusLabel(status, dispatched = false) {
  const key = typeof status === "string" ? status.trim().toLowerCase().replace(/[\s-]/g, "_") : "";
  if (key === "queued" && dispatched) return AGENT_DISPATCH_STARTED_TEXT;
  return AGENT_DISPATCH_STATUS_LABELS_DE[key] || AGENT_DISPATCH_STATUS_LABELS_DE.running;
}

/**
 * Resolve a short German label for the dispatcher safety status.
 *
 * @param {string|null|undefined} safetyStatus
 * @param {boolean} [requiresApproval]
 * @returns {string|null}
 */
export function resolveAutonomousDispatchSafetyLabel(safetyStatus, requiresApproval = false) {
  if (requiresApproval) return AGENT_DISPATCH_SAFETY_STATUS_LABELS_DE.approval_needed;
  if (typeof safetyStatus !== "string" || !safetyStatus.trim()) return null;
  const key = safetyStatus.trim().toLowerCase().replace(/[\s-]/g, "_");
  return AGENT_DISPATCH_SAFETY_STATUS_LABELS_DE[key] || null;
}

/**
 * Resolve a user-facing dispatcher error text.
 *
 * @param {string|null|undefined} errorCode
 * @param {string|null|undefined} errorMessage
 * @returns {string|null}
 */
export function buildAutonomousDispatchErrorText(errorCode, errorMessage = null) {
  if (typeof errorCode === "string" && errorCode.trim()) {
    const key = errorCode.trim().toLowerCase().replace(/[\s-]/g, "_");
    if (AGENT_DISPATCH_ERROR_LABELS_DE[key]) return AGENT_DISPATCH_ERROR_LABELS_DE[key];
  }
  if (typeof errorMessage === "string" && errorMessage.trim()) return errorMessage.trim();
  return null;
}

/**
 * Returns true when the run can be approved directly in the card.
 *
 * @param {object|null} run
 * @returns {boolean}
 */
export function canApproveAutonomousDispatchRun(run) {
  if (!run || typeof run !== "object") return false;
  if (!run.autonomyRunId) return false;
  if (run.status !== "waiting_for_approval") return false;
  if (run.safetyStatus === "blocked" || run.status === "blocked") return false;
  return run.requiresApproval === true;
}

// ── Sentinel V1 UI Card ────────────────────────────────────────────────────

/**
 * Card-level overall status labels for the SentinelStatusCard.
 *
 *   healthy  → "System wirkt stabil"
 *   degraded → "System braucht Prüfung"
 *   critical → "Eingriff empfohlen"
 *   unknown  → "Status unbekannt"
 */
export const SENTINEL_V1_CARD_STATUS_LABELS_DE = {
  healthy:  "System wirkt stabil",
  degraded: "System braucht Prüfung",
  critical: "Eingriff empfohlen",
  unknown:  "Status unbekannt",
};

/**
 * Short German severity labels for individual Sentinel findings.
 *
 *   info     → "Hinweis"
 *   warning  → "Achtung"
 *   critical → "Kritisch"
 */
export const SENTINEL_V1_SEVERITY_LABELS_DE = {
  info:     "Hinweis",
  warning:  "Achtung",
  critical: "Kritisch",
};

/**
 * Short German category labels for Sentinel findings.
 */
export const SENTINEL_V1_CATEGORY_LABELS_DE = {
  pipeline:     "Pipeline",
  table_health: "Tabellen-Gesundheit",
  autonomy_run: "Agentenlauf",
  handoff:      "Übergabe",
  provider:     "Provider",
  system:       "System",
};

/** CTA label for the primary scan button in the SentinelStatusCard. */
export const SENTINEL_V1_SCAN_BUTTON_LABEL = "Sentinel prüfen";

/** Alternative CTA label shown after an initial result is present. */
export const SENTINEL_V1_STATUS_UPDATE_LABEL = "Status aktualisieren";

/** Error text shown when the scan endpoint is unreachable. */
export const SENTINEL_V1_API_ERROR_TEXT =
  "Sentinel konnte gerade nicht prüfen. Bitte später erneut versuchen.";

/**
 * Derive the card-level status from the raw GET /api/admin/agent-sentinel/status
 * response.  Maps explicit status strings and falls back to deriving from
 * severity counts when the field is absent.
 *
 * Returns one of: "healthy" | "degraded" | "critical" | "unknown"
 *
 * @param {any} data – raw API response body
 * @returns {"healthy"|"degraded"|"critical"|"unknown"}
 */
export function normalizeSentinelV1CardStatus(data) {
  if (!data || typeof data !== "object") return "unknown";
  const rawStatus = typeof data.status === "string" ? data.status.trim().toLowerCase() : "";
  if (rawStatus === "healthy" || rawStatus === "ok" || rawStatus === "stable") return "healthy";
  if (rawStatus === "degraded" || rawStatus === "warning") return "degraded";
  if (rawStatus === "critical") return "critical";

  // Derive from explicit severity counts when status is missing / not mapped
  const criticalCount =
    (typeof data.criticalCount === "number" ? data.criticalCount : 0) +
    (typeof data.critical_count === "number" ? data.critical_count : 0);
  const warningCount =
    (typeof data.warningCount === "number" ? data.warningCount : 0) +
    (typeof data.warning_count === "number" ? data.warning_count : 0);

  if (criticalCount > 0) return "critical";
  if (warningCount > 0) return "degraded";
  if (data.active === true || rawStatus === "active" || rawStatus === "scanning") return "healthy";
  return "unknown";
}

/**
 * Normalize the findings array from a Sentinel status/scan response into
 * the compact, display-safe shape required by the SentinelStatusCard.
 *
 * Each finding:
 *   {
 *     title:       string|null  – short finding title / area (≤80 chars)
 *     category:    string       – one of SENTINEL_V1_CATEGORY_LABELS_DE keys
 *     severity:    string       – "info" | "warning" | "critical"
 *     affectedArea: string|null – affected component / area (≤60 chars)
 *     recommendation: string|null – short recommended action (≤120 chars)
 *   }
 *
 * Returns at most 10 items; invalid entries are dropped.
 *
 * @param {any} data – raw API response body
 * @returns {Array}
 */
export function normalizeSentinelV1Findings(data) {
  if (!data || typeof data !== "object") return [];
  const rawList =
    Array.isArray(data.findings)       ? data.findings :
    Array.isArray(data.issues)         ? data.issues :
    Array.isArray(data.data?.findings) ? data.data.findings :
    Array.isArray(data.data?.issues)   ? data.data.issues :
    Array.isArray(data)                ? data : [];

  const KNOWN_CATEGORIES = new Set(Object.keys(SENTINEL_V1_CATEGORY_LABELS_DE));
  const KNOWN_SEVERITIES = new Set(Object.keys(SENTINEL_V1_SEVERITY_LABELS_DE));

  const SEVERITY_ORDER = { critical: 0, warning: 1, info: 2 };

  return rawList
    .slice(0, 20) // read up to 20, trim to 10 after sort
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const title = sanitizeShortText(item.title || item.name || item.area || item.component, 80);
      const rawCat = typeof item.category === "string" ? item.category.toLowerCase().replace(/-/g, "_") : "system";
      const category = KNOWN_CATEGORIES.has(rawCat) ? rawCat : "system";
      const rawSev =
        typeof item.severity === "string"   ? item.severity.toLowerCase() :
        typeof item.risk === "string"        ? item.risk.toLowerCase() :
        typeof item.riskLevel === "string"   ? item.riskLevel.toLowerCase() : "info";
      const severity = KNOWN_SEVERITIES.has(rawSev) ? rawSev : "info";
      const affectedArea = sanitizeShortText(
        item.affectedArea || item.affected_area || item.area || item.component, 60
      );
      const recommendation = sanitizeShortText(
        item.recommendation || item.nextStep || item.next_step || item.suggestion, 120
      );
      if (!title && !affectedArea) return null;
      return { title, category, severity, affectedArea, recommendation };
    })
    .filter(Boolean)
    .sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 2) - (SEVERITY_ORDER[b.severity] ?? 2))
    .slice(0, 10);
}

/**
 * Build the short "next recommended action" text for the SentinelStatusCard
 * based on the derived card status.
 *
 * @param {"healthy"|"degraded"|"critical"|"unknown"} cardStatus
 * @returns {string}
 */
export function buildSentinelV1NextAction(cardStatus) {
  if (cardStatus === "critical") return "Kritische Findings prüfen und Maßnahmen einleiten.";
  if (cardStatus === "degraded") return "Warnungen im Blick behalten und Findings prüfen.";
  if (cardStatus === "healthy")  return "System läuft stabil. Regelmäßige Prüfung empfohlen.";
  return "Status prüfen und Scan starten.";
}

/**
 * Normalize the full GET /api/admin/agent-sentinel/status response into the
 * complete SentinelStatusCard data shape.
 *
 * Returns:
 *   {
 *     cardStatus:    "healthy"|"degraded"|"critical"|"unknown"
 *     lastScan:      string|null   – ISO timestamp
 *     infoCount:     number
 *     warningCount:  number
 *     criticalCount: number
 *     findings:      Array         – normalizeSentinelV1Findings result
 *     nextAction:    string        – recommended next step (German)
 *   }
 *
 * @param {any} data – raw API response body (may be null/undefined)
 * @returns {object}
 */
export function normalizeSentinelV1StatusCard(data) {
  if (!data || typeof data !== "object") {
    return {
      cardStatus: "unknown",
      lastScan: null,
      infoCount: 0,
      warningCount: 0,
      criticalCount: 0,
      findings: [],
      nextAction: buildSentinelV1NextAction("unknown"),
    };
  }

  const cardStatus = normalizeSentinelV1CardStatus(data);

  const lastScan =
    (typeof data.lastScan === "string" && data.lastScan)   ||
    (typeof data.last_scan === "string" && data.last_scan) ||
    (typeof data.scannedAt === "string" && data.scannedAt) ||
    null;

  const infoCount = Math.max(
    0,
    typeof data.infoCount === "number"    ? Math.floor(data.infoCount)    :
    typeof data.info_count === "number"   ? Math.floor(data.info_count)   : 0
  );
  const warningCount = Math.max(
    0,
    typeof data.warningCount === "number"  ? Math.floor(data.warningCount)  :
    typeof data.warning_count === "number" ? Math.floor(data.warning_count) : 0
  );
  const criticalCount = Math.max(
    0,
    typeof data.criticalCount === "number"  ? Math.floor(data.criticalCount)  :
    typeof data.critical_count === "number" ? Math.floor(data.critical_count) : 0
  );

  const findings  = normalizeSentinelV1Findings(data);
  const nextAction = buildSentinelV1NextAction(cardStatus);

  return { cardStatus, lastScan, infoCount, warningCount, criticalCount, findings, nextAction };
}

// ── Agent Role Registry V1 ─────────────────────────────────────────────────

/** Card title for the agent registry compact card */
export const AGENT_REGISTRY_CARD_TITLE = "Agenten-Rollen";

/** Shown when the registry endpoint returns an empty list */
export const AGENT_REGISTRY_EMPTY_TEXT = "Keine Agents in der Registry gefunden.";

/** Shown when the registry endpoint is unreachable */
export const AGENT_REGISTRY_ERROR_TEXT = "Agenten-Registry nicht erreichbar.";

/** Shown when a single agent endpoint is unreachable */
export const AGENT_REGISTRY_DETAIL_ERROR_TEXT = "Agent-Details nicht verfügbar.";

/** Shown when system context is unavailable */
export const AGENT_CONTEXT_UNAVAILABLE_TEXT = "Systemkontext nicht verfügbar.";

/** Shown when system context is partially available */
export const AGENT_CONTEXT_PARTIAL_TEXT = "Systemkontext teilweise verfügbar";

/** Shown when a data source is missing */
export const AGENT_CONTEXT_MISSING_SOURCE_TEXT = "Datenquelle fehlt";

/** Shown when a context area is not yet connected */
export const AGENT_CONTEXT_NOT_CONNECTED_TEXT = "Noch nicht angebunden";

/**
 * German badge labels for each capability key.
 * Used in the AgentRegistryCard capability badge display.
 */
export const AGENT_CAPABILITY_LABELS_DE = {
  canAnalyze:        "Analysieren",
  canDesign:         "Design",
  canCode:           "Coden",
  canCreatePR:       "PR erstellen",
  canMergeMain:      "Main-Merge",
  canReadBackend:    "Backend lesen",
  canReadFrontend:   "Frontend lesen",
  canReadDatabase:   "DB lesen",
  canWriteDatabase:  "DB schreiben",
  requiresSafetyGate: "SafetyGate",
};

/**
 * Ordered list of capability keys for consistent badge display.
 */
export const AGENT_CAPABILITY_KEYS = [
  "canAnalyze",
  "canDesign",
  "canCode",
  "canCreatePR",
  "canMergeMain",
  "canReadBackend",
  "canReadFrontend",
  "canReadDatabase",
  "canWriteDatabase",
  "requiresSafetyGate",
];

/**
 * German labels for context area keys.
 */
export const AGENT_CONTEXT_AREA_LABELS_DE = {
  backend:    "Backend",
  frontend:   "Frontend",
  database:   "Datenbank",
  jobs:       "Jobs",
  sentinel:   "Sentinel",
  autonomy:   "Autonomy",
  github:     "GitHub/PR",
  design:     "Design/Screenshots",
};

/**
 * Returns the CSS color variant for a capability badge based on its state.
 *
 * @param {string} key   – capability key (e.g. "canCode")
 * @param {boolean|string|null|undefined} value – capability value from backend
 * @returns {"allowed"|"restricted"|"blocked"|"critical"}
 */
export function resolveCapabilityBadgeState(key, value) {
  // requiresSafetyGate: amber when true (critical path, not alarming)
  if (key === "requiresSafetyGate") {
    return value === true ? "critical" : "blocked";
  }
  if (value === true || value === "allowed" || value === "yes") return "allowed";
  if (value === "restricted" || value === "limited")             return "restricted";
  if (value === false || value === "blocked" || value === "no")  return "blocked";
  return "blocked";
}

/**
 * Normalize a single agent entry from the registry API response.
 *
 * Accepts both camelCase and snake_case fields from the backend.
 * Falls back to sensible defaults so the UI never crashes on partial data.
 *
 * @param {any} raw – raw agent object from GET /api/admin/agents/registry
 * @returns {object}
 */
export function normalizeAgentRegistryEntry(raw) {
  if (!raw || typeof raw !== "object") return null;

  const id    = (typeof raw.id    === "string" && raw.id.trim())    || "";
  const label = (typeof raw.label === "string" && raw.label.trim()) ||
                (typeof raw.name  === "string" && raw.name.trim())  || id;
  const role  = (typeof raw.role  === "string" && raw.role.trim())  ||
                (typeof raw.roleLabel === "string" && raw.roleLabel.trim()) || "";
  const area  = (typeof raw.area  === "string" && raw.area.trim())  ||
                (typeof raw.mainArea === "string" && raw.mainArea.trim()) ||
                (typeof raw.main_area === "string" && raw.main_area.trim()) || "";
  const description = (typeof raw.description === "string" && raw.description.trim()) || "";

  // Normalise capabilities – accept top-level caps object or nested
  const rawCaps = (raw.capabilities && typeof raw.capabilities === "object")
    ? raw.capabilities
    : raw;

  const capabilities = {};
  for (const key of AGENT_CAPABILITY_KEYS) {
    const snakeKey = key.replace(/([A-Z])/g, (m) => `_${m.toLowerCase()}`);
    const val = rawCaps[key] ?? rawCaps[snakeKey] ?? null;
    capabilities[key] = val;
  }

  return { id, label, role, area, description, capabilities };
}

/**
 * Normalize the full registry list from GET /api/admin/agents/registry.
 *
 * @param {any} data – raw API response (may be null/undefined/array/object)
 * @returns {Array} – array of normalizeAgentRegistryEntry results
 */
export function normalizeAgentRegistry(data) {
  const list =
    Array.isArray(data)            ? data :
    Array.isArray(data?.agents)    ? data.agents :
    Array.isArray(data?.registry)  ? data.registry :
    Array.isArray(data?.data)      ? data.data : [];

  return list.map(normalizeAgentRegistryEntry).filter(Boolean);
}

/**
 * Normalize the system context payload from
 * GET /api/admin/agents/:agentId/system-context
 *
 * @param {any} data – raw API response
 * @returns {object}
 */
export function normalizeAgentSystemContext(data) {
  if (!data || typeof data !== "object") {
    return {
      available: false,
      partial:   false,
      usedAreas:    [],
      availableAreas: [],
      missingAreas:   [],
      knownIssues:    [],
    };
  }

  const available = data.available === true || data.contextAvailable === true;
  const partial   = data.partial === true   || data.partiallyAvailable === true ||
                    data.context_partial === true;

  const toArray = (v) => Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];

  const usedAreas      = toArray(data.usedAreas      ?? data.used_areas      ?? data.context ?? []);
  const availableAreas = toArray(data.availableAreas ?? data.available_areas ?? data.areas   ?? []);
  const missingAreas   = toArray(data.missingAreas   ?? data.missing_areas   ?? data.missing ?? []);
  const knownIssues    = toArray(data.knownIssues     ?? data.known_issues    ?? data.issues  ?? []);

  return { available, partial, usedAreas, availableAreas, missingAreas, knownIssues };
}

// ── Agent Capability Router V1 ─────────────────────────────────────────────

/** Card title for the routing decision card */
export const AGENT_ROUTING_CARD_TITLE = "Agenten-Routing";

/** Shown when the router API is not reachable */
export const AGENT_ROUTING_API_ERROR_TEXT = "Routing-Dienst nicht erreichbar.";

/** Shown for unknown task types */
export const AGENT_ROUTING_UNKNOWN_TASK_TEXT = "Nicht eindeutig erkannt";

/** Shown when no agent could be selected */
export const AGENT_ROUTING_NO_AGENT_TEXT = "Kein Agent ausgewählt";

/** Label for the "Details anzeigen" toggle */
export const AGENT_ROUTING_DETAILS_LABEL = "Details anzeigen";

/** Label for the "Routing prüfen" button */
export const AGENT_ROUTING_CHECK_LABEL = "Routing prüfen";

/** Shown when a required input is missing */
export const AGENT_ROUTING_MISSING_CONTEXT_TEXT = "Fehlender Kontext";

/**
 * German labels for task type keys.
 */
export const AGENT_ROUTING_TASK_TYPE_LABELS_DE = {
  frontend_design_review:    "Designprüfung",
  frontend_ui_changeset:     "UI-Änderungsvorschlag",
  screenshot_design_analysis:"Screenshot-Analyse",
  backend_diagnosis:         "Backend-Diagnose",
  data_pipeline_diagnosis:   "Datenpipeline-Prüfung",
  database_health_review:    "Datenbankprüfung",
  repair_plan_request:       "Reparaturplan",
  code_patch_request:        "Code-Umsetzung",
  create_pr_request:         "PR-Erstellung",
  merge_request:             "Main-Merge",
  test_review:               "Testprüfung",
  regression_review:         "Regression-Prüfung",
  sentinel_finding_review:   "Sentinel-Finding",
  conference_summary:        "Konferenz-Zusammenfassung",
  unknown:                   "Nicht eindeutig erkannt",
};

/**
 * German labels for risk levels.
 */
export const AGENT_ROUTING_RISK_LABELS_DE = {
  low:      "niedrig",
  medium:   "mittel",
  high:     "hoch",
  critical: "kritisch",
};

/**
 * German labels for safety gate statuses.
 */
export const AGENT_ROUTING_SAFETY_LABELS_DE = {
  allowed:           "erlaubt",
  requires_approval: "Freigabe nötig",
  blocked:           "blockiert",
};

/**
 * Normalize a raw router classify/route response to a stable shape.
 *
 * @param {any} data  – raw API response data
 * @returns {object|null}
 */
export function normalizeAgentRoutingDecision(data) {
  if (!data || typeof data !== "object") return null;

  const taskType = (typeof data.taskType    === "string" && data.taskType.trim()) ||
                   (typeof data.task_type   === "string" && data.task_type.trim()) ||
                   "unknown";

  const targetAgent = (typeof data.targetAgent  === "string" && data.targetAgent.trim()) ||
                      (typeof data.target_agent  === "string" && data.target_agent.trim()) ||
                      (typeof data.agent         === "string" && data.agent.trim()) ||
                      "";

  const plannedAction = (typeof data.plannedAction  === "string" && data.plannedAction.trim()) ||
                        (typeof data.planned_action  === "string" && data.planned_action.trim()) ||
                        (typeof data.action           === "string" && data.action.trim()) ||
                        "";

  const riskLevel = (typeof data.riskLevel   === "string" && data.riskLevel.trim())   ||
                    (typeof data.risk_level   === "string" && data.risk_level.trim())  ||
                    (typeof data.risk         === "string" && data.risk.trim()) ||
                    "low";

  const safetyStatus = (typeof data.safetyStatus  === "string" && data.safetyStatus.trim())  ||
                       (typeof data.safety_status  === "string" && data.safety_status.trim()) ||
                       (typeof data.safety         === "string" && data.safety.trim()) ||
                       "allowed";

  const approvalRequired = data.approvalRequired === true ||
                           data.approval_required === true ||
                           safetyStatus === "requires_approval";

  const reason = (typeof data.reason  === "string" && data.reason.trim()) ||
                 (typeof data.why     === "string" && data.why.trim()) ||
                 "";

  const toStringArray = (v) =>
    Array.isArray(v) ? v.filter((x) => typeof x === "string" && x.trim()) : [];

  const missingInputs = toStringArray(data.missingInputs ?? data.missing_inputs ?? data.missing ?? []);
  const nextStep      = (typeof data.nextStep  === "string" && data.nextStep.trim()) ||
                        (typeof data.next_step  === "string" && data.next_step.trim()) ||
                        "";

  // Explanation fields (from /explain endpoint or merged into route result)
  const agentReason   = (typeof data.agentReason   === "string" && data.agentReason.trim())   ||
                        (typeof data.agent_reason   === "string" && data.agent_reason.trim())  || "";
  const actionReason  = (typeof data.actionReason  === "string" && data.actionReason.trim())  ||
                        (typeof data.action_reason  === "string" && data.action_reason.trim()) || "";
  const registryRule  = (typeof data.registryRule  === "string" && data.registryRule.trim())  ||
                        (typeof data.registry_rule  === "string" && data.registry_rule.trim()) || "";
  const safetyRule    = (typeof data.safetyRule    === "string" && data.safetyRule.trim())    ||
                        (typeof data.safety_rule    === "string" && data.safety_rule.trim())   || "";

  return {
    taskType,
    targetAgent,
    plannedAction,
    riskLevel,
    safetyStatus,
    approvalRequired,
    reason,
    missingInputs,
    nextStep,
    agentReason,
    actionReason,
    registryRule,
    safetyRule,
  };
}

// ── Step 5 (Agent Hub): Offene Aktionen – Central Pending Actions Overview ───

/**
 * Possible type values for a normalized pending agent action.
 */
export const AGENT_PENDING_ACTION_TYPES = [
  "approval",
  "patch",
  "pull_request",
  "repair",
  "sentinel",
  "blocked",
  "pipeline",
  "runtime",
  "follow_up",
  "unknown",
];

/**
 * Possible status values for a normalized pending agent action.
 */
export const AGENT_PENDING_ACTION_STATUSES = [
  "pending",
  "waiting_approval",
  "blocked",
  "ready",
  "running",
  "completed",
  "failed",
  "unknown",
];

/**
 * German labels for pending-action types.
 */
export const AGENT_PENDING_ACTION_TYPE_LABELS_DE = {
  approval:      "Freigabe erforderlich",
  patch:         "Patch vorbereitet",
  pull_request:  "PR offen",
  repair:        "Repair Queue",
  sentinel:      "Sentinel",
  blocked:       "Blockiert",
  pipeline:      "Pipeline",
  runtime:       "Laufzeit",
  follow_up:     "Follow-up",
  unknown:       "Ausstehend",
};

/**
 * German labels for pending-action status values.
 */
export const AGENT_PENDING_ACTION_STATUS_LABELS_DE = {
  pending:           "Ausstehend",
  waiting_approval:  "Wartet auf Freigabe",
  blocked:           "Blockiert",
  ready:             "Bereit",
  running:           "Läuft",
  completed:         "Abgeschlossen",
  failed:            "Fehlgeschlagen",
  unknown:           "Unbekannt",
};

/**
 * Heading text for the "Offene Aktionen" card.
 */
export const AGENT_PENDING_ACTIONS_CARD_TITLE = "Offene Aktionen";

/**
 * Text shown when there are no open actions.
 */
export const AGENT_PENDING_ACTIONS_EMPTY_TEXT = "Keine offenen Aktionen";

/**
 * Fallback text for "Details ansehen" CTA when no safe approval endpoint is available.
 */
export const AGENT_PENDING_ACTIONS_DETAILS_LABEL = "Details ansehen";

/**
 * Label for the "Freigeben" CTA (only shown when a safe approval flow is confirmed).
 */
export const AGENT_PENDING_ACTIONS_APPROVE_LABEL = "Freigeben";

/**
 * Label for the "Ablehnen" CTA.
 */
export const AGENT_PENDING_ACTIONS_REJECT_LABEL = "Ablehnen";

/**
 * Label for the "PR ansehen" CTA.
 */
export const AGENT_PENDING_ACTIONS_VIEW_PR_LABEL = "PR ansehen";

/**
 * Label for the "Status aktualisieren" CTA.
 */
export const AGENT_PENDING_ACTIONS_REFRESH_LABEL = "Status aktualisieren";

/**
 * Label for the "Als Plan öffnen" CTA.
 */
export const AGENT_PENDING_ACTIONS_OPEN_PLAN_LABEL = "Als Plan öffnen";

/**
 * Fallback German text for unknown block reasons.
 */
export const AGENT_PENDING_ACTIONS_UNKNOWN_BLOCK_TEXT = "Aktion ist blockiert. Details prüfen.";

/**
 * Resolves a human-readable German label for a pending-action type.
 *
 * @param {string|null|undefined} type
 * @returns {string}
 */
export function resolvePendingActionTypeLabel(type) {
  if (typeof type !== "string") return AGENT_PENDING_ACTION_TYPE_LABELS_DE.unknown;
  return AGENT_PENDING_ACTION_TYPE_LABELS_DE[type.trim()] ||
         AGENT_PENDING_ACTION_TYPE_LABELS_DE.unknown;
}

/**
 * Resolves a human-readable German label for a pending-action status.
 *
 * @param {string|null|undefined} status
 * @returns {string}
 */
export function resolvePendingActionStatusLabel(status) {
  if (typeof status !== "string") return AGENT_PENDING_ACTION_STATUS_LABELS_DE.unknown;
  return AGENT_PENDING_ACTION_STATUS_LABELS_DE[status.trim()] ||
         AGENT_PENDING_ACTION_STATUS_LABELS_DE.unknown;
}

/**
 * Infers the action type from various raw source fields when no explicit type
 * is present.
 *
 * @param {object} raw
 * @returns {string}
 */
function inferPendingActionType(raw) {
  if (raw.requiresApproval === true || raw.requires_approval === true) return "approval";
  const blockReason = raw.blockReason ?? raw.block_reason ?? null;
  if (blockReason) return "blocked";
  const pullRequestUrl = raw.pullRequestUrl ?? raw.prUrl ?? raw.html_url ?? raw.pull_request_url ?? null;
  if (pullRequestUrl) return "pull_request";
  const source = typeof raw.source === "string" ? raw.source.toLowerCase() : "";
  if (source.includes("sentinel") || source.includes("escalation")) return "sentinel";
  if (source.includes("repair") || source.includes("queue")) return "repair";
  if (source.includes("pipeline")) return "pipeline";
  if (source.includes("runtime")) return "runtime";
  if (source.includes("follow_up") || source.includes("followup")) return "follow_up";
  const action = typeof raw.action === "string" ? raw.action.toLowerCase() : "";
  if (action.includes("patch") || action.includes("repair")) return "patch";
  if (action.includes("approval") || action.includes("approve")) return "approval";
  if (action.includes("pull_request") || action.includes("pr")) return "pull_request";
  return "unknown";
}

/**
 * Infers the action status from raw source fields.
 *
 * @param {object} raw
 * @returns {string}
 */
function inferPendingActionStatus(raw) {
  const s = raw.status ?? raw.state ?? raw.workflowState ?? raw.workflow_state ?? null;
  if (typeof s === "string") {
    const v = s.trim().toLowerCase();
    if (v === "waiting_approval" || v === "waiting-approval") return "waiting_approval";
    if (v === "blocked") return "blocked";
    if (v === "ready") return "ready";
    if (v === "running" || v === "in_progress" || v === "active") return "running";
    if (v === "completed" || v === "done" || v === "resolved") return "completed";
    if (v === "failed" || v === "error" || v === "errored") return "failed";
    if (v === "pending" || v === "queued") return "pending";
  }
  if (raw.requiresApproval === true || raw.requires_approval === true) return "waiting_approval";
  const blockReason = raw.blockReason ?? raw.block_reason ?? null;
  if (blockReason) return "blocked";
  return "pending";
}

/**
 * Normalizes a single raw action object from any backend source into the
 * unified pending-action shape used by AgentPendingActionsCard.
 *
 * Safe: never throws, all fields optional, strings truncated.
 *
 * @param {any} raw
 * @returns {object|null}
 */
export function normalizeSingleAgentPendingAction(raw) {
  if (!raw || typeof raw !== "object") return null;

  const id = typeof raw.id === "string" && raw.id ? raw.id.slice(0, 128) : null;

  const rawType = typeof raw.type === "string" ? raw.type.trim() : null;
  const type = (rawType && AGENT_PENDING_ACTION_TYPES.includes(rawType))
    ? rawType
    : inferPendingActionType(raw);

  const rawStatus = typeof raw.status === "string" ? raw.status.trim() : null;
  const status = (rawStatus && AGENT_PENDING_ACTION_STATUSES.includes(rawStatus))
    ? rawStatus
    : inferPendingActionStatus(raw);

  // title: prefer explicit title, fall back to summary/action
  const titleRaw = raw.title ?? raw.summary ?? raw.action ?? null;
  const title = typeof titleRaw === "string" && titleRaw.trim()
    ? titleRaw.trim().slice(0, 120)
    : null;

  const description = typeof raw.description === "string" && raw.description.trim()
    ? raw.description.trim().slice(0, 300)
    : null;

  const severity = typeof raw.severity === "string" ? raw.severity.trim().slice(0, 40) : null;

  const agentId = typeof raw.agentId === "string" ? raw.agentId.trim().slice(0, 40)
    : typeof raw.agent === "string" ? raw.agent.trim().slice(0, 40)
    : typeof raw.agent_id === "string" ? raw.agent_id.trim().slice(0, 40)
    : null;

  const source = typeof raw.source === "string" ? raw.source.trim().slice(0, 80) : null;

  const requiresApproval = raw.requiresApproval === true || raw.requires_approval === true
    ? true
    : raw.requiresApproval === false || raw.requires_approval === false
      ? false
      : null;

  const blocked = raw.blocked === true ? true : raw.blocked === false ? false : null;

  const blockReason = typeof raw.blockReason === "string" && raw.blockReason.trim()
    ? raw.blockReason.trim().slice(0, 120)
    : typeof raw.block_reason === "string" && raw.block_reason.trim()
      ? raw.block_reason.trim().slice(0, 120)
      : null;

  const blockReasonText = resolveAgentBlockReasonText(blockReason, null, requiresApproval === true)
    || (blockReason ? `Blockiert: ${blockReason}` : null)
    || (blocked === true ? AGENT_PENDING_ACTIONS_UNKNOWN_BLOCK_TEXT : null);

  const nextRequiredStep = typeof raw.nextRequiredStep === "string" && raw.nextRequiredStep.trim()
    ? raw.nextRequiredStep.trim().slice(0, 120)
    : typeof raw.next_required_step === "string" && raw.next_required_step.trim()
      ? raw.next_required_step.trim().slice(0, 120)
      : null;

  // PR fields – reuse existing normalizers
  const pullRequestUrl = normalizePullRequestUrl(raw);
  const prUrlValid = isValidGithubPullRequestUrl(pullRequestUrl);
  const pullRequestNumber = normalizePullRequestNumber(raw)
    ?? (pullRequestUrl ? extractPrNumberFromUrl(pullRequestUrl) : null);
  const pullRequestState = normalizePullRequestState(raw);
  const mergedToMain = raw.mergedToMain === true || raw.merged_to_main === true
    ? true
    : raw.mergedToMain === false || raw.merged_to_main === false ? false : null;

  // Canonical / legacy flow
  const rawFlowType = typeof raw.canonicalFlow === "string" ? raw.canonicalFlow
    : typeof raw.legacyFlow === "string" ? raw.legacyFlow
    : typeof raw.flowType === "string" ? raw.flowType
    : typeof raw.flow_type === "string" ? raw.flow_type
    : null;
  const canonicalFlow = raw.canonicalFlow === true
    || rawFlowType === "canonical"
    || raw.isCanonical === true
    ? true : null;
  const legacyFlow = raw.legacyFlow === true
    || rawFlowType === "legacy"
    || raw.legacyRouteUsed === true
    ? true : null;

  // Follow-up fields
  const followUpResolved = raw.followUpResolved === true || raw.follow_up_resolved === true
    ? true : null;
  const selectedImprovement = raw.selectedImprovement ?? raw.selected_improvement ?? null;

  const createdAt = typeof raw.createdAt === "string" ? raw.createdAt
    : typeof raw.created_at === "string" ? raw.created_at
    : null;
  const updatedAt = typeof raw.updatedAt === "string" ? raw.updatedAt
    : typeof raw.updated_at === "string" ? raw.updated_at
    : null;

  const actionLabel = resolvePendingActionTypeLabel(type);
  const details = raw.details ?? raw.raw ?? null;

  // Must have at least a meaningful title or a type that is not unknown to be useful
  if (!title && type === "unknown") return null;

  return {
    id,
    type,
    title,
    description,
    severity,
    status,
    agentId,
    source,
    requiresApproval,
    blocked,
    blockReason,
    blockReasonText,
    nextRequiredStep,
    pullRequestUrl: prUrlValid ? pullRequestUrl : null,
    pullRequestUrlRaw: pullRequestUrl,
    pullRequestUrlValid: prUrlValid,
    pullRequestNumber,
    pullRequestState,
    mergedToMain,
    canonicalFlow,
    legacyFlow,
    followUpResolved,
    selectedImprovement,
    createdAt,
    updatedAt,
    actionLabel,
    details,
  };
}

/**
 * Normalizes escalation summary data into pending-action items.
 *
 * @param {object|null} escalationSummary – result of normalizeEscalationSummary
 * @returns {object[]}
 */
function escalationToPendingActions(escalationSummary) {
  if (!escalationSummary || !escalationSummary.hasEscalations) return [];
  const items = [];
  const total = typeof escalationSummary.total === "number" ? escalationSummary.total : 1;
  const level = escalationSummary.highestLevel ?? escalationSummary.level ?? null;
  const severityText = level ? `Eskalationsstufe: ${level}` : null;
  items.push(normalizeSingleAgentPendingAction({
    id: "escalation-summary",
    type: "sentinel",
    title: `Eskalation${total > 1 ? ` (${total})` : ""}: Sentinel-Prüfung erforderlich`,
    description: severityText,
    severity: level,
    source: "escalation",
    status: "waiting_approval",
  }));
  return items.filter(Boolean);
}

/**
 * Normalizes repair queue data into pending-action items.
 *
 * @param {object} repairQueue – { items: [], total: number, critical: number }
 * @returns {object[]}
 */
function repairQueueToPendingActions(repairQueue) {
  if (!repairQueue) return [];
  const items = Array.isArray(repairQueue.items) ? repairQueue.items : [];
  const total = typeof repairQueue.total === "number" ? repairQueue.total : items.length;
  if (total === 0 && items.length === 0) return [];
  // Show at most 3 individual items; otherwise show a summary
  if (items.length > 0 && items.length <= 3) {
    return items.map((item, i) => normalizeSingleAgentPendingAction({
      id: item.id ?? `repair-${i}`,
      type: "repair",
      title: item.summary ?? item.title ?? item.issue ?? `Repair Queue – Fall ${i + 1}`,
      description: item.description ?? null,
      severity: item.severity ?? null,
      agentId: item.agentId ?? item.agent ?? null,
      source: "repair-queue",
      status: item.status ?? "pending",
    })).filter(Boolean);
  }
  return [normalizeSingleAgentPendingAction({
    id: "repair-queue-summary",
    type: "repair",
    title: `Repair Queue: ${total} offene Fälle`,
    source: "repair-queue",
    status: "pending",
    severity: repairQueue.critical > 0 ? "critical" : null,
  })].filter(Boolean);
}

/**
 * Normalizes persistent sentinel issues into pending-action items.
 *
 * @param {object[]} persistentIssues
 * @returns {object[]}
 */
function sentinelIssuesToPendingActions(persistentIssues) {
  if (!Array.isArray(persistentIssues) || persistentIssues.length === 0) return [];
  return persistentIssues.slice(0, 3).map((issue, i) => normalizeSingleAgentPendingAction({
    id: issue.id ?? `sentinel-issue-${i}`,
    type: "sentinel",
    title: issue.summary ?? issue.title ?? issue.issue ?? `Sentinel-Fall ${i + 1}`,
    description: issue.description ?? null,
    severity: issue.severity ?? issue.level ?? null,
    agentId: issue.agentId ?? issue.agent ?? null,
    source: "sentinel",
    status: issue.status ?? "pending",
    requiresApproval: issue.requiresApproval ?? issue.requires_approval ?? false,
  })).filter(Boolean);
}

/**
 * Converts a session-level pending action (from the last chat response)
 * into a pending-action item.
 *
 * @param {object|null} sessionPendingAction – result of normalizePendingAction
 * @param {string|null} agentId
 * @returns {object[]}
 */
function sessionPendingActionToItems(sessionPendingAction, agentId) {
  if (!sessionPendingAction) return [];
  const action = normalizeSingleAgentPendingAction({
    id: sessionPendingAction.id ?? "session-pending",
    type: "blocked",
    title: sessionPendingAction.summary ?? sessionPendingAction.action ?? "Ausstehende Aktion",
    agentId: agentId ?? null,
    source: "session",
    status: "pending",
    action: sessionPendingAction.action,
  });
  return action ? [action] : [];
}

/**
 * Converts a workflow-blocked session (requiresApproval / blockReason) into
 * a pending-action item.
 *
 * @param {object|null} activeWorkRun – the active run object
 * @param {string|null} agentId
 * @returns {object[]}
 */
function workRunToPendingActions(activeWorkRun, agentId) {
  if (!activeWorkRun) return [];
  const requiresApproval = activeWorkRun.requiresApproval === true
    || activeWorkRun.requires_approval === true;
  const blockReason = activeWorkRun.blockReason ?? activeWorkRun.block_reason ?? null;
  if (!requiresApproval && !blockReason) return [];
  const action = normalizeSingleAgentPendingAction({
    id: activeWorkRun.runId ?? activeWorkRun.id ?? "work-run",
    type: requiresApproval ? "approval" : "blocked",
    title: requiresApproval
      ? "Freigabe erforderlich"
      : `Blockiert: ${blockReason ?? "unbekannter Grund"}`,
    agentId: agentId ?? null,
    source: "work-run",
    status: requiresApproval ? "waiting_approval" : "blocked",
    requiresApproval,
    blockReason,
    nextRequiredStep: activeWorkRun.nextRequiredStep ?? activeWorkRun.next_required_step ?? null,
    canonicalFlow: activeWorkRun.canonicalFlow ?? activeWorkRun.canonical_route ?? null,
    legacyFlow: activeWorkRun.legacyRouteUsed ?? activeWorkRun.legacy_route_used ?? null,
    pullRequestUrl: activeWorkRun.pullRequestUrl ?? activeWorkRun.prUrl ?? null,
    pullRequestNumber: activeWorkRun.pullRequestNumber ?? activeWorkRun.prNumber ?? null,
    mergedToMain: activeWorkRun.mergedToMain ?? activeWorkRun.merged_to_main ?? null,
  });
  return action ? [action] : [];
}

/**
 * Central aggregator: normalizes data from multiple backend sources into a
 * unified flat list of pending-action items for the AgentPendingActionsCard.
 *
 * Sources object shape:
 * {
 *   persistentIssues:     object[]|null,   – normalizePersistentSentinelIssues result
 *   repairQueue:          object|null,      – { items, total, critical }
 *   escalationSummary:    object|null,      – normalizeEscalationSummary result
 *   sessionPendingAction: object|null,      – normalizePendingAction result
 *   activeWorkRun:        object|null,      – createActiveWorkRun result
 *   agentId:              string|null,      – current agent identifier
 * }
 *
 * Returns a de-duplicated, display-safe array of normalized actions.
 * Terminal (completed) and empty items are filtered out.
 * Never throws.
 *
 * @param {object} sources
 * @returns {object[]}
 */
export function normalizeAgentPendingActions(sources) {
  if (!sources || typeof sources !== "object") return [];
  try {
    const {
      persistentIssues = null,
      repairQueue = null,
      escalationSummary = null,
      sessionPendingAction = null,
      activeWorkRun = null,
      agentId = null,
    } = sources;

    const allItems = [
      ...workRunToPendingActions(activeWorkRun, agentId),
      ...sessionPendingActionToItems(sessionPendingAction, agentId),
      ...escalationToPendingActions(escalationSummary),
      ...sentinelIssuesToPendingActions(persistentIssues),
      ...repairQueueToPendingActions(repairQueue),
    ];

    // Filter out completed items and deduplicate by id
    const seenIds = new Set();
    return allItems.filter((item) => {
      if (!item) return false;
      if (item.status === "completed") return false;
      if (item.id) {
        if (seenIds.has(item.id)) return false;
        seenIds.add(item.id);
      }
      return true;
    });
  } catch {
    return [];
  }
}

/**
 * Resolve a German label for a task type.
 *
 * @param {string} taskType
 * @returns {string}
 */
export function resolveRoutingTaskTypeLabel(taskType) {
  if (typeof taskType !== "string") return AGENT_ROUTING_TASK_TYPE_LABELS_DE.unknown;
  return AGENT_ROUTING_TASK_TYPE_LABELS_DE[taskType.trim()] ||
         AGENT_ROUTING_TASK_TYPE_LABELS_DE.unknown;
}

/**
 * Resolve a German label for a risk level.
 *
 * @param {string} level
 * @returns {string}
 */
export function resolveRoutingRiskLabel(level) {
  if (typeof level !== "string") return AGENT_ROUTING_RISK_LABELS_DE.low;
  return AGENT_ROUTING_RISK_LABELS_DE[level.trim()] || level;
}

/**
 * Resolve a German label for a safety status.
 *
 * @param {string} status
 * @returns {string}
 */
export function resolveRoutingSafetyLabel(status) {
  if (typeof status !== "string") return AGENT_ROUTING_SAFETY_LABELS_DE.allowed;
  return AGENT_ROUTING_SAFETY_LABELS_DE[status.trim()] || status;
}
